import { macroCondition, getOwnConfig, importSync } from '@embroider/macros';
import { assert, warn, deprecate } from '@ember/debug';
import { dasherize } from '@ember/string';
import { getOwner, setOwner } from '@ember/application';
import EmberObject, { get, set } from '@ember/object';
import { _backburner } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import { addToTransaction, subscribe, addTransactionCB } from '@ember-data/tracking/-private';
import { tagForProperty } from '@ember/-internals/metal';
import { dependentKeyCompat } from '@ember/object/compat';
import { compare } from '@ember/utils';
import { dirtyTag } from '@glimmer/validator';
import Ember from 'ember';
import { reads } from '@ember/object/computed';
import ArrayProxy from '@ember/array/proxy';
import PromiseProxyMixin from '@ember/object/promise-proxy-mixin';
import ObjectProxy from '@ember/object/proxy';

/**
  @module @ember-data/store
*/

/**
 This method normalizes a modelName into the format Ember Data uses
 internally by dasherizing it.

  @method normalizeModelName
  @static
  @public
  @deprecated
  @for @ember-data/store
  @param {String} modelName
  @return {String} normalizedModelName
*/
function normalizeModelName$1(modelName) {
  return dasherize(modelName);
}
function _initializerDefineProperty(target, property, descriptor, context) {
  if (!descriptor) return;
  Object.defineProperty(target, property, {
    enumerable: descriptor.enumerable,
    configurable: descriptor.configurable,
    writable: descriptor.writable,
    value: descriptor.initializer ? descriptor.initializer.call(context) : void 0
  });
}
function _classPrivateFieldBase(receiver, privateKey) {
  if (!Object.prototype.hasOwnProperty.call(receiver, privateKey)) {
    throw new TypeError("attempted to use private field on non-instance");
  }
  return receiver;
}
var id = 0;
function _classPrivateFieldKey(name) {
  return "__private_" + id++ + "_" + name;
}
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) {
  var desc = {};
  Object.keys(descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;
  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }
  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);
  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }
  if (desc.initializer === void 0) {
    Object.defineProperty(target, property, desc);
    desc = null;
  }
  return desc;
}
var _class$3, _descriptor$3, _descriptor2$1, _descriptor3, _descriptor4, _store$1, _request;
function urlFromLink(link) {
  if (typeof link === 'string') return link;
  return link.href;
}
let Document = (_class$3 = (_store$1 = /*#__PURE__*/_classPrivateFieldKey("store"), _request = /*#__PURE__*/_classPrivateFieldKey("request"), class Document {
  constructor(store, identifier) {
    Object.defineProperty(this, _request, {
      value: _request2
    });
    _initializerDefineProperty(this, "links", _descriptor$3, this);
    _initializerDefineProperty(this, "data", _descriptor2$1, this);
    _initializerDefineProperty(this, "errors", _descriptor3, this);
    _initializerDefineProperty(this, "meta", _descriptor4, this);
    Object.defineProperty(this, _store$1, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldBase(this, _store$1)[_store$1] = store;
    this.identifier = identifier;
  }
  fetch(options = {}) {
    assert(`No self link`, this.links?.self);
    options.cacheOptions = options.cacheOptions || {};
    options.cacheOptions.key = this.identifier?.lid;
    return _classPrivateFieldBase(this, _request)[_request]('self', options);
  }
  next(options) {
    return _classPrivateFieldBase(this, _request)[_request]('next', options);
  }
  prev(options) {
    return _classPrivateFieldBase(this, _request)[_request]('prev', options);
  }
  first(options) {
    return _classPrivateFieldBase(this, _request)[_request]('first', options);
  }
  last(options) {
    return _classPrivateFieldBase(this, _request)[_request]('last', options);
  }
  toJSON() {
    const data = {};
    data.identifier = this.identifier;
    if (this.data !== undefined) {
      data.data = this.data;
    }
    if (this.links !== undefined) {
      data.links = this.links;
    }
    if (this.errors !== undefined) {
      data.errors = this.errors;
    }
    if (this.meta !== undefined) {
      data.meta = this.meta;
    }
    return data;
  }
}), _descriptor$3 = _applyDecoratedDescriptor(_class$3.prototype, "links", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2$1 = _applyDecoratedDescriptor(_class$3.prototype, "data", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class$3.prototype, "errors", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class$3.prototype, "meta", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class$3);
async function _request2(link, options = {}) {
  const href = this.links?.[link];
  if (!href) {
    return null;
  }
  const response = await _classPrivateFieldBase(this, _store$1)[_store$1].request(Object.assign(options, {
    url: urlFromLink(href)
  }));
  return response.content;
}

/**
 * A service which an application may provide to the store via
 * the store's `lifetimes` property to configure the behavior
 * of the CacheHandler.
 *
 * The default behavior for request lifetimes is to never expire
 * unless manually refreshed via `cacheOptions.reload` or `cacheOptions.backgroundReload`.
 *
 * Implementing this service allows you to programatically define
 * when a request should be considered expired.
 *
 * @class <Interface> LifetimesService
 * @public
 */

const MUTATION_OPS = new Set(['createRecord', 'updateRecord', 'deleteRecord']);
function isErrorDocument(document) {
  return 'errors' in document;
}
function maybeUpdateUiObjects(store, request, options, document, isFromCache) {
  const {
    identifier
  } = options;
  if (!document) {
    assert(`The CacheHandler expected response content but none was found`, !options.shouldHydrate);
    return document;
  }
  if (isErrorDocument(document)) {
    if (!identifier && !options.shouldHydrate) {
      return document;
    }
    let doc;
    if (identifier) {
      doc = store._documentCache.get(identifier);
    }
    if (!doc) {
      doc = new Document(store, identifier);
      copyDocumentProperties(doc, document);
      if (identifier) {
        store._documentCache.set(identifier, doc);
      }
    } else if (!isFromCache) {
      doc.data = undefined;
      copyDocumentProperties(doc, document);
    }
    return options.shouldHydrate ? doc : document;
  }
  if (Array.isArray(document.data)) {
    const {
      recordArrayManager
    } = store;
    if (!identifier) {
      if (!options.shouldHydrate) {
        return document;
      }
      const data = recordArrayManager.createArray({
        type: request.url,
        identifiers: document.data,
        doc: document,
        query: request
      });
      const doc = new Document(store, null);
      doc.data = data;
      doc.meta = document.meta;
      doc.links = document.links;
      return doc;
    }
    let managed = recordArrayManager._keyedArrays.get(identifier.lid);
    if (!managed) {
      managed = recordArrayManager.createArray({
        type: identifier.lid,
        identifiers: document.data,
        doc: document
      });
      recordArrayManager._keyedArrays.set(identifier.lid, managed);
      const doc = new Document(store, identifier);
      doc.data = managed;
      doc.meta = document.meta;
      doc.links = document.links;
      store._documentCache.set(identifier, doc);
      return options.shouldHydrate ? doc : document;
    } else {
      const doc = store._documentCache.get(identifier);
      if (!isFromCache) {
        recordArrayManager.populateManagedArray(managed, document.data, document);
        doc.data = managed;
        doc.meta = document.meta;
        doc.links = document.links;
      }
      return options.shouldHydrate ? doc : document;
    }
  } else {
    if (!identifier && !options.shouldHydrate) {
      return document;
    }
    const data = document.data ? store.peekRecord(document.data) : null;
    let doc;
    if (identifier) {
      doc = store._documentCache.get(identifier);
    }
    if (!doc) {
      doc = new Document(store, identifier);
      doc.data = data;
      copyDocumentProperties(doc, document);
      if (identifier) {
        store._documentCache.set(identifier, doc);
      }
    } else if (!isFromCache) {
      doc.data = data;
      copyDocumentProperties(doc, document);
    }
    return options.shouldHydrate ? doc : document;
  }
}
function calcShouldFetch(store, request, hasCachedValue, identifier) {
  const {
    cacheOptions
  } = request;
  return request.op && MUTATION_OPS.has(request.op) || cacheOptions?.reload || !hasCachedValue || (store.lifetimes && identifier ? store.lifetimes.isHardExpired(identifier, store) : false);
}
function calcShouldBackgroundFetch(store, request, willFetch, identifier) {
  const {
    cacheOptions
  } = request;
  return !willFetch && (cacheOptions?.backgroundReload || (store.lifetimes && identifier ? store.lifetimes.isSoftExpired(identifier, store) : false));
}
function isMutation(request) {
  return Boolean(request.op && MUTATION_OPS.has(request.op));
}
function fetchContentAndHydrate(next, context, identifier, shouldFetch, shouldBackgroundFetch) {
  const {
    store
  } = context.request;
  const shouldHydrate = context.request[EnableHydration] || false;
  let isMut = false;
  if (isMutation(context.request)) {
    isMut = true;
    // TODO should we handle multiple records in request.records by iteratively calling willCommit for each
    const record = context.request.data?.record || context.request.records?.[0];
    assert(`Expected to receive a list of records included in the ${context.request.op} request`, record || !shouldHydrate);
    if (record) {
      store.cache.willCommit(record, context);
    }
  }
  if (store.lifetimes?.willRequest) {
    store.lifetimes.willRequest(context.request, identifier, store);
  }
  const promise = next(context.request).then(document => {
    store.requestManager._pending.delete(context.id);
    store._enableAsyncFlush = true;
    let response;
    store._join(() => {
      if (isMutation(context.request)) {
        const record = context.request.data?.record || context.request.records?.[0];
        if (record) {
          response = store.cache.didCommit(record, document);

          // a mutation combined with a 204 has no cache impact when no known records were involved
          // a createRecord with a 201 with an empty response and no known records should similarly
          // have no cache impact
        } else if (isCacheAffecting(document)) {
          response = store.cache.put(document);
        }
      } else {
        response = store.cache.put(document);
      }
      response = maybeUpdateUiObjects(store, context.request, {
        shouldHydrate,
        shouldFetch,
        shouldBackgroundFetch,
        identifier
      }, response, false);
    });
    store._enableAsyncFlush = null;
    if (store.lifetimes?.didRequest) {
      store.lifetimes.didRequest(context.request, document.response, identifier, store);
    }
    if (shouldFetch) {
      return response;
    } else if (shouldBackgroundFetch) {
      store.notifications._flush();
    }
  }, error => {
    store.requestManager._pending.delete(context.id);
    if (context.request.signal?.aborted) {
      throw error;
    }
    store.requestManager._pending.delete(context.id);
    store._enableAsyncFlush = true;
    let response;
    store._join(() => {
      if (isMutation(context.request)) {
        // TODO similar to didCommit we should spec this to be similar to cache.put for handling full response
        // currently we let the response remain undefiend.
        const errors = error && error.content && typeof error.content === 'object' && 'errors' in error.content && Array.isArray(error.content.errors) ? error.content.errors : undefined;
        const record = context.request.data?.record || context.request.records?.[0];
        store.cache.commitWasRejected(record, errors);
        // re-throw the original error to preserve `errors` property.
        throw error;
      } else {
        response = store.cache.put(error);
        response = maybeUpdateUiObjects(store, context.request, {
          shouldHydrate,
          shouldFetch,
          shouldBackgroundFetch,
          identifier
        }, response, false);
      }
    });
    store._enableAsyncFlush = null;
    if (identifier && store.lifetimes?.didRequest) {
      store.lifetimes.didRequest(context.request, error.response, identifier, store);
    }
    if (!shouldBackgroundFetch) {
      const newError = cloneError(error);
      newError.content = response;
      throw newError;
    } else {
      store.notifications._flush();
    }
  });
  if (!isMut) {
    return promise;
  }
  assert(`Expected a mutation`, isMutation(context.request));

  // for mutations we need to enqueue the promise with the requestStateService
  // TODO should we enque a request per record in records?
  const record = context.request.data?.record || context.request.records?.[0];
  return store._requestCache._enqueue(promise, {
    data: [{
      op: 'saveRecord',
      recordIdentifier: record,
      options: undefined
    }]
  });
}
function isAggregateError(error) {
  return error instanceof AggregateError || error.name === 'AggregateError' && Array.isArray(error.errors);
}
// TODO @runspired, consider if we should deep freeze errors (potentially only in debug) vs cloning them
function cloneError(error) {
  const isAggregate = isAggregateError(error);
  const cloned = isAggregate ?
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  new AggregateError(structuredClone(error.errors), error.message) : new Error(error.message);
  cloned.stack = error.stack;
  cloned.error = error.error;

  // copy over enumerable properties
  Object.assign(cloned, error);
  return cloned;
}
const SkipCache = Symbol.for('ember-data:skip-cache');
const EnableHydration = Symbol.for('ember-data:enable-hydration');

/**
 * A CacheHandler that adds support for using an EmberData Cache with a RequestManager.
 *
 * This handler will only run when a request has supplied a `store` instance. Requests
 * issued by the store via `store.request()` will automatically have the `store` instance
 * attached to the request.
 *
 * ```ts
 * requestManager.request({
 *   store: store,
 *   url: '/api/posts',
 *   method: 'GET'
 * });
 * ```
 *
 * When this handler elects to handle a request, it will return the raw `StructuredDocument`
 * unless the request has `[EnableHydration]` set to `true`. In this case, the handler will
 * return a `Document` instance that will automatically update the UI when the cache is updated
 * in the future and will hydrate any identifiers in the StructuredDocument into Record instances.
 *
 * When issuing a request via the store, [EnableHydration] is automatically set to `true`. This
 * means that if desired you can issue requests that utilize the cache without needing to also
 * utilize Record instances if desired.
 *
 * Said differently, you could elect to issue all requests via a RequestManager, without ever using
 * the store directly, by setting [EnableHydration] to `true` and providing a store instance. Not
 * necessarily the most useful thing, but the decoupled nature of the RequestManager and incremental-feature
 * approach of EmberData allows for this flexibility.
 *
 * ```ts
 * import { EnableHydration } from '@warp-drive/core-types/request';
 *
 * requestManager.request({
 *   store: store,
 *   url: '/api/posts',
 *   method: 'GET',
 *   [EnableHydration]: true
 * });
 *
 * @typedoc
 */
const CacheHandler = {
  request(context, next) {
    // if we have no cache or no cache-key skip cache handling
    if (!context.request.store || context.request.cacheOptions?.[SkipCache]) {
      return next(context.request);
    }
    const {
      store
    } = context.request;
    const identifier = store.identifierCache.getOrCreateDocumentIdentifier(context.request);
    const peeked = identifier ? store.cache.peekRequest(identifier) : null;

    // determine if we should skip cache
    if (calcShouldFetch(store, context.request, !!peeked, identifier)) {
      return fetchContentAndHydrate(next, context, identifier, true, false);
    }

    // if we have not skipped cache, determine if we should update behind the scenes
    if (calcShouldBackgroundFetch(store, context.request, false, identifier)) {
      const promise = fetchContentAndHydrate(next, context, identifier, false, true);
      store.requestManager._pending.set(context.id, promise);
    }
    const shouldHydrate = context.request[EnableHydration] || false;
    if ('error' in peeked) {
      const content = shouldHydrate ? maybeUpdateUiObjects(store, context.request, {
        shouldHydrate,
        identifier
      }, peeked.content, true) : peeked.content;
      const newError = cloneError(peeked);
      newError.content = content;
      throw newError;
    }
    const result = shouldHydrate ? maybeUpdateUiObjects(store, context.request, {
      shouldHydrate,
      identifier
    }, peeked.content, true) : peeked.content;
    return result;
  }
};
function copyDocumentProperties(target, source) {
  if ('links' in source) {
    target.links = source.links;
  }
  if ('meta' in source) {
    target.meta = source.meta;
  }
  if ('errors' in source) {
    target.errors = source.errors;
  }
}
function isCacheAffecting(document) {
  if (!isMutation(document.request)) {
    return true;
  }
  // a mutation combined with a 204 has no cache impact when no known records were involved
  // a createRecord with a 201 with an empty response and no known records should similarly
  // have no cache impact

  if (document.request.op === 'createRecord' && document.response?.status === 201) {
    return document.content ? Object.keys(document.content).length > 0 : false;
  }
  return document.response?.status !== 204;
}

/*
 * Returns the Cache instance associated with a given
 * Model or Identifier
 */

const CacheForIdentifierCache = new Map();
function setCacheFor(identifier, cache) {
  assert(`Illegal set of identifier`, !CacheForIdentifierCache.has(identifier) || CacheForIdentifierCache.get(identifier) === cache);
  CacheForIdentifierCache.set(identifier, cache);
}
function removeRecordDataFor(identifier) {
  CacheForIdentifierCache.delete(identifier);
}
function peekCache(instance) {
  if (CacheForIdentifierCache.has(instance)) {
    return CacheForIdentifierCache.get(instance);
  }
  return null;
}

/**
  @module @ember-data/store
*/

// Used by the store to normalize IDs entering the store.  Despite the fact
// that developers may provide IDs as numbers (e.g., `store.findRecord('person', 1)`),
// it is important that internally we use strings, since IDs may be serialized
// and lose type information.  For example, Ember's router may put a record's
// ID into the URL, and if we later try to deserialize that URL and find the
// corresponding record, we will not know if it is a string or a number.

function coerceId(id) {
  if (id === null || id === undefined || id === '') {
    return null;
  }
  if (typeof id === 'string') {
    return id;
  }
  if (typeof id === 'symbol') {
    return id.toString();
  }
  return '' + id;
}
function ensureStringId(id) {
  let normalized = null;
  if (typeof id === 'string') {
    normalized = id.length > 0 ? id : null;
  } else if (typeof id === 'number' && !isNaN(id)) {
    normalized = '' + id;
  }
  if (normalized === null) {
    throw new Error(`Expected id to be a string or number, received ${String(id)}`);
  }
  return normalized;
}

// provided for additional debuggability
const DEBUG_CLIENT_ORIGINATED = Symbol('record-originated-on-client');
const DEBUG_IDENTIFIER_BUCKET = Symbol('identifier-bucket');
function isNonEmptyString(str) {
  return str && typeof str === 'string';
}

/**
  @module @ember-data/store
*/

function installPolyfill() {
  const isFastBoot = typeof FastBoot !== 'undefined';
  const CRYPTO = isFastBoot ? FastBoot.require('crypto') : window.crypto;
  if (!CRYPTO.randomUUID) {
    // we might be able to optimize this by requesting more bytes than we need at a time
    const rng = function () {
      // WHATWG crypto RNG - http://wiki.whatwg.org/wiki/Crypto
      let rnds8 = new Uint8Array(16);
      if (!CRYPTO.getRandomValues && !isFastBoot) {
        throw new Error(`Unable to generate bytes for UUID`);
      }
      return CRYPTO.getRandomValues ? CRYPTO.getRandomValues(rnds8) : CRYPTO.randomFillSync(rnds8);
    };

    /*
     * Convert array of 16 byte values to UUID string format of the form:
     * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
     */
    const byteToHex = [];
    for (let i = 0; i < 256; ++i) {
      byteToHex[i] = (i + 0x100).toString(16).substr(1);
    }
    const bytesToUuid = function (buf) {
      let bth = byteToHex;
      // join used to fix memory issue caused by concatenation: https://bugs.chromium.org/p/v8/issues/detail?id=3175#c4
      return [bth[buf[0]], bth[buf[1]], bth[buf[2]], bth[buf[3]], '-', bth[buf[4]], bth[buf[5]], '-', bth[buf[6]], bth[buf[7]], '-', bth[buf[8]], bth[buf[9]], '-', bth[buf[10]], bth[buf[11]], bth[buf[12]], bth[buf[13]], bth[buf[14]], bth[buf[15]]].join('');
    };
    CRYPTO.randomUUID = function uuidv4() {
      let rnds = rng();

      // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`
      rnds[6] = rnds[6] & 0x0f | 0x40;
      rnds[8] = rnds[8] & 0x3f | 0x80;
      return bytesToUuid(rnds);
    };
  }
}

/**
  @module @ember-data/store
*/
const IDENTIFIERS = new Set();
const DOCUMENTS = new Set();
function isStableIdentifier(identifier) {
  return IDENTIFIERS.has(identifier);
}
function isDocumentIdentifier(identifier) {
  return DOCUMENTS.has(identifier);
}
const isFastBoot = typeof FastBoot !== 'undefined';
const _crypto = isFastBoot ? FastBoot.require('crypto') : window.crypto;
if (macroCondition(getOwnConfig().polyfillUUID)) {
  installPolyfill();
}
function uuidv4() {
  assert('crypto.randomUUID needs to be avaliable. Some browsers incorrectly disallow it in insecure contexts. You maybe want to enable the polyfill: https://github.com/emberjs/data#randomuuid-polyfill', _crypto.randomUUID);
  return _crypto.randomUUID();
}
function freeze(obj) {
  if (typeof Object.freeze === 'function') {
    return Object.freeze(obj);
  }
  return obj;
}
let configuredForgetMethod;
let configuredGenerationMethod;
let configuredResetMethod;
let configuredUpdateMethod;
function setIdentifierGenerationMethod(method) {
  configuredGenerationMethod = method;
}
function setIdentifierUpdateMethod(method) {
  configuredUpdateMethod = method;
}
function setIdentifierForgetMethod(method) {
  configuredForgetMethod = method;
}
function setIdentifierResetMethod(method) {
  configuredResetMethod = method;
}
function defaultGenerationMethod(data, bucket) {
  if (bucket === 'record') {
    if (isNonEmptyString(data.lid)) {
      return data.lid;
    }
    if (data.id !== undefined) {
      let {
        type,
        id
      } = data;
      // TODO: add test for id not a string
      if (isNonEmptyString(coerceId(id))) {
        return `@lid:${normalizeModelName$1(type)}-${id}`;
      }
    }
    return uuidv4();
  } else if (bucket === 'document') {
    if (!data.url) {
      return null;
    }
    if (!data.method || data.method.toUpperCase() === 'GET') {
      return data.url;
    }
    return null;
  }
  assert(`Unknown bucket ${bucket}`, false);
}
function defaultEmptyCallback(...args) {}
let DEBUG_MAP;
if (macroCondition(getOwnConfig().env.DEBUG)) {
  DEBUG_MAP = new WeakMap();
}

/**
 * Each instance of {Store} receives a unique instance of a IdentifierCache.
 *
 * This cache is responsible for assigning or retrieving the unique identify
 * for arbitrary resource data encountered by the store. Data representing
 * a unique resource or record should always be represented by the same
 * identifier.
 *
 * It can be configured by consuming applications.
 *
 * @class IdentifierCache
   @public
 */
class IdentifierCache {
  constructor() {
    this._cache = {
      lids: new Map(),
      types: Object.create(null),
      documents: new Map()
    };
    // we cache the user configuredGenerationMethod at init because it must
    // be configured prior and is not allowed to be changed
    this._generate = configuredGenerationMethod || defaultGenerationMethod;
    this._update = configuredUpdateMethod || defaultEmptyCallback;
    this._forget = configuredForgetMethod || defaultEmptyCallback;
    this._reset = configuredResetMethod || defaultEmptyCallback;
    this._merge = defaultEmptyCallback;
    this._isDefaultConfig = !configuredGenerationMethod;
  }

  /**
   * Internal hook to allow management of merge conflicts with identifiers.
   *
   * we allow late binding of this private internal merge so that
   * the cache can insert itself here to handle elimination of duplicates
   *
   * @method __configureMerge
   * @private
   */
  __configureMerge(method) {
    this._merge = method || defaultEmptyCallback;
  }

  /**
   * @method _getRecordIdentifier
   * @private
   */

  _getRecordIdentifier(resource, shouldGenerate = false) {
    // short circuit if we're already the stable version
    if (isStableIdentifier(resource)) {
      if (macroCondition(getOwnConfig().env.DEBUG)) {
        // TODO should we instead just treat this case as a new generation skipping the short circuit?
        if (!this._cache.lids.has(resource.lid) || this._cache.lids.get(resource.lid) !== resource) {
          throw new Error(`The supplied identifier ${resource} does not belong to this store instance`);
        }
      }
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: Peeked Identifier was already Stable ${String(resource)}`);
      }
      return resource;
    }
    let lid = coerceId(resource.lid);
    let identifier = lid !== null ? this._cache.lids.get(lid) : undefined;
    if (identifier !== undefined) {
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: cache HIT ${identifier}`, resource);
      }
      return identifier;
    }
    if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`Identifiers: ${shouldGenerate ? 'Generating' : 'Peeking'} Identifier`, resource);
    }
    if (shouldGenerate === false) {
      if (!resource.type || !resource.id) {
        return;
      }
    }

    // `type` must always be present
    assert('resource.type needs to be a string', 'type' in resource && isNonEmptyString(resource.type));
    let type = resource.type && normalizeModelName$1(resource.type);
    let id = coerceId(resource.id);
    let keyOptions = getTypeIndex(this._cache.types, type);

    // go straight for the stable RecordIdentifier key'd to `lid`
    if (lid !== null) {
      identifier = keyOptions.lid.get(lid);
    }

    // we may have not seen this resource before
    // but just in case we check our own secondary lookup (`id`)
    if (identifier === undefined && id !== null) {
      identifier = keyOptions.id.get(id);
    }
    if (identifier === undefined) {
      // we have definitely not seen this resource before
      // so we allow the user configured `GenerationMethod` to tell us
      let newLid = this._generate(resource, 'record');
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: lid ${newLid} determined for resource`, resource);
      }

      // we do this _even_ when `lid` is present because secondary lookups
      // may need to be populated, but we enforce not giving us something
      // different than expected
      if (lid !== null && newLid !== lid) {
        throw new Error(`You should not change the <lid> of a RecordIdentifier`);
      } else if (lid === null && !this._isDefaultConfig) {
        // allow configuration to tell us that we have
        // seen this `lid` before. E.g. a secondary lookup
        // connects this resource to a previously seen
        // resource.
        identifier = keyOptions.lid.get(newLid);
      }
      if (shouldGenerate === true) {
        if (identifier === undefined) {
          // if we still don't have an identifier, time to generate one
          identifier = makeStableRecordIdentifier(id, type, newLid, 'record', false);

          // populate our unique table
          if (macroCondition(getOwnConfig().env.DEBUG)) {
            // realistically if you hit this it means you changed `type` :/
            // TODO consider how to handle type change assertions more gracefully
            if (this._cache.lids.has(identifier.lid)) {
              throw new Error(`You should not change the <type> of a RecordIdentifier`);
            }
          }
          this._cache.lids.set(identifier.lid, identifier);

          // populate our primary lookup table
          // TODO consider having the `lid` cache be
          // one level up
          keyOptions.lid.set(identifier.lid, identifier);
          if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
            if (shouldGenerate) {
              // eslint-disable-next-line no-console
              console.log(`Identifiers: generated ${String(identifier)} for`, resource);
              if (resource[DEBUG_IDENTIFIER_BUCKET]) {
                // eslint-disable-next-line no-console
                console.trace(`[WARNING] Identifiers: generated a new identifier from a previously used identifier. This is likely a bug.`);
              }
            }
          }
        }

        // populate our own secondary lookup table
        // even for the "successful" secondary lookup
        // by `_generate()`, since we missed the cache
        // previously
        // we use identifier.id instead of id here
        // because they may not match and we prefer
        // what we've set via resource data
        if (identifier.id !== null) {
          keyOptions.id.set(identifier.id, identifier);

          // TODO allow filling out of `id` here
          // for the `username` non-client created
          // case.
        }
      }
    }
    if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      if (!identifier && !shouldGenerate) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: cache MISS`, resource);
      }
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
    return identifier;
  }

  /**
   * allows us to peek without generating when needed
   * useful for the "create" case when we need to see if
   * we are accidentally overwritting something
   *
   * @method peekRecordIdentifier
   * @param resource
   * @returns {StableRecordIdentifier | undefined}
   * @private
   */
  peekRecordIdentifier(resource) {
    return this._getRecordIdentifier(resource, false);
  }

  /**
    Returns the DocumentIdentifier for the given Request, creates one if it does not yet exist.
    Returns `null` if the request does not have a `cacheKey` or `url`.
     @method getOrCreateDocumentIdentifier
    @param request
    @returns {StableDocumentIdentifier | null}
    @public
  */
  getOrCreateDocumentIdentifier(request) {
    let cacheKey = request.cacheOptions?.key;
    if (!cacheKey) {
      cacheKey = this._generate(request, 'document');
    }
    if (!cacheKey) {
      return null;
    }
    let identifier = this._cache.documents.get(cacheKey);
    if (identifier === undefined) {
      identifier = {
        lid: cacheKey
      };
      if (macroCondition(getOwnConfig().env.DEBUG)) {
        Object.freeze(identifier);
      }
      DOCUMENTS.add(identifier);
      this._cache.documents.set(cacheKey, identifier);
    }
    return identifier;
  }

  /**
    Returns the Identifier for the given Resource, creates one if it does not yet exist.
     Specifically this means that we:
     - validate the `id` `type` and `lid` combo against known identifiers
    - return an object with an `lid` that is stable (repeated calls with the same
      `id` + `type` or `lid` will return the same `lid` value)
    - this referential stability of the object itself is guaranteed
     @method getOrCreateRecordIdentifier
    @param resource
    @returns {StableRecordIdentifier}
    @public
  */

  getOrCreateRecordIdentifier(resource) {
    return this._getRecordIdentifier(resource, true);
  }

  /**
   Returns a new Identifier for the supplied data. Call this method to generate
   an identifier when a new resource is being created local to the client and
   potentially does not have an `id`.
    Delegates generation to the user supplied `GenerateMethod` if one has been provided
   with the signature `generateMethod({ type }, 'record')`.
    @method createIdentifierForNewRecord
   @param data
   @returns {StableRecordIdentifier}
   @public
  */
  createIdentifierForNewRecord(data) {
    let newLid = this._generate(data, 'record');
    let identifier = makeStableRecordIdentifier(data.id || null, data.type, newLid, 'record', true);
    let keyOptions = getTypeIndex(this._cache.types, data.type);

    // populate our unique table
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      if (this._cache.lids.has(identifier.lid)) {
        throw new Error(`The lid generated for the new record is not unique as it matches an existing identifier`);
      }
    }
    this._cache.lids.set(identifier.lid, identifier);

    // populate the type+lid cache
    keyOptions.lid.set(newLid, identifier);
    if (data.id) {
      keyOptions.id.set(data.id, identifier);
    }
    if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      // eslint-disable-next-line no-console
      console.log(`Identifiers: created identifier ${String(identifier)} for newly generated resource`, data);
    }
    return identifier;
  }

  /**
   Provides the opportunity to update secondary lookup tables for existing identifiers
   Called after an identifier created with `createIdentifierForNewRecord` has been
   committed.
    Assigned `id` to an `Identifier` if `id` has not previously existed; however,
   attempting to change the `id` or calling update without providing an `id` when
   one is missing will throw an error.
     - sets `id` (if `id` was previously `null`)
    - `lid` and `type` MUST NOT be altered post creation
     If a merge occurs, it is possible the returned identifier does not match the originally
    provided identifier. In this case the abandoned identifier will go through the usual
    `forgetRecordIdentifier` codepaths.
     @method updateRecordIdentifier
    @param identifierObject
    @param data
    @returns {StableRecordIdentifier}
    @public
  */
  updateRecordIdentifier(identifierObject, data) {
    let identifier = this.getOrCreateRecordIdentifier(identifierObject);
    let newId = data.id !== undefined ? coerceId(data.id) : null;
    let existingIdentifier = detectMerge(this._cache.types, identifier, data, newId, this._cache.lids);
    if (!existingIdentifier) {
      // If the incoming type does not match the identifier type, we need to create an identifier for the incoming
      // data so we can merge the incoming data with the existing identifier, see #7325 and #7363
      if (data.type && identifier.type !== normalizeModelName$1(data.type)) {
        let incomingDataResource = {
          ...data
        };
        // Need to strip the lid from the incomingData in order force a new identifier creation
        delete incomingDataResource.lid;
        existingIdentifier = this.getOrCreateRecordIdentifier(incomingDataResource);
      }
    }
    if (existingIdentifier) {
      let keyOptions = getTypeIndex(this._cache.types, identifier.type);
      let generatedIdentifier = identifier;
      identifier = this._mergeRecordIdentifiers(keyOptions, generatedIdentifier, existingIdentifier, data, newId);
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: merged identifiers ${generatedIdentifier.lid} and ${existingIdentifier.lid} for resource into ${identifier.lid}`, data);
      }
    }
    let id = identifier.id;
    performRecordIdentifierUpdate(identifier, data, this._update);
    newId = identifier.id;

    // add to our own secondary lookup table
    if (id !== newId && newId !== null) {
      if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
        // eslint-disable-next-line no-console
        console.log(`Identifiers: updated id for identifier ${identifier.lid} from '${id}' to '${newId}' for resource`, data);
      }
      let keyOptions = getTypeIndex(this._cache.types, identifier.type);
      keyOptions.id.set(newId, identifier);
      if (id !== null) {
        keyOptions.id.delete(id);
      }
    } else if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      // eslint-disable-next-line no-console
      console.log(`Identifiers: updated identifier ${identifier.lid} resource`, data);
    }
    return identifier;
  }

  /**
   * @method _mergeRecordIdentifiers
   * @private
   */
  _mergeRecordIdentifiers(keyOptions, identifier, existingIdentifier, data, newId) {
    // delegate determining which identifier to keep to the configured MergeMethod
    let kept = this._merge(identifier, existingIdentifier, data);
    let abandoned = kept === identifier ? existingIdentifier : identifier;

    // cleanup the identifier we no longer need
    this.forgetRecordIdentifier(abandoned);

    // ensure a secondary cache entry for this id for the identifier we do keep
    keyOptions.id.set(newId, kept);
    // ensure a secondary cache entry for this id for the abandoned identifier's type we do keep
    let baseKeyOptions = getTypeIndex(this._cache.types, existingIdentifier.type);
    baseKeyOptions.id.set(newId, kept);

    // make sure that the `lid` on the data we are processing matches the lid we kept
    data.lid = kept.lid;
    return kept;
  }

  /**
   Provides the opportunity to eliminate an identifier from secondary lookup tables
   as well as eliminates it from ember-data's own lookup tables and book keeping.
    Useful when a record has been deleted and the deletion has been persisted and
   we do not care about the record anymore. Especially useful when an `id` of a
   deleted record might be reused later for a new record.
    @method forgetRecordIdentifier
   @param identifierObject
   @public
  */
  forgetRecordIdentifier(identifierObject) {
    let identifier = this.getOrCreateRecordIdentifier(identifierObject);
    let keyOptions = getTypeIndex(this._cache.types, identifier.type);
    if (identifier.id !== null) {
      keyOptions.id.delete(identifier.id);
    }
    this._cache.lids.delete(identifier.lid);
    keyOptions.lid.delete(identifier.lid);
    IDENTIFIERS.delete(identifierObject);
    this._forget(identifier, 'record');
    if (macroCondition(getOwnConfig().debug.LOG_IDENTIFIERS)) {
      // eslint-disable-next-line no-console
      console.log(`Identifiers: released identifier ${identifierObject.lid}`);
    }
  }
  destroy() {
    this._cache.documents.forEach(identifier => {
      DOCUMENTS.delete(identifier);
    });
    this._reset();
  }
}
function getTypeIndex(typeMap, type) {
  let typeIndex = typeMap[type];
  if (typeIndex === undefined) {
    typeIndex = {
      lid: new Map(),
      id: new Map()
    };
    typeMap[type] = typeIndex;
  }
  return typeIndex;
}
function makeStableRecordIdentifier(id, type, lid, bucket, clientOriginated = false) {
  let recordIdentifier = {
    lid,
    id,
    type
  };
  IDENTIFIERS.add(recordIdentifier);
  if (macroCondition(getOwnConfig().env.DEBUG)) {
    // we enforce immutability in dev
    //  but preserve our ability to do controlled updates to the reference
    let wrapper = {
      get lid() {
        return recordIdentifier.lid;
      },
      get id() {
        return recordIdentifier.id;
      },
      get type() {
        return recordIdentifier.type;
      },
      toString() {
        let {
          type,
          id,
          lid
        } = recordIdentifier;
        return `${clientOriginated ? '[CLIENT_ORIGINATED] ' : ''}${type}:${id} (${lid})`;
      },
      toJSON() {
        let {
          type,
          id,
          lid
        } = recordIdentifier;
        return {
          type,
          id,
          lid
        };
      }
    };
    wrapper[DEBUG_CLIENT_ORIGINATED] = clientOriginated;
    wrapper[DEBUG_IDENTIFIER_BUCKET] = bucket;
    IDENTIFIERS.add(wrapper);
    DEBUG_MAP.set(wrapper, recordIdentifier);
    wrapper = freeze(wrapper);
    return wrapper;
  }
  return recordIdentifier;
}
function performRecordIdentifierUpdate(identifier, data, updateFn) {
  if (macroCondition(getOwnConfig().env.DEBUG)) {
    let {
      lid
    } = data;
    let id = 'id' in data ? data.id : undefined;
    let type = 'type' in data && data.type && normalizeModelName$1(data.type);

    // get the mutable instance behind our proxy wrapper
    let wrapper = identifier;
    identifier = DEBUG_MAP.get(wrapper);
    if (lid !== undefined) {
      let newLid = coerceId(lid);
      if (newLid !== identifier.lid) {
        throw new Error(`The 'lid' for a RecordIdentifier cannot be updated once it has been created. Attempted to set lid for '${wrapper}' to '${lid}'.`);
      }
    }
    if (id !== undefined) {
      let newId = coerceId(id);
      if (identifier.id !== null && identifier.id !== newId) {
        // here we warn and ignore, as this may be a mistake, but we allow the user
        // to have multiple cache-keys pointing at a single lid so we cannot error
        warn(`The 'id' for a RecordIdentifier should not be updated once it has been set. Attempted to set id for '${wrapper}' to '${newId}'.`, false, {
          id: 'ember-data:multiple-ids-for-identifier'
        });
      }
    }

    // TODO consider just ignoring here to allow flexible polymorphic support
    if (type && type !== identifier.type) {
      throw new Error(`The 'type' for a RecordIdentifier cannot be updated once it has been set. Attempted to set type for '${wrapper}' to '${type}'.`);
    }
    updateFn(wrapper, data, 'record');
  } else {
    updateFn(identifier, data, 'record');
  }

  // upgrade the ID, this is a "one time only" ability
  // for the multiple-cache-key scenario we "could"
  // use a heuristic to guess the best id for display
  // (usually when `data.id` is available and `data.attributes` is not)
  if (data.id !== undefined) {
    identifier.id = coerceId(data.id);
  }
}
function detectMerge(typesCache, identifier, data, newId, lids) {
  const {
    id,
    type,
    lid
  } = identifier;
  if (id !== null && id !== newId && newId !== null) {
    let keyOptions = getTypeIndex(typesCache, identifier.type);
    let existingIdentifier = keyOptions.id.get(newId);
    return existingIdentifier !== undefined ? existingIdentifier : false;
  } else {
    let newType = data.type && normalizeModelName$1(data.type);

    // If the ids and type are the same but lid is not the same, we should trigger a merge of the identifiers
    if (id !== null && id === newId && newType === type && data.lid && data.lid !== lid) {
      let existingIdentifier = lids.get(data.lid);
      return existingIdentifier !== undefined ? existingIdentifier : false;
      // If the lids are the same, and ids are the same, but types are different we should trigger a merge of the identifiers
    } else if (id !== null && id === newId && newType && newType !== type && data.lid && data.lid === lid) {
      let keyOptions = getTypeIndex(typesCache, newType);
      let existingIdentifier = keyOptions.id.get(id);
      return existingIdentifier !== undefined ? existingIdentifier : false;
    }
  }
  return false;
}
var _class$2, _descriptor$2;

/**
  @module @ember-data/store
*/
/**
  @module @ember-data/store
*/
/**
   A `RecordReference` is a low-level API that allows users and
   addon authors to perform meta-operations on a record.

   @class RecordReference
   @public
   @extends Reference
*/
let RecordReference = (_class$2 = class RecordReference {
  // unsubscribe token given to us by the notification manager

  constructor(store, identifier) {
    this.___token = void 0;
    this.___identifier = void 0;
    _initializerDefineProperty(this, "_ref", _descriptor$2, this);
    this.store = store;
    this.___identifier = identifier;
    this.___token = store.notifications.subscribe(identifier, (_, bucket, notifiedKey) => {
      if (bucket === 'identity' || bucket === 'attributes' && notifiedKey === 'id') {
        this._ref++;
      }
    });
  }
  destroy() {
    this.store.notifications.unsubscribe(this.___token);
  }
  get type() {
    return this.identifier().type;
  }

  /**
     The `id` of the record that this reference refers to.
      Together, the `type` and `id` properties form a composite key for
     the identity map.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.id(); // '1'
     ```
      @method id
    @public
     @return {String} The id of the record.
  */
  id() {
    this._ref; // consume the tracked prop
    return this.___identifier.id;
  }

  /**
     The `identifier` of the record that this reference refers to.
      Together, the `type` and `id` properties form a composite key for
     the identity map.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.identifier(); // '1'
     ```
      @method identifier
    @public
     @return {String} The identifier of the record.
  */
  identifier() {
    return this.___identifier;
  }

  /**
     How the reference will be looked up when it is loaded. Currently
     this always returns `identity` to signify that a record will be
     loaded by its `type` and `id`.
      Example
      ```javascript
     const userRef = store.getReference('user', 1);
      userRef.remoteType(); // 'identity'
     ```
      @method remoteType
     @public
     @return {String} 'identity'
  */
  remoteType() {
    return 'identity';
  }

  /**
    This API allows you to provide a reference with new data. The
    simplest usage of this API is similar to `store.push`: you provide a
    normalized hash of data and the object represented by the reference
    will update.
     If you pass a promise to `push`, Ember Data will not ask the adapter
    for the data if another attempt to fetch it is made in the
    interim. When the promise resolves, the underlying object is updated
    with the new data, and the promise returned by *this function* is resolved
    with that object.
     For example, `recordReference.push(promise)` will be resolved with a
    record.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // provide data for reference
     userRef.push({
       data: {
         id: "1",
         type: "user",
         attributes: {
           username: "@user"
         }
       }
     }).then(function(user) {
       userRef.value() === user;
     });
     ```
     @method push
    @public
    @param objectOrPromise a JSON:API ResourceDocument or a promise resolving to one
    @return a promise for the value (record or relationship)
  */
  push(objectOrPromise) {
    // TODO @deprecate pushing unresolved payloads
    return Promise.resolve(objectOrPromise).then(data => {
      return this.store.push(data);
    });
  }

  /**
    If the entity referred to by the reference is already loaded, it is
    present as `reference.value`. Otherwise the value returned by this function
    is `null`.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.value(); // user
     ```
      @method value
    @public
     @return {Model} the record for this RecordReference
  */
  value() {
    return this.store.peekRecord(this.___identifier);
  }

  /**
     Triggers a fetch for the backing entity based on its `remoteType`
     (see `remoteType` definitions per reference type).
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // load user (via store.find)
     userRef.load().then(...)
     ```
      @method load
    @public
     @return {Promise<record>} the record for this RecordReference
  */
  load() {
    const id = this.id();
    if (id !== null) {
      return this.store.findRecord(this.type, id);
    }
    assert(`Unable to fetch record of type ${this.type} without an id`);
  }

  /**
     Reloads the record if it is already loaded. If the record is not
     loaded it will load the record via `store.findRecord`
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // or trigger a reload
     userRef.reload().then(...)
     ```
      @method reload
    @public
     @return {Promise<record>} the record for this RecordReference
  */
  reload() {
    const id = this.id();
    if (id !== null) {
      return this.store.findRecord(this.type, id, {
        reload: true
      });
    }
    assert(`Unable to fetch record of type ${this.type} without an id`);
  }
}, _descriptor$2 = _applyDecoratedDescriptor(_class$2.prototype, "_ref", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _class$2);
function legacyCachePut(store, doc) {
  const jsonApiDoc = doc.content;
  let ret;
  store._join(() => {
    let included = jsonApiDoc.included;
    let i, length;
    if (included) {
      for (i = 0, length = included.length; i < length; i++) {
        store._instanceCache.loadData(included[i]);
      }
    }
    if (Array.isArray(jsonApiDoc.data)) {
      length = jsonApiDoc.data.length;
      let identifiers = [];
      for (i = 0; i < length; i++) {
        identifiers.push(store._instanceCache.loadData(jsonApiDoc.data[i]));
      }
      ret = {
        data: identifiers
      };
      return;
    }
    if (jsonApiDoc.data === null) {
      ret = {
        data: null
      };
      return;
    }
    assert(`Expected an object in the 'data' property in a call to 'push', but was ${typeof jsonApiDoc.data}`, typeof jsonApiDoc.data === 'object');
    ret = {
      data: store._instanceCache.loadData(jsonApiDoc.data)
    };
    return;
  });
  return ret;
}

/**
 * The CacheManager wraps a Cache enforcing that only
 * the public API surface area is exposed.
 *
 * Hence, it is the value of `Store.cache`, wrapping
 * the cache instance returned by `Store.createCache`.
 *
 * This class is the the return value of both the
 * `recordDataFor` function supplied to the store
 * hook `instantiateRecord`, and the `recordDataFor`
 * method on the `CacheStoreWrapper`. It is not
 * directly instantiable.
 *
 * It handles translating between cache versions when
 * necessary, for instance when a Store is configured
 * to use both a v1 and a v2 cache depending on some
 * heuristic.
 *
 * Starting with the v2 spec, the cache is designed such
 * that it must be implemented as a singleton. However,
 * because the v1 spec was not designed for this whenever
 * we encounter any v1 cache we must wrap all caches, even
 * singletons, in non-singleton managers to preserve v1
 * compatibility.
 *
 * To avoid this performance penalty being paid by all
 * applications, singleton behavior may be opted-in via
 * the configuration supplied to your Ember application
 * at build time. This effectively removes support for
 * v1 caches.
 *
 * ```js
 * let app = new EmberApp(defaults, {
 *   emberData: {
 *     useSingletonManager: true
 *   },
 * });
 * ```
 *
 * @class CacheManager
 * @public
 */
var _store = /*#__PURE__*/_classPrivateFieldKey("store");
var _cache = /*#__PURE__*/_classPrivateFieldKey("cache");
var _identifier = /*#__PURE__*/_classPrivateFieldKey("identifier");
var _isDeprecated = /*#__PURE__*/_classPrivateFieldKey("isDeprecated");
class NonSingletonCacheManager {
  get managedVersion() {
    return _classPrivateFieldBase(this, _cache)[_cache].version || '1';
  }
  constructor(store, _cache2, identifier) {
    Object.defineProperty(this, _isDeprecated, {
      value: _isDeprecated2
    });
    this.version = '2';
    Object.defineProperty(this, _store, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _cache, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _identifier, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldBase(this, _store)[_store] = store;
    _classPrivateFieldBase(this, _cache)[_cache] = _cache2;
    _classPrivateFieldBase(this, _identifier)[_identifier] = identifier;
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](_cache2)) {
      deprecate(`This RecordData uses the deprecated V1 RecordData Spec. Upgrade to V2 to maintain compatibility.`, false, {
        id: 'ember-data:deprecate-v1-cache',
        until: '5.0',
        since: {
          available: '4.7',
          enabled: '4.7'
        },
        for: 'ember-data'
      });
    }
  }
  // Cache Management
  // ================
  /**
   * Cache the response to a request
   *
   * Unlike `store.push` which has UPSERT
   * semantics, `put` has `replace` semantics similar to
   * the `http` method `PUT`
   *
   * the individually cacheable
   * e resource data it may contain
   * should upsert, but the document data surrounding it should
   * fully replace any existing information
   *
   * Note that in order to support inserting arbitrary data
   * to the cache that did not originate from a request `put`
   * should expect to sometimes encounter a document with only
   * a `content` member and therefor must not assume the existence
   * of `request` and `response` on the document.
   *
   * @method put
   * @param {StructuredDocument} doc
   * @returns {ResourceDocument}
   * @public
   */
  put(doc) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      if (doc instanceof Error) {
        // in legacy we don't know how to handle this
        throw doc;
      }
      return legacyCachePut(_classPrivateFieldBase(this, _store)[_store], doc);
    }
    return cache.put(doc);
  }

  /**
   * Perform an operation on the cache to update the remote state.
   *
   * Note: currently the only valid operation is a MergeOperation
   * which occurs when a collision of identifiers is detected.
   *
   * @method patch
   * @public
   * @param op the operation to perform
   * @returns {void}
   */
  patch(op) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      return;
    }
    cache.patch(op);
  }

  /**
   * Update resource data with a local mutation. Currently supports operations
   * on relationships only.
   *
   * @method mutate
   * @public
   * @param mutation
   */
  // isResource is only needed for interop with v1 cache
  mutate(mutation, isResource) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      const instanceCache = _classPrivateFieldBase(this, _store)[_store]._instanceCache;
      switch (mutation.op) {
        case 'addToRelatedRecords':
          cache.addToHasMany(mutation.field, mutation.value.map(i => instanceCache.getResourceCache(i)), mutation.index);
          return;
        case 'removeFromRelatedRecords':
          cache.removeFromHasMany(mutation.field, mutation.value.map(i => instanceCache.getResourceCache(i)));
          return;
        case 'replaceRelatedRecords':
          cache.setDirtyHasMany(mutation.field, mutation.value.map(i => instanceCache.getResourceCache(i)));
          return;
        case 'replaceRelatedRecord':
          if (isResource) {
            cache.setDirtyBelongsTo(mutation.field, mutation.value ? instanceCache.getResourceCache(mutation.value) : null);
            return;
          }
          cache.removeFromHasMany(mutation.field, [instanceCache.getResourceCache(mutation.prior)]);
          cache.addToHasMany(mutation.field, [instanceCache.getResourceCache(mutation.value)], mutation.index);
          return;
        case 'sortRelatedRecords':
          return;
        default:
          return;
      }
    } else {
      cache.mutate(mutation);
    }
  }

  /**
   * Peek resource data from the Cache.
   *
   * In development, if the return value
   * is JSON the return value
   * will be deep-cloned and deep-frozen
   * to prevent mutation thereby enforcing cache
   * Immutability.
   *
   * This form of peek is useful for implementations
   * that want to feed raw-data from cache to the UI
   * or which want to interact with a blob of data
   * directly from the presentation cache.
   *
   * An implementation might want to do this because
   * de-referencing records which read from their own
   * blob is generally safer because the record does
   * not require retaining connections to the Store
   * and Cache to present data on a per-field basis.
   *
   * This generally takes the place of `getAttr` as
   * an API and may even take the place of `getRelationship`
   * depending on implementation specifics, though this
   * latter usage is less recommended due to the advantages
   * of the Graph handling necessary entanglements and
   * notifications for relational data.
   *
   * @method peek
   * @public
   * @param {StableRecordIdentifier | StableDocumentIdentifier} identifier
   * @returns {ResourceDocument | ResourceBlob | null} the known resource data
   */

  peek(identifier) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      throw new Error(`Expected cache to implement peek`);
    }
    return cache.peek(identifier);
  }

  /**
   * Peek the Cache for the existing request data associated with
   * a cacheable request
   *
   * @method peekRequest
   * @param {StableDocumentIdentifier}
   * @returns {StableDocumentIdentifier | null}
   * @public
   */
  peekRequest(identifier) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      throw new Error(`Expected cache to implement peekRequest`);
    }
    return cache.peekRequest(identifier);
  }

  /**
   * Push resource data from a remote source into the cache for this identifier
   *
   * @method upsert
   * @public
   * @param identifier
   * @param data
   * @param hasRecord
   * @returns {void | string[]} if `hasRecord` is true then calculated key changes should be returned
   */
  upsert(identifier, data, hasRecord) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      data = identifier;
      hasRecord = data;
      identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
    }
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      return cache.pushData(data, hasRecord);
    }
    return cache.upsert(identifier, data, hasRecord);
  }

  // Cache Forking Support
  // =====================

  /**
   * Create a fork of the cache from the current state.
   *
   * Applications should typically not call this method themselves,
   * preferring instead to fork at the Store level, which will
   * utilize this method to fork the cache.
   *
   * @method fork
   * @public
   * @returns Promise<Cache>
   */
  fork() {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      throw new Error(`Expected cache to implement fork`);
    }
    return cache.fork();
  }

  /**
   * Merge a fork back into a parent Cache.
   *
   * Applications should typically not call this method themselves,
   * preferring instead to merge at the Store level, which will
   * utilize this method to merge the caches.
   *
   * @method merge
   * @param {Cache} cache
   * @public
   * @returns Promise<void>
   */
  merge(updates) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      throw new Error(`Expected cache to implement merge`);
    }
    return cache.merge(updates);
  }

  /**
   * Generate the list of changes applied to all
   * record in the store.
   *
   * Each individual resource or document that has
   * been mutated should be described as an individual
   * `Change` entry in the returned array.
   *
   * A `Change` is described by an object containing up to
   * three properties: (1) the `identifier` of the entity that
   * changed; (2) the `op` code of that change being one of
   * `upsert` or `remove`, and if the op is `upsert` a `patch`
   * containing the data to merge into the cache for the given
   * entity.
   *
   * This `patch` is opaque to the Store but should be understood
   * by the Cache and may expect to be utilized by an Adapter
   * when generating data during a `save` operation.
   *
   * It is generally recommended that the `patch` contain only
   * the updated state, ignoring fields that are unchanged
   *
   * ```ts
   * interface Change {
   *  identifier: StableRecordIdentifier | StableDocumentIdentifier;
   *  op: 'upsert' | 'remove';
   *  patch?: unknown;
   * }
   * ```
   *
   * @method diff
   * @public
   */
  diff() {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      throw new Error(`Expected cache to implement diff`);
    }
    return cache.diff();
  }

  // SSR Support
  // ===========

  /**
   * Serialize the entire contents of the Cache into a Stream
   * which may be fed back into a new instance of the same Cache
   * via `cache.hydrate`.
   *
   * @method dump
   * @returns {Promise<ReadableStream>}
   * @public
   */
  dump() {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      throw new Error(`Expected cache to implement dump`);
    }
    return cache.dump();
  }

  /**
   * hydrate a Cache from a Stream with content previously serialized
   * from another instance of the same Cache, resolving when hydration
   * is complete.
   *
   * This method should expect to be called both in the context of restoring
   * the Cache during application rehydration after SSR **AND** at unknown
   * times during the lifetime of an already booted application when it is
   * desired to bulk-load additional information into the cache. This latter
   * behavior supports optimizing pre/fetching of data for route transitions
   * via data-only SSR modes.
   *
   * @method hydrate
   * @param {ReadableStream} stream
   * @returns {Promise<void>}
   * @public
   */
  hydrate(stream) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      throw new Error(`Expected cache to implement hydrate`);
    }
    return cache.hydrate(stream);
  }

  // Cache
  // =====

  /**
   * Retrieve the identifier for this v1 cache
   *
   * DEPRECATED Caches should not be assumed to be 1:1 with resources
   *
   * @method getResourceIdentifier
   * @public
   * @deprecated
   */
  getResourceIdentifier() {
    return _classPrivateFieldBase(this, _identifier)[_identifier];
  }

  /**
   * Push resource data from a remote source into the cache for this identifier
   *
   * DEPRECATED Use upsert. Caches should not be assumed to be 1:1 with resources
   *
   * @method pushData
   * @param data
   * @param hasRecord
   * @returns {void | string[]} if `hasRecord` is true then calculated key changes should be returned
   * @public
   * @deprecated
   */
  pushData(data, hasRecord) {
    return this.upsert(_classPrivateFieldBase(this, _identifier)[_identifier], data, hasRecord);
  }

  // Resource Support
  // ================

  /**
   * [LIFECYCLE] Signal to the cache that a new record has been instantiated on the client
   *
   * It returns properties from options that should be set on the record during the create
   * process. This return value behavior is deprecated.
   *
   * @method clientDidCreate
   * @public
   * @param identifier
   * @param options
   */
  clientDidCreate(identifier, options) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      options = identifier;
      identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
    }
    const cache = _classPrivateFieldBase(this, _cache)[_cache];

    // TODO deprecate return value
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      cache.clientDidCreate();
      // if a V2 is calling a V1 we need to call both methods
      return cache._initRecordCreateOptions(options);
    } else {
      return cache.clientDidCreate(identifier, options);
    }
  }

  /**
   * Pass options to the cache that were supplied to a new record
   * instantiated on the client.
   *
   * DEPRECATED: options are now passed via `clientDidCreate`
   *
   * @method clientDidCreate
   * @public
   * @deprecated
   * @param options
   */
  _initRecordCreateOptions(options) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      return cache._initRecordCreateOptions(options);
    }
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * will be part of a save transaction.
   *
   * @method willCommit
   * @public
   * @param identifier
   */
  willCommit(identifier, context) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
    }
    const cache = _classPrivateFieldBase(this, _cache)[_cache];

    // TODO deprecate return value
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      cache.willCommit();
    } else {
      assert(`Cannot call a v2 cache willCommit from a v1 cache`, !!context);
      cache.willCommit(identifier, context);
    }
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * was successfully updated as part of a save transaction.
   *
   * @method didCommit
   * @public
   * @param identifier
   * @param data
   */
  didCommit(identifier, result) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      // called by something V1
      if (!isStableIdentifier(identifier)) {
        cache.didCommit(identifier);
        return {
          data: _classPrivateFieldBase(this, _identifier)[_identifier]
        };
      }
      cache.didCommit(result.content?.data);
      return {
        data: _classPrivateFieldBase(this, _identifier)[_identifier]
      };
    } else {
      // called by something V1
      if (!isStableIdentifier(identifier)) {
        cache.didCommit(_classPrivateFieldBase(this, _identifier)[_identifier], {
          content: {
            data: identifier
          }
        });
        return {
          data: _classPrivateFieldBase(this, _identifier)[_identifier]
        };
      }
      return cache.didCommit(identifier, result);
    }
  }

  /**
   * [LIFECYCLE] Signals to the cache that a resource
   * was update via a save transaction failed.
   *
   * @method commitWasRejected
   * @public
   * @param identifier
   * @param errors
   */
  commitWasRejected(identifier, errors) {
    _classPrivateFieldBase(this, _cache)[_cache].commitWasRejected(identifier || _classPrivateFieldBase(this, _identifier)[_identifier], errors);
  }

  /**
   * [LIFECYCLE] Signals to the cache that all data for a resource
   * should be cleared.
   *
   * @method unloadRecord
   * @public
   * @param identifier
   */
  unloadRecord(identifier) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      cache.unloadRecord();
    } else {
      cache.unloadRecord(identifier || _classPrivateFieldBase(this, _identifier)[_identifier]);
    }
  }

  // Granular Resource Data APIs
  // ===========================

  /**
   * Retrieve the data for an attribute from the cache
   *
   * @method getAttr
   * @public
   * @param identifier
   * @param propertyName
   * @returns {unknown}
   */
  getAttr(identifier, propertyName) {
    // called by something V1
    if (!isStableIdentifier(identifier)) {
      propertyName = identifier;
      identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
    }
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    return _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.getAttr(propertyName) : cache.getAttr(identifier, propertyName);
  }

  /**
   * Mutate the data for an attribute in the cache
   *
   * @method setAttr
   * @public
   * @param identifier
   * @param propertyName
   * @param value
   */
  setAttr(identifier, propertyName, value) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.setDirtyAttribute(propertyName, value) : cache.setAttr(identifier, propertyName, value);
  }

  /**
   * Mutate the data for an attribute in the cache
   *
   * DEPRECATED use setAttr
   *
   * @method setDirtyAttribute
   * @public
   * @deprecated
   * @param identifier
   * @param propertyName
   * @param value
   */
  setDirtyAttribute(propertyName, value) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.setDirtyAttribute(propertyName, value) : cache.setAttr(_classPrivateFieldBase(this, _identifier)[_identifier], propertyName, value);
  }

  /**
   * Query the cache for the changed attributes of a resource.
   *
   * DEPRECATED use changedAttrs
   *
   * @method changedAttributes
   * @public
   * @deprecated
   * @param identifier
   * @returns
   */
  changedAttributes() {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      return cache.changedAttributes();
    }
    return cache.changedAttrs(_classPrivateFieldBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for the changed attributes of a resource.
   *
   * @method changedAttrs
   * @public
   * @deprecated
   * @param identifier
   * @returns
   */
  changedAttrs(identifier) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      return cache.changedAttributes();
    }
    return cache.changedAttrs(identifier);
  }

  /**
   * Query the cache for whether any mutated attributes exist
   *
   * DEPRECATED use hasChangedAttrs
   *
   * @method hasChangedAttributes
   * @public
   * @deprecated
   * @returns {boolean}
   */
  hasChangedAttributes() {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    return _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.hasChangedAttributes() : cache.hasChangedAttrs(_classPrivateFieldBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether any mutated attributes exist
   *
   * @method hasChangedAttrs
   * @public
   * @param identifier
   * @returns {boolean}
   */
  hasChangedAttrs(identifier) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    return _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.hasChangedAttributes() : cache.hasChangedAttrs(identifier);
  }

  /**
   * Tell the cache to discard any uncommitted mutations to attributes
   *
   * DEPRECATED use rollbackAttrs
   *
   * @method rollbackAttributes
   * @public
   * @deprecated
   * @returns
   */
  rollbackAttributes() {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    return _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.rollbackAttributes() : cache.rollbackAttrs(_classPrivateFieldBase(this, _identifier)[_identifier]);
  }

  /**
   * Tell the cache to discard any uncommitted mutations to attributes
   *
   * @method rollbackAttrs
   * @public
   * @param identifier
   * @returns the names of attributes that were restored
   */
  rollbackAttrs(identifier) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    return _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.rollbackAttributes() : cache.rollbackAttrs(identifier);
  }

  // Relationships
  // =============

  // the third arg here is "private". In a world with only V2 it is not necessary
  // but in one in which we must convert a call from V2 -> V1 it is required to do this
  // or else to do nasty schema lookup things
  // @runspired has implemented this concept in relationships spikes and is confident
  // we do not need any signal about whether a relationship is a collection or not at this
  // boundary
  /**
   * Query the cache for the current state of a relationship property
   *
   * @method getRelationship
   * @public
   * @param identifier
   * @param propertyName
   * @returns resource relationship object
   */
  getRelationship(identifier, propertyName, isCollection = false) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      let isBelongsTo = !isCollection;
      return isBelongsTo ? cache.getBelongsTo(propertyName) : cache.getHasMany(propertyName);
    }
    return cache.getRelationship(identifier, propertyName);
  }

  /**
   * Query the cache for the current state of a belongsTo field
   *
   * DEPRECATED use `getRelationship`
   *
   * @method getBelongsTo
   * @public
   * @deprecated
   * @param propertyName
   * @returns single resource relationship object
   */
  getBelongsTo(propertyName) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      return cache.getBelongsTo(propertyName);
    } else {
      let identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
      return cache.getRelationship(identifier, propertyName);
    }
  }

  /**
   * Query the cache for the current state of a hasMany field
   *
   * DEPRECATED use `getRelationship`
   *
   * @method getHasMany
   * @public
   * @deprecated
   * @param propertyName
   * @returns single resource relationship object
   */
  getHasMany(propertyName) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    if (_classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache)) {
      return cache.getHasMany(propertyName);
    } else {
      let identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
      return cache.getRelationship(identifier, propertyName);
    }
  }

  /**
   * Mutate the current state of a belongsTo relationship
   *
   * DEPRECATED use update
   *
   * @method setDirtyBelongsTo
   * @public
   * @deprecated
   * @param propertyName
   * @param value
   */
  setDirtyBelongsTo(propertyName, value) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.setDirtyBelongsTo(propertyName, value) : cache.mutate({
      op: 'replaceRelatedRecord',
      record: _classPrivateFieldBase(this, _identifier)[_identifier],
      field: propertyName,
      value: value ? value.getResourceIdentifier() : null
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by adding values
   * An index may optionally be specified which the cache should use for
   * where in the list to insert the records
   *
   * DEPRECATED use update
   *
   * @method addToHasMany
   * @deprecated
   * @public
   * @param propertyName
   * @param value
   * @param idx
   */
  addToHasMany(propertyName, value, idx) {
    const identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.addToHasMany(propertyName, value, idx) : cache.mutate({
      op: 'addToRelatedRecords',
      field: propertyName,
      record: identifier,
      value: value.map(v => v.getResourceIdentifier())
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by removing values.
   *
   * DEPRECATED use update
   *
   * @method removeFromHasMany
   * @deprecated
   * @public
   * @param propertyName
   * @param value
   */
  removeFromHasMany(propertyName, value) {
    const identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.removeFromHasMany(propertyName, value) : cache.mutate({
      op: 'removeFromRelatedRecords',
      record: identifier,
      field: propertyName,
      value: value.map(v => v.getResourceIdentifier())
    });
  }

  /**
   * Mutate the current state of a hasMany relationship by replacing it entirely
   *
   * DEPRECATED use `setHasMany`
   *
   * @method setDirtyHasMany
   * @public
   * @deprecated
   * @param propertyName
   * @param value
   */
  setDirtyHasMany(propertyName, value) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.setDirtyHasMany(propertyName, value) : cache.mutate({
      op: 'replaceRelatedRecords',
      record: _classPrivateFieldBase(this, _identifier)[_identifier],
      field: propertyName,
      value: value.map(rd => rd.getResourceIdentifier())
    });
  }

  // Resource State
  // ===============

  /**
   * Update the cache state for the given resource to be marked as locally deleted,
   * or remove such a mark.
   *
   * @method setIsDeleted
   * @public
   * @param identifier
   * @param isDeleted
   */
  setIsDeleted(identifier, isDeleted) {
    if (!isStableIdentifier(identifier)) {
      isDeleted = identifier;
      identifier = _classPrivateFieldBase(this, _identifier)[_identifier];
    }
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.setIsDeleted(isDeleted) : cache.setIsDeleted(identifier, isDeleted);
  }

  /**
   * Query the cache for any validation errors applicable to the given resource.
   *
   * @method getErrors
   * @public
   * @param identifier
   * @returns
   */
  getErrors(identifier) {
    return _classPrivateFieldBase(this, _cache)[_cache].getErrors(identifier || _classPrivateFieldBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource has any available data
   *
   * @method isEmpty
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isEmpty(identifier) {
    const cache = _classPrivateFieldBase(this, _cache)[_cache];
    return _classPrivateFieldBase(this, _isDeprecated)[_isDeprecated](cache) ? cache.isEmpty?.(identifier || _classPrivateFieldBase(this, _identifier)[_identifier]) || false : cache.isEmpty(identifier || _classPrivateFieldBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource was created locally and not
   * yet persisted.
   *
   * @method isNew
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isNew(identifier) {
    return _classPrivateFieldBase(this, _cache)[_cache].isNew(identifier || _classPrivateFieldBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource is marked as deleted (but not
   * necessarily persisted yet).
   *
   * @method isDeleted
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isDeleted(identifier) {
    return _classPrivateFieldBase(this, _cache)[_cache].isDeleted(identifier || _classPrivateFieldBase(this, _identifier)[_identifier]);
  }

  /**
   * Query the cache for whether a given resource has been deleted and that deletion
   * has also been persisted.
   *
   * @method isDeletionCommitted
   * @public
   * @param identifier
   * @returns {boolean}
   */
  isDeletionCommitted(identifier) {
    return _classPrivateFieldBase(this, _cache)[_cache].isDeletionCommitted(identifier || _classPrivateFieldBase(this, _identifier)[_identifier]);
  }
}
function _isDeprecated2(cache) {
  let version = cache.version || '1';
  return version !== this.version;
}
var _cache3 = /*#__PURE__*/_classPrivateFieldKey("cache");
class SingletonCacheManager {
  constructor(cache) {
    this.version = '2';
    Object.defineProperty(this, _cache3, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldBase(this, _cache3)[_cache3] = cache;
  }
  put(doc) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].put(doc);
  }
  peek(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].peek(identifier);
  }
  peekRequest(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].peekRequest(identifier);
  }
  fork() {
    return _classPrivateFieldBase(this, _cache3)[_cache3].fork();
  }
  merge(cache) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].merge(cache);
  }
  diff() {
    return _classPrivateFieldBase(this, _cache3)[_cache3].diff();
  }
  dump() {
    return _classPrivateFieldBase(this, _cache3)[_cache3].dump();
  }
  hydrate(stream) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].hydrate(stream);
  }

  // Cache
  // =====

  upsert(identifier, data, hasRecord) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].upsert(identifier, data, hasRecord);
  }
  patch(op) {
    _classPrivateFieldBase(this, _cache3)[_cache3].patch(op);
  }
  clientDidCreate(identifier, options) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].clientDidCreate(identifier, options);
  }
  willCommit(identifier, context) {
    _classPrivateFieldBase(this, _cache3)[_cache3].willCommit(identifier, context);
  }
  didCommit(identifier, result) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].didCommit(identifier, result);
  }
  commitWasRejected(identifier, errors) {
    _classPrivateFieldBase(this, _cache3)[_cache3].commitWasRejected(identifier, errors);
  }
  unloadRecord(identifier) {
    _classPrivateFieldBase(this, _cache3)[_cache3].unloadRecord(identifier);
  }

  // Attrs
  // =====

  getAttr(identifier, propertyName) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].getAttr(identifier, propertyName);
  }
  setAttr(identifier, propertyName, value) {
    _classPrivateFieldBase(this, _cache3)[_cache3].setAttr(identifier, propertyName, value);
  }
  changedAttrs(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].changedAttrs(identifier);
  }
  hasChangedAttrs(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].hasChangedAttrs(identifier);
  }
  rollbackAttrs(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].rollbackAttrs(identifier);
  }
  getRelationship(identifier, propertyName) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].getRelationship(identifier, propertyName);
  }
  mutate(mutation) {
    _classPrivateFieldBase(this, _cache3)[_cache3].mutate(mutation);
  }

  // State
  // =============

  setIsDeleted(identifier, isDeleted) {
    _classPrivateFieldBase(this, _cache3)[_cache3].setIsDeleted(identifier, isDeleted);
  }
  getErrors(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].getErrors(identifier);
  }
  isEmpty(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].isEmpty(identifier);
  }
  isNew(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].isNew(identifier);
  }
  isDeleted(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].isDeleted(identifier);
  }
  isDeletionCommitted(identifier) {
    return _classPrivateFieldBase(this, _cache3)[_cache3].isDeletionCommitted(identifier);
  }
}
function constructResource(type, id, lid) {
  if (typeof type === 'object' && type !== null) {
    let resource = type;
    if (isStableIdentifier(resource)) {
      return resource;
    }
    if ('id' in resource) {
      resource.id = coerceId(resource.id);
    }
    assert('Expected either id or lid to be a valid string', 'id' in resource && isNonEmptyString(resource.id) || isNonEmptyString(resource.lid));
    assert('if id is present, the type must be a string', !('id' in resource) || typeof resource.type === 'string');
    return resource;
  } else {
    const trueId = coerceId(id);
    if (!isNonEmptyString(trueId)) {
      if (isNonEmptyString(lid)) {
        return {
          lid
        };
      }
      throw new Error('Expected either id or lid to be a valid string');
    }
    assert('type must be a string', typeof type === 'string');
    if (isNonEmptyString(lid)) {
      return {
        type,
        id: trueId,
        lid
      };
    }
    return {
      type,
      id: trueId
    };
  }
}

/**
  @module @ember-data/store
*/

class LegacyWrapper {
  constructor(_store) {
    this._store = _store;
    this._willNotify = false;
    this._pendingNotifies = new Map();
  }
  get identifierCache() {
    return this._store.identifierCache;
  }
  _scheduleNotification(identifier, key) {
    let pending = this._pendingNotifies.get(identifier);
    if (!pending) {
      pending = new Set();
      this._pendingNotifies.set(identifier, pending);
    }
    pending.add(key);
    if (this._willNotify === true) {
      return;
    }
    this._willNotify = true;
    // it's possible a RecordData adhoc notifies us,
    // in which case we sync flush
    if (this._store._cbs) {
      this._store._schedule('notify', () => this._flushNotifications());
    } else {
      this._flushNotifications();
    }
  }
  _flushNotifications() {
    if (this._willNotify === false) {
      return;
    }
    let pending = this._pendingNotifies;
    this._pendingNotifies = new Map();
    this._willNotify = false;
    pending.forEach((set, identifier) => {
      set.forEach(key => {
        this._store.notifications.notify(identifier, 'relationships', key);
      });
    });
  }
  notifyChange(identifier, namespace, key) {
    assert(`Expected a stable identifier`, isStableIdentifier(identifier) || isDocumentIdentifier(identifier));

    // TODO do we still get value from this?
    if (namespace === 'relationships' && key) {
      this._scheduleNotification(identifier, key);
      return;
    }

    // @ts-expect-error
    this._store.notifications.notify(identifier, namespace, key);
  }
  notifyErrorsChange(type, id, lid) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      deprecate(`StoreWrapper.notifyErrorsChange has been deprecated in favor of StoreWrapper.notifyChange`, false, {
        id: 'ember-data:deprecate-v1cache-store-apis',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        }
      });
    }
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._store.notifications.notify(identifier, 'errors');
  }
  attributesDefinitionFor(type) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      deprecate(`StoreWrapper.attributesDefinitionFor has been deprecated in favor of StoreWrapper.getSchemaDefinitionService().attributesDefinitionFor`, false, {
        id: 'ember-data:deprecate-v1cache-store-apis',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        }
      });
    }
    return this._store.getSchemaDefinitionService().attributesDefinitionFor({
      type
    });
  }
  relationshipsDefinitionFor(type) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      deprecate(`StoreWrapper.relationshipsDefinitionFor has been deprecated in favor of StoreWrapper.getSchemaDefinitionService().relationshipsDefinitionFor`, false, {
        id: 'ember-data:deprecate-v1cache-store-apis',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        }
      });
    }
    return this._store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type
    });
  }
  getSchemaDefinitionService() {
    return this._store.getSchemaDefinitionService();
  }
  notifyPropertyChange(type, id, lid, key) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      deprecate(`StoreWrapper.notifyPropertyChange has been deprecated in favor of StoreWrapper.notifyChange`, false, {
        id: 'ember-data:deprecate-v1cache-store-apis',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        }
      });
    }
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._store.notifications.notify(identifier, 'attributes', key);
  }
  notifyHasManyChange(type, id, lid, key) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      deprecate(`StoreWrapper.notifyHasManyChange has been deprecated in favor of StoreWrapper.notifyChange`, false, {
        id: 'ember-data:deprecate-v1cache-store-apis',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        }
      });
    }
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._scheduleNotification(identifier, key);
  }
  notifyBelongsToChange(type, id, lid, key) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      deprecate(`StoreWrapper.notifyBelongsToChange has been deprecated in favor of StoreWrapper.notifyChange`, false, {
        id: 'ember-data:deprecate-v1cache-store-apis',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        }
      });
    }
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._scheduleNotification(identifier, key);
  }
  notifyStateChange(type, id, lid, key) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      deprecate(`StoreWrapper.notifyStateChange has been deprecated in favor of StoreWrapper.notifyChange`, false, {
        id: 'ember-data:deprecate-v1cache-store-apis',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        }
      });
    }
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    this._store.notifications.notify(identifier, 'state');
  }
  recordDataFor(type, id, lid) {
    let identifier;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      if (!isStableIdentifier(type)) {
        // we also deprecate create capability. This behavior was problematic because
        // there's no outside association between this RecordData and an Identifier.
        // It's likely a mistake when we hit this code-path, but we said in an early
        // RFC we'd allow this.
        // With V2 we are enforcing someone to use the record-data and identifier-cache APIs to
        // create a new identifier and then call clientDidCreate on the RecordData
        // instead.
        identifier = !id && !lid ? this.identifierCache.createIdentifierForNewRecord({
          type: type
        }) : this.identifierCache.getOrCreateRecordIdentifier(constructResource(type, id, lid));
      } else {
        identifier = type;
      }
    } else {
      assert(`Expected a stable identifier`, isStableIdentifier(type));
      identifier = type;
    }
    const cache = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? this._store._instanceCache.getResourceCache(identifier) : this._store.cache;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      if (!id && !lid && typeof type === 'string') {
        cache.clientDidCreate(identifier);
        this._store.recordArrayManager.identifierAdded(identifier);
      }
    }
    return cache;
  }
  setRecordId(type, id, lid) {
    let identifier;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      if (!isStableIdentifier(type)) {
        const modelName = normalizeModelName$1(type);
        const resource = constructResource(modelName, null, coerceId(lid));
        identifier = this.identifierCache.peekRecordIdentifier(resource);
      } else {
        identifier = type;
      }
    } else {
      assert(`Expected a stable identifier`, isStableIdentifier(type));
      identifier = type;
    }
    assert(`Unable to find an identifier to update the ID for for ${String(lid)}`, identifier);
    this._store._instanceCache.setRecordId(identifier, id);
  }
  isRecordInUse(type, id, lid) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      deprecate(`StoreWrapper.isRecordInUSe has been deprecated in favor of StoreWrapper.hasRecord`, false, {
        id: 'ember-data:deprecate-v1cache-store-apis',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        }
      });
    }
    const resource = constructResource(type, id, lid);
    const identifier = this.identifierCache.peekRecordIdentifier(resource);
    const record = identifier && this._store._instanceCache.peek({
      identifier,
      bucket: 'record'
    });
    return record ? !(record.isDestroyed || record.isDestroying) : false;
  }
  hasRecord(identifier) {
    return Boolean(this._store._instanceCache.peek({
      identifier,
      bucket: 'record'
    }));
  }
  disconnectRecord(type, id, lid) {
    let identifier;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
      if (typeof type === 'string') {
        deprecate(`StoreWrapper.disconnectRecord(<type>) has been deprecated in favor of StoreWrapper.disconnectRecord(<identifier>)`, false, {
          id: 'ember-data:deprecate-v1cache-store-apis',
          for: 'ember-data',
          until: '5.0',
          since: {
            enabled: '4.7',
            available: '4.7'
          }
        });
        let resource = constructResource(type, id, lid);
        identifier = this.identifierCache.peekRecordIdentifier(resource);
      } else {
        identifier = type;
      }
    } else {
      identifier = type;
    }
    assert(`Expected a stable identifier`, isStableIdentifier(identifier));
    this._store._instanceCache.disconnect(identifier);
    this._pendingNotifies.delete(identifier);
  }
}
class V2CacheStoreWrapper {
  constructor(_store) {
    this._store = _store;
    this._willNotify = false;
    this._pendingNotifies = new Map();
  }
  get identifierCache() {
    return this._store.identifierCache;
  }
  _scheduleNotification(identifier, key) {
    let pending = this._pendingNotifies.get(identifier);
    if (!pending) {
      pending = new Set();
      this._pendingNotifies.set(identifier, pending);
    }
    pending.add(key);
    if (this._willNotify === true) {
      return;
    }
    this._willNotify = true;
    // it's possible a cache adhoc notifies us,
    // in which case we sync flush
    if (this._store._cbs) {
      this._store._schedule('notify', () => this._flushNotifications());
    } else {
      // TODO @runspired determine if relationship mutations should schedule
      // into join/run vs immediate flush
      this._flushNotifications();
    }
  }
  _flushNotifications() {
    if (this._willNotify === false) {
      return;
    }
    let pending = this._pendingNotifies;
    this._pendingNotifies = new Map();
    this._willNotify = false;
    pending.forEach((set, identifier) => {
      set.forEach(key => {
        this._store.notifications.notify(identifier, 'relationships', key);
      });
    });
  }
  notifyChange(identifier, namespace, key) {
    assert(`Expected a stable identifier`, isStableIdentifier(identifier) || isDocumentIdentifier(identifier));

    // TODO do we still get value from this?
    if (namespace === 'relationships' && key) {
      this._scheduleNotification(identifier, key);
      return;
    }

    // @ts-expect-error
    this._store.notifications.notify(identifier, namespace, key);
  }
  getSchemaDefinitionService() {
    return this._store.getSchemaDefinitionService();
  }
  recordDataFor(identifier) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA)) {
      deprecate(`StoreWrapper.recordDataFor is deprecated. With Singleton Cache, this method is no longer needed as the caller is its own cache reference.`, false, {
        for: '@ember-data/store',
        id: 'ember-data:deprecate-record-data-for',
        since: {
          available: '4.10',
          enabled: '4.10'
        },
        until: '5.0'
      });
    }
    assert(`Expected a stable identifier`, isStableIdentifier(identifier));
    return macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? this._store._instanceCache.getResourceCache(identifier) : void 0;
  }
  setRecordId(identifier, id) {
    assert(`Expected a stable identifier`, isStableIdentifier(identifier));
    this._store._instanceCache.setRecordId(identifier, id);
  }
  hasRecord(identifier) {
    return Boolean(this._store._instanceCache.peek({
      identifier,
      bucket: 'record'
    }));
  }
  disconnectRecord(identifier) {
    assert(`Expected a stable identifier`, isStableIdentifier(identifier));
    this._store._instanceCache.disconnect(identifier);
    this._pendingNotifies.delete(identifier);
  }
}
const CacheStoreWrapper = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS) ? LegacyWrapper : V2CacheStoreWrapper;
let _peekGraph;
if (macroCondition(getOwnConfig().packages.HAS_GRAPH_PACKAGE)) {
  let __peekGraph;
  _peekGraph = wrapper => {
    let a = importSync('@ember-data/graph/-private').peekGraph;
    __peekGraph = __peekGraph || a;
    return __peekGraph(wrapper);
  };
}

/**
  @module @ember-data/store
*/

const RecordCache = new Map();
function peekRecordIdentifier(record) {
  return RecordCache.get(record);
}

/**
  Retrieves the unique referentially-stable [RecordIdentifier](/ember-data/release/classes/StableRecordIdentifier)
  assigned to the given record instance.
  ```js
  import { recordIdentifierFor } from "@ember-data/store";
  // ... gain access to a record, for instance with peekRecord or findRecord
  const record = store.peekRecord("user", "1");
  // get the identifier for the record (see docs for StableRecordIdentifier)
  const identifier = recordIdentifierFor(record);
  // access the identifier's properties.
  const { id, type, lid } = identifier;
  ```
  @method recordIdentifierFor
  @public
  @static
  @for @ember-data/store
  @param {Object} record a record instance previously obstained from the store.
  @returns {StableRecordIdentifier}
 */
function recordIdentifierFor(record) {
  assert(`${String(record)} is not a record instantiated by @ember-data/store`, RecordCache.has(record));
  return RecordCache.get(record);
}
function setRecordIdentifier(record, identifier) {
  if (macroCondition(getOwnConfig().env.DEBUG)) {
    if (RecordCache.has(record) && RecordCache.get(record) !== identifier) {
      throw new Error(`${String(record)} was already assigned an identifier`);
    }
  }

  /*
  It would be nice to do a reverse check here that an identifier has not
  previously been assigned a record; however, unload + rematerialization
  prevents us from having a great way of doing so when CustomRecordClasses
  don't necessarily give us access to a `isDestroyed` for dematerialized
  instance.
  */

  RecordCache.set(record, identifier);
}
const StoreMap = new Map();
function storeFor(record) {
  const store = StoreMap.get(record);
  assert(`A record in a disconnected state cannot utilize the store. This typically means the record has been destroyed, most commonly by unloading it.`, store);
  return store;
}
class InstanceCache {
  constructor(store) {
    this.__instances = {
      record: new Map(),
      resourceCache: new Map(),
      reference: new WeakMap()
    };
    this.store = store;
    this._storeWrapper = new CacheStoreWrapper(this.store);
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA)) {
      this.__cacheFor = resource => {
        // TODO enforce strict
        const identifier = this.store.identifierCache.getOrCreateRecordIdentifier(resource);
        return this.getResourceCache(identifier);
      };
    }
    store.identifierCache.__configureMerge((identifier, matchedIdentifier, resourceData) => {
      let keptIdentifier = identifier;
      if (identifier.id !== matchedIdentifier.id) {
        keptIdentifier = 'id' in resourceData && identifier.id === resourceData.id ? identifier : matchedIdentifier;
      } else if (identifier.type !== matchedIdentifier.type) {
        keptIdentifier = 'type' in resourceData && identifier.type === resourceData.type ? identifier : matchedIdentifier;
      }
      let staleIdentifier = identifier === keptIdentifier ? matchedIdentifier : identifier;

      // check for duplicate entities
      let keptHasRecord = this.__instances.record.has(keptIdentifier);
      let staleHasRecord = this.__instances.record.has(staleIdentifier);
      let keptResourceCache = this.__instances.resourceCache.get(keptIdentifier) || null;
      let staleResourceCache = this.__instances.resourceCache.get(staleIdentifier) || null;

      // we cannot merge entities when both have records
      // (this may not be strictly true, we could probably swap the cache data the record points at)
      if (keptHasRecord && staleHasRecord) {
        // TODO we probably don't need to throw these errors anymore
        // we can probably just "swap" what data source the abandoned
        // record points at so long as
        // it itself is not retained by the store in any way.
        if ('id' in resourceData) {
          throw new Error(`Failed to update the 'id' for the RecordIdentifier '${identifier.type}:${String(identifier.id)} (${identifier.lid})' to '${String(resourceData.id)}', because that id is already in use by '${matchedIdentifier.type}:${String(matchedIdentifier.id)} (${matchedIdentifier.lid})'`);
        }
        assert(`Failed to update the RecordIdentifier '${identifier.type}:${String(identifier.id)} (${identifier.lid})' to merge with the detected duplicate identifier '${matchedIdentifier.type}:${String(matchedIdentifier.id)} (${String(matchedIdentifier.lid)})'`);
      }
      let resourceCache = keptResourceCache || staleResourceCache;
      if (resourceCache) {
        resourceCache.patch({
          op: 'mergeIdentifiers',
          record: staleIdentifier,
          value: keptIdentifier
        });
      } else if (macroCondition(!getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA)) {
        this.store.cache.patch({
          op: 'mergeIdentifiers',
          record: staleIdentifier,
          value: keptIdentifier
        });
      } else if (macroCondition(getOwnConfig().packages.HAS_JSON_API_PACKAGE)) {
        this.store.cache.patch({
          op: 'mergeIdentifiers',
          record: staleIdentifier,
          value: keptIdentifier
        });
      }
      if (staleResourceCache === null) {
        return keptIdentifier;
      }

      /*
      TODO @runspired consider adding this to make polymorphism even nicer
      if (HAS_GRAPH_PACKAGE) {
      if (identifier.type !== matchedIdentifier.type) {
        const graphFor = importSync('@ember-data/graph/-private').graphFor;
        graphFor(this).registerPolymorphicType(identifier.type, matchedIdentifier.type);
      }
      }
      */

      this.unloadRecord(staleIdentifier);
      return keptIdentifier;
    });
  }
  peek({
    identifier,
    bucket
  }) {
    return this.__instances[bucket]?.get(identifier);
  }
  getRecord(identifier, properties) {
    let record = this.__instances.record.get(identifier);
    if (!record) {
      assert(`Cannot create a new record instance while the store is being destroyed`, !this.store.isDestroying && !this.store.isDestroyed);
      const cache = this.getResourceCache(identifier);
      if (macroCondition(getOwnConfig().deprecations.DEPRECATE_INSTANTIATE_RECORD_ARGS)) {
        if (this.store.instantiateRecord.length > 2) {
          deprecate(`Expected store.instantiateRecord to have an arity of 2. recordDataFor and notificationManager args have been deprecated.`, false, {
            for: '@ember-data/store',
            id: 'ember-data:deprecate-instantiate-record-args',
            since: {
              available: '4.12',
              enabled: '4.12'
            },
            until: '5.0'
          });
        }
        assert(`Cannot create a record for ${identifier.type + ':' + String(identifier.id)} (${identifier.lid}) as no resource data exists`,
        // @ts-expect-error managedVersion is private and debug only
        Boolean(cache.managedVersion === '1' || cache.peek(identifier)));
        record = this.store.instantiateRecord(identifier, properties || {},
        // @ts-expect-error
        this.__cacheFor, this.store.notifications);
      } else {
        assert(`Cannot create a record for ${identifier.type + ':' + String(identifier.id)} (${identifier.lid}) as no resource data exists`, cache.peek(identifier));
        record = this.store.instantiateRecord(identifier, properties || {});
      }
      setRecordIdentifier(record, identifier);
      setCacheFor(record, cache);
      StoreMap.set(record, this.store);
      this.__instances.record.set(identifier, record);
      if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
        // eslint-disable-next-line no-console
        console.log(`InstanceCache: created Record for ${String(identifier)}`, properties);
      }
    }
    return record;
  }
  getResourceCache(identifier) {
    if (macroCondition(!getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA)) {
      const cache = this.store.cache;
      setCacheFor(identifier, cache);
      this.__instances.resourceCache.set(identifier, cache);
      return cache;
    }
    let cache = this.__instances.resourceCache.get(identifier);
    if (cache) {
      return cache;
    }
    if (this.store.createRecordDataFor) {
      deprecate(`Store.createRecordDataFor(<type>, <id>, <lid>, <storeWrapper>) has been deprecated in favor of Store.createCache(<storeWrapper>)`, false, {
        id: 'ember-data:deprecate-v1-cache',
        for: 'ember-data',
        until: '5.0',
        since: {
          enabled: '4.12',
          available: '4.12'
        }
      });
      if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1CACHE_STORE_APIS)) {
        if (this.store.createRecordDataFor.length > 2) {
          let cacheInstance = this.store.createRecordDataFor(identifier.type, identifier.id,
          // @ts-expect-error
          identifier.lid, this._storeWrapper);
          cache = new NonSingletonCacheManager(this.store, cacheInstance, identifier);
        }
      }
      if (!cache) {
        let cacheInstance = this.store.createRecordDataFor(identifier, this._storeWrapper);
        cache = cacheInstance.version === '2' ? cacheInstance : new NonSingletonCacheManager(this.store, cacheInstance, identifier);
      }
    } else {
      cache = this.store.cache;
    }
    setCacheFor(identifier, cache);
    this.__instances.resourceCache.set(identifier, cache);
    if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
      // eslint-disable-next-line no-console
      console.log(`InstanceCache: created Cache for ${String(identifier)}`);
    }
    return cache;
  }
  getReference(identifier) {
    let cache = this.__instances.reference;
    let reference = cache.get(identifier);
    if (!reference) {
      reference = new RecordReference(this.store, identifier);
      cache.set(identifier, reference);
    }
    return reference;
  }
  recordIsLoaded(identifier, filterDeleted = false) {
    const cache = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? this.__instances.resourceCache.get(identifier) || this.cache : this.cache;
    if (!cache) {
      return false;
    }
    const isNew = cache.isNew(identifier);
    const isEmpty = cache.isEmpty(identifier);

    // if we are new we must consider ourselves loaded
    if (isNew) {
      return !cache.isDeleted(identifier);
    }
    // even if we have a past request, if we are now empty we are not loaded
    // typically this is true after an unloadRecord call

    // if we are not empty, not new && we have a fulfilled request then we are loaded
    // we should consider allowing for something to be loaded that is simply "not empty".
    // which is how RecordState currently handles this case; however, RecordState is buggy
    // in that it does not account for unloading.
    return filterDeleted && cache.isDeletionCommitted(identifier) ? false : !isEmpty;
  }
  disconnect(identifier) {
    const record = this.__instances.record.get(identifier);
    assert('Cannot destroy record while it is still materialized', !record || record.isDestroyed || record.isDestroying);
    if (macroCondition(getOwnConfig().packages.HAS_GRAPH_PACKAGE)) {
      let graph = _peekGraph(this.store);
      if (graph) {
        graph.remove(identifier);
      }
    }
    this.store.identifierCache.forgetRecordIdentifier(identifier);
    this.__instances.resourceCache.delete(identifier);
    removeRecordDataFor(identifier);
    this.store._requestCache._clearEntries(identifier);
    if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
      // eslint-disable-next-line no-console
      console.log(`InstanceCache: disconnected ${String(identifier)}`);
    }
  }
  unloadRecord(identifier) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      const requests = this.store.getRequestStateService().getPendingRequestsForRecord(identifier);
      if (requests.some(req => {
        return req.type === 'mutation';
      })) {
        assert(`You can only unload a record which is not inFlight. '${String(identifier)}'`);
      }
    }
    if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`InstanceCache: unloading record for ${String(identifier)}`);
    }

    // TODO is this join still necessary?
    this.store._join(() => {
      const record = this.__instances.record.get(identifier);
      const cache = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? this.__instances.resourceCache.get(identifier) : this.cache;
      if (record) {
        this.store.teardownRecord(record);
        this.__instances.record.delete(identifier);
        StoreMap.delete(record);
        RecordCache.delete(record);
        removeRecordDataFor(record);
        if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
          // eslint-disable-next-line no-console
          console.log(`InstanceCache: destroyed record for ${String(identifier)}`);
        }
      }
      if (cache) {
        cache.unloadRecord(identifier);
        this.__instances.resourceCache.delete(identifier);
        removeRecordDataFor(identifier);
        if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
          // eslint-disable-next-line no-console
          console.log(`InstanceCache: destroyed cache for ${String(identifier)}`);
        }
      } else {
        this.disconnect(identifier);
      }
      this.store._requestCache._clearEntries(identifier);
      if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
        // eslint-disable-next-line no-console
        console.log(`InstanceCache: unloaded RecordData for ${String(identifier)}`);
        // eslint-disable-next-line no-console
        console.groupEnd();
      }
    });
  }
  clear(type) {
    const cache = this.store.identifierCache._cache;
    if (type === undefined) {
      // it would be cool if we could just de-ref cache here
      // but probably would require WeakRef models to do so.
      cache.lids.forEach(identifier => {
        this.unloadRecord(identifier);
      });
    } else {
      const typeCache = cache.types;
      let identifiers = typeCache[type]?.lid;
      // const rds = this.__instances.resourceCache;
      if (identifiers) {
        identifiers.forEach(identifier => {
          // if (rds.has(identifier)) {
          this.unloadRecord(identifier);
          // }
          // TODO we don't remove the identifier, should we?
        });
      }
    }
  }

  // TODO this should move into something coordinating operations
  setRecordId(identifier, id) {
    const {
      type,
      lid
    } = identifier;
    let oldId = identifier.id;

    // ID absolutely can't be missing if the oldID is empty (missing Id in response for a new record)
    assert(`'${type}' was saved to the server, but the response does not have an id and your record does not either.`, !(id === null && oldId === null));

    // ID absolutely can't be different than oldID if oldID is not null
    // TODO this assertion and restriction may not strictly be needed in the identifiers world
    assert(`Cannot update the id for '${type}:${lid}' from '${String(oldId)}' to '${id}'.`, !(oldId !== null && id !== oldId));

    // ID can be null if oldID is not null (altered ID in response for a record)
    // however, this is more than likely a developer error.
    if (oldId !== null && id === null) {
      warn(`Your ${type} record was saved to the server, but the response does not have an id.`, !(oldId !== null && id === null));
      return;
    }
    if (macroCondition(getOwnConfig().debug.LOG_INSTANCE_CACHE)) {
      // eslint-disable-next-line no-console
      console.log(`InstanceCache: updating id to '${id}' for record ${String(identifier)}`);
    }
    let existingIdentifier = this.store.identifierCache.peekRecordIdentifier({
      type,
      id
    });
    assert(`'${type}' was saved to the server, but the response returned the new id '${id}', which has already been used with another record.'`, !existingIdentifier || existingIdentifier === identifier);
    if (identifier.id === null) {
      // TODO potentially this needs to handle merged result
      this.store.identifierCache.updateRecordIdentifier(identifier, {
        type,
        id
      });
    }

    // TODO update resource cache if needed ?
    // TODO handle consequences of identifier merge for notifications
    this.store.notifications.notify(identifier, 'identity');
  }

  // TODO ths should be wrapped in a deprecation flag since cache.put
  // handles this the rest of the time
  loadData(data) {
    let modelName = data.type;
    assert(`You must include an 'id' for ${modelName} in an object passed to 'push'`, data.id !== null && data.id !== undefined && data.id !== '');
    assert(`You tried to push data with a type '${modelName}' but no model could be found with that name.`, this.store.getSchemaDefinitionService().doesTypeExist(modelName));
    const resource = constructResource(normalizeModelName$1(data.type), ensureStringId(data.id), coerceId(data.lid));
    let identifier = this.store.identifierCache.peekRecordIdentifier(resource);
    let isUpdate = false;

    // store.push will be from empty
    // findRecord will be from root.loading
    // this cannot be loading state if we do not already have an identifier
    // all else will be updates
    if (identifier) {
      const isLoading = _isLoading(this, identifier) || !this.recordIsLoaded(identifier);
      isUpdate = !_isEmpty(this, identifier) && !isLoading;

      // exclude store.push (root.empty) case
      if (isUpdate || isLoading) {
        identifier = this.store.identifierCache.updateRecordIdentifier(identifier, data);
      }
    } else {
      identifier = this.store.identifierCache.getOrCreateRecordIdentifier(data);
    }
    const cache = this.getResourceCache(identifier);
    const hasRecord = this.__instances.record.has(identifier);
    cache.upsert(identifier, data, hasRecord);
    return identifier;
  }
}
function _resourceIsFullDeleted(identifier, cache) {
  return cache.isDeletionCommitted(identifier) || cache.isNew(identifier) && cache.isDeleted(identifier);
}
function resourceIsFullyDeleted(instanceCache, identifier) {
  const cache = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? instanceCache.__instances.resourceCache.get(identifier) : instanceCache.cache;
  return !cache || _resourceIsFullDeleted(identifier, cache);
}

/*
    When a find request is triggered on the store, the user can optionally pass in
    attributes and relationships to be preloaded. These are meant to behave as if they
    came back from the server, except the user obtained them out of band and is informing
    the store of their existence. The most common use case is for supporting client side
    nested URLs, such as `/posts/1/comments/2` so the user can do
    `store.findRecord('comment', 2, { preload: { post: 1 } })` without having to fetch the post.

    Preloaded data can be attributes and relationships passed in either as IDs or as actual
    models.
  */

function preloadData(store, identifier, preload) {
  let jsonPayload = {};
  //TODO(Igor) consider the polymorphic case
  const schemas = store.getSchemaDefinitionService();
  const relationships = schemas.relationshipsDefinitionFor(identifier);
  Object.keys(preload).forEach(key => {
    let preloadValue = preload[key];
    let relationshipMeta = relationships[key];
    if (relationshipMeta) {
      if (!jsonPayload.relationships) {
        jsonPayload.relationships = {};
      }
      jsonPayload.relationships[key] = preloadRelationship(relationshipMeta, preloadValue);
    } else {
      if (!jsonPayload.attributes) {
        jsonPayload.attributes = {};
      }
      jsonPayload.attributes[key] = preloadValue;
    }
  });
  const cache = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? store._instanceCache.getResourceCache(identifier) : store.cache;
  const hasRecord = Boolean(store._instanceCache.peek({
    identifier,
    bucket: 'record'
  }));
  cache.upsert(identifier, jsonPayload, hasRecord);
}
function preloadRelationship(schema, preloadValue) {
  const relatedType = schema.type;
  if (schema.kind === 'hasMany') {
    assert('You need to pass in an array to set a hasMany property on a record', Array.isArray(preloadValue));
    return {
      data: preloadValue.map(value => _convertPreloadRelationshipToJSON(value, relatedType))
    };
  }
  assert('You should not pass in an array to set a belongsTo property on a record', !Array.isArray(preloadValue));
  return {
    data: preloadValue ? _convertPreloadRelationshipToJSON(preloadValue, relatedType) : null
  };
}

/*
  findRecord('user', '1', { preload: { friends: ['1'] }});
  findRecord('user', '1', { preload: { friends: [record] }});
*/
function _convertPreloadRelationshipToJSON(value, type) {
  if (typeof value === 'string' || typeof value === 'number') {
    return {
      type,
      id: value
    };
  }
  // TODO if not a record instance assert it's an identifier
  // and allow identifiers to be used
  return recordIdentifierFor(value);
}
function _isEmpty(instanceCache, identifier) {
  const cache = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? instanceCache.__instances.resourceCache.get(identifier) : instanceCache.cache;
  if (!cache) {
    return true;
  }
  const isNew = cache.isNew(identifier);
  const isDeleted = cache.isDeleted(identifier);
  const isEmpty = cache.isEmpty(identifier);
  return (!isNew || isDeleted) && isEmpty;
}
function _isLoading(cache, identifier) {
  const req = cache.store.getRequestStateService();
  // const fulfilled = req.getLastRequestForRecord(identifier);
  const isLoaded = cache.recordIsLoaded(identifier);
  return !isLoaded &&
  // fulfilled === null &&
  req.getPendingRequestsForRecord(identifier).some(req => req.type === 'query');
}
function _clearCaches() {
  RecordCache.clear();
  StoreMap.clear();
  CacheForIdentifierCache.clear();
}
let _modelForMixin;
if (macroCondition(getOwnConfig().packages.HAS_MODEL_PACKAGE)) {
  let _found;
  _modelForMixin = function () {
    if (!_found) {
      _found = importSync('@ember-data/model/-private')._modelForMixin;
    }
    return _found(...arguments);
  };
}
class DSModelSchemaDefinitionService {
  constructor(store) {
    this.store = store;
    this._relationshipsDefCache = Object.create(null);
    this._attributesDefCache = Object.create(null);
  }

  // Following the existing RD implementation
  attributesDefinitionFor(identifier) {
    let modelName, attributes;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_STRING_ARG_SCHEMAS)) {
      if (typeof identifier === 'string') {
        deprecate(`attributesDefinitionFor expects either a record identifier or an argument of shape { type: string }, received a string.`, false, {
          id: 'ember-data:deprecate-string-arg-schemas',
          for: 'ember-data',
          until: '5.0',
          since: {
            enabled: '4.5',
            available: '4.5'
          }
        });
        modelName = identifier;
      } else {
        modelName = identifier.type;
      }
    } else {
      modelName = identifier.type;
    }
    attributes = this._attributesDefCache[modelName];
    if (attributes === undefined) {
      let modelClass = this.store.modelFor(modelName);
      let attributeMap = modelClass.attributes;
      attributes = Object.create(null);
      attributeMap.forEach((meta, name) => attributes[name] = meta);
      this._attributesDefCache[modelName] = attributes;
    }
    return attributes;
  }

  // Following the existing RD implementation
  relationshipsDefinitionFor(identifier) {
    let modelName, relationships;
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_STRING_ARG_SCHEMAS)) {
      if (typeof identifier === 'string') {
        deprecate(`relationshipsDefinitionFor expects either a record identifier or an argument of shape { type: string }, received a string.`, false, {
          id: 'ember-data:deprecate-string-arg-schemas',
          for: 'ember-data',
          until: '5.0',
          since: {
            enabled: '4.5',
            available: '4.5'
          }
        });
        modelName = identifier;
      } else {
        modelName = identifier.type;
      }
    } else {
      modelName = identifier.type;
    }
    relationships = this._relationshipsDefCache[modelName];
    if (relationships === undefined) {
      let modelClass = this.store.modelFor(modelName);
      relationships = modelClass.relationshipsObject || null;
      this._relationshipsDefCache[modelName] = relationships;
    }
    return relationships;
  }
  doesTypeExist(modelName) {
    let normalizedModelName = normalizeModelName$1(modelName);
    let factory = getModelFactory(this.store, this.store._modelFactoryCache, normalizedModelName);
    return factory !== null;
  }
}
function getModelFactory(store, cache, normalizedModelName) {
  let factory = cache[normalizedModelName];
  if (!factory) {
    let owner = getOwner(store);
    factory = owner.factoryFor(`model:${normalizedModelName}`);
    if (macroCondition(getOwnConfig().packages.HAS_MODEL_PACKAGE)) {
      if (!factory) {
        //Support looking up mixins as base types for polymorphic relationships
        factory = _modelForMixin(store, normalizedModelName);
      }
    }
    if (!factory) {
      // we don't cache misses in case someone wants to register a missing model
      return null;
    }
    let klass = factory.class;
    if (klass.isModel) {
      let hasOwnModelNameSet = klass.modelName && Object.prototype.hasOwnProperty.call(klass, 'modelName');
      if (!hasOwnModelNameSet) {
        Object.defineProperty(klass, 'modelName', {
          value: normalizedModelName
        });
      }
    }
    cache[normalizedModelName] = factory;
  }
  return factory;
}

// if modelFor turns out to be a bottleneck we should replace with a Map
// and clear it during store teardown.
const AvailableShims = new WeakMap();
function getShimClass(store, modelName) {
  let shims = AvailableShims.get(store);
  if (!shims) {
    shims = Object.create(null);
    AvailableShims.set(store, shims);
  }
  let shim = shims[modelName];
  if (shim === undefined) {
    shim = shims[modelName] = new ShimModelClass(store, modelName);
  }
  return shim;
}
function mapFromHash(hash) {
  let map = new Map();
  for (let i in hash) {
    if (Object.prototype.hasOwnProperty.call(hash, i)) {
      map.set(i, hash[i]);
    }
  }
  return map;
}

// Mimics the static apis of DSModel
class ShimModelClass {
  constructor(store, modelName) {
    this.__store = store;
    this.modelName = modelName;
  }
  get fields() {
    let attrs = this.__store.getSchemaDefinitionService().attributesDefinitionFor({
      type: this.modelName
    });
    let relationships = this.__store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    });
    let fields = new Map();
    Object.keys(attrs).forEach(key => fields.set(key, 'attribute'));
    Object.keys(relationships).forEach(key => fields.set(key, relationships[key].kind));
    return fields;
  }
  get attributes() {
    let attrs = this.__store.getSchemaDefinitionService().attributesDefinitionFor({
      type: this.modelName
    });
    return mapFromHash(attrs);
  }
  get relationshipsByName() {
    let relationships = this.__store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    });
    return mapFromHash(relationships);
  }
  eachAttribute(callback, binding) {
    let attrDefs = this.__store.getSchemaDefinitionService().attributesDefinitionFor({
      type: this.modelName
    });
    Object.keys(attrDefs).forEach(key => {
      callback.call(binding, key, attrDefs[key]);
    });
  }
  eachRelationship(callback, binding) {
    let relationshipDefs = this.__store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type: this.modelName
    });
    Object.keys(relationshipDefs).forEach(key => {
      callback.call(binding, key, relationshipDefs[key]);
    });
  }
  eachTransformedAttribute(callback, binding) {
    const attrDefs = this.__store.getSchemaDefinitionService().attributesDefinitionFor({
      type: this.modelName
    });
    Object.keys(attrDefs).forEach(key => {
      if (attrDefs[key].type) {
        callback.call(binding, key, attrDefs[key]?.type ?? null);
      }
    });
  }
}
let tokenId = 0;
const CacheOperations = new Set(['added', 'removed', 'state', 'updated']);
function isCacheOperationValue(value) {
  return CacheOperations.has(value);
}
function runLoopIsFlushing() {
  //@ts-expect-error
  return !!_backburner.currentInstance && _backburner._autorun !== true;
}
function _unsubscribe(tokens, token, cache) {
  let identifier = tokens.get(token);
  if (macroCondition(getOwnConfig().debug.LOG_NOTIFICATIONS)) {
    if (!identifier) {
      // eslint-disable-next-line no-console
      console.log('Passed unknown unsubscribe token to unsubscribe', identifier);
    }
  }
  if (identifier) {
    tokens.delete(token);
    const map = cache.get(identifier);
    map?.delete(token);
  }
}

/**
 * The NotificationManager provides the ability to subscribe to
 * changes to Cache state.
 *
 * This Feature is what allows EmberData to create subscriptions that
 * work with any framework or change-notification system.
 *
 * @class NotificationManager
 * @public
 */
class NotificationManager {
  constructor(store) {
    this.store = store;
    this.isDestroyed = false;
    this._buffered = new Map();
    this._hasFlush = false;
    this._cache = new Map();
    this._tokens = new Map();
  }

  /**
   * Subscribe to changes for a given resource identifier, resource addition/removal, or document addition/removal.
   *
   * ```ts
   * export type CacheOperation = 'added' | 'removed' | 'updated' | 'state';
   *
   * export interface NotificationCallback {
   *   (identifier: StableRecordIdentifier, notificationType: 'attributes' | 'relationships', key?: string): void;
   *   (identifier: StableRecordIdentifier, notificationType: 'errors' | 'meta' | 'identity' | 'state'): void;
   *   (identifier: StableRecordIdentifier, notificationType: NotificationType, key?: string): void;
   * }
   * export interface ResourceOperationCallback {
   *   // resource updates
   *   (identifier: StableRecordIdentifier, notificationType: CacheOperation): void;
   * }
   * export interface DocumentOperationCallback {
   *   // document updates
   *   (identifier: StableDocumentIdentifier, notificationType: CacheOperation): void;
   * }
   * ```
   *
   * @method subscribe
   * @public
   * @param {StableDocumentIdentifier | StableRecordIdentifier | 'resource' | 'document'} identifier
   * @param {NotificationCallback | ResourceOperationCallback | DocumentOperationCallback} callback
   * @returns {UnsubscribeToken} an opaque token to be used with unsubscribe
   */

  subscribe(identifier, callback) {
    assert(`Expected to receive a stable Identifier to subscribe to`, identifier === 'resource' || identifier === 'document' || isStableIdentifier(identifier) || isDocumentIdentifier(identifier));
    let map = this._cache.get(identifier);
    if (!map) {
      map = new Map();
      this._cache.set(identifier, map);
    }
    let unsubToken = macroCondition(getOwnConfig().env.DEBUG) ? {
      _tokenRef: tokenId++
    } : {};
    map.set(unsubToken, callback);
    this._tokens.set(unsubToken, identifier);
    return unsubToken;
  }

  /**
   * remove a previous subscription
   *
   * @method unsubscribe
   * @public
   * @param {UnsubscribeToken} token
   */
  unsubscribe(token) {
    if (!this.isDestroyed) {
      _unsubscribe(this._tokens, token, this._cache);
    }
  }

  /**
   * Custom Caches and Application Code should not call this method directly.
   *
   * @method notify
   * @param identifier
   * @param value
   * @param key
   * @return {Boolean} whether a notification was delivered to any subscribers
   * @private
   */

  notify(identifier, value, key) {
    assert(`Notify does not accept a key argument for the namespace '${value}'. Received key '${key || ''}'.`, !key || value === 'attributes' || value === 'relationships');
    if (!isStableIdentifier(identifier) && !isDocumentIdentifier(identifier)) {
      if (macroCondition(getOwnConfig().debug.LOG_NOTIFICATIONS)) {
        // eslint-disable-next-line no-console
        console.log(`Notifying: Expected to receive a stable Identifier to notify '${value}' '${key || ''}' with, but ${String(identifier)} is not in the cache`, identifier);
      }
      return false;
    }
    if (macroCondition(getOwnConfig().debug.LOG_NOTIFICATIONS)) {
      // eslint-disable-next-line no-console
      console.log(`Buffering Notify: ${String(identifier.lid)}\t${value}\t${key || ''}`);
    }
    const hasSubscribers = Boolean(this._cache.get(identifier)?.size);
    if (isCacheOperationValue(value) || hasSubscribers) {
      let buffer = this._buffered.get(identifier);
      if (!buffer) {
        buffer = [];
        this._buffered.set(identifier, buffer);
      }
      buffer.push([value, key]);
      void this._scheduleNotify();
    }
    return hasSubscribers;
  }
  _onNextFlush(cb) {
    this._onFlushCB = cb;
  }
  _scheduleNotify() {
    const asyncFlush = this.store._enableAsyncFlush;
    if (this._hasFlush) {
      if (asyncFlush !== false && !runLoopIsFlushing()) {
        return;
      }
    }
    if (asyncFlush && !runLoopIsFlushing()) {
      this._hasFlush = true;
      return;
    }
    this._flush();
  }
  _flush() {
    if (this._buffered.size) {
      this._buffered.forEach((states, identifier) => {
        states.forEach(args => {
          // @ts-expect-error
          this._flushNotification(identifier, args[0], args[1]);
        });
      });
      this._buffered = new Map();
    }
    this._hasFlush = false;
    this._onFlushCB?.();
    this._onFlushCB = undefined;
  }
  _flushNotification(identifier, value, key) {
    if (macroCondition(getOwnConfig().debug.LOG_NOTIFICATIONS)) {
      // eslint-disable-next-line no-console
      console.log(`Notifying: ${String(identifier)}\t${value}\t${key || ''}`);
    }

    // TODO for documents this will need to switch based on Identifier kind
    if (isCacheOperationValue(value)) {
      let callbackMap = this._cache.get(isDocumentIdentifier(identifier) ? 'document' : 'resource');
      if (callbackMap) {
        callbackMap.forEach(cb => {
          cb(identifier, value);
        });
      }
    }
    let callbackMap = this._cache.get(identifier);
    if (!callbackMap || !callbackMap.size) {
      return false;
    }
    callbackMap.forEach(cb => {
      // @ts-expect-error overload doesn't narrow within body
      cb(identifier, value, key);
    });
    return true;
  }
  destroy() {
    this.isDestroyed = true;
    this._tokens.clear();
    this._cache.clear();
  }
}
const PromiseArrayProxy = ArrayProxy.extend(PromiseProxyMixin);
const PromiseObjectProxy = ObjectProxy.extend(PromiseProxyMixin);
var _dec, _class$1, _descriptor$1;

/**
  @module @ember-data/store
*/

/**
  A `PromiseArray` is an object that acts like both an `Ember.Array`
  and a promise. When the promise is resolved the resulting value
  will be set to the `PromiseArray`'s `content` property. This makes
  it easy to create data bindings with the `PromiseArray` that will be
  updated when the promise resolves.

  This class should not be imported and instantiated directly.

  For more information see the [Ember.PromiseProxyMixin
  documentation](/ember/release/classes/PromiseProxyMixin).

  Example

  ```javascript
  let promiseArray = PromiseArray.create({
    promise: $.getJSON('/some/remote/data.json')
  });

  promiseArray.length; // 0

  promiseArray.then(function() {
    promiseArray.length; // 100
  });
  ```

  @class PromiseArray
  @public
  @extends Ember.ArrayProxy
  @uses Ember.PromiseProxyMixin
*/
let PromiseArray = (_dec = reads('content.meta'), _class$1 = class PromiseArray extends PromiseArrayProxy {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "meta", _descriptor$1, this);
  }
}, _descriptor$1 = _applyDecoratedDescriptor(_class$1.prototype, "meta", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class$1);
function _promiseObject(promise) {
  return PromiseObjectProxy.create({
    promise
  });
}
function _promiseArray(promise) {
  return PromiseArray.create({
    promise
  });
}

// constructor is accessed in some internals but not including it in the copyright for the deprecation
const ALLOWABLE_METHODS = ['constructor', 'then', 'catch', 'finally'];
const ALLOWABLE_PROPS = ['__ec_yieldable__', '__ec_cancel__'];
const PROXIED_ARRAY_PROPS = ['length', '[]', 'firstObject', 'lastObject', 'meta', 'content', 'isPending', 'isSettled', 'isRejected', 'isFulfilled', 'promise', 'reason'];
const PROXIED_OBJECT_PROPS = ['content', 'isPending', 'isSettled', 'isRejected', 'isFulfilled', 'promise', 'reason'];
function promiseArray(promise) {
  const promiseObjectProxy = _promiseArray(promise);
  if (macroCondition(!getOwnConfig().env.DEBUG)) {
    return promiseObjectProxy;
  }
  const handler = {
    get(target, prop, receiver) {
      if (typeof prop === 'symbol') {
        return Reflect.get(target, prop, receiver);
      }
      if (ALLOWABLE_PROPS.includes(prop)) {
        return target[prop];
      }
      if (!ALLOWABLE_METHODS.includes(prop)) {
        deprecate(`Accessing ${prop} on this PromiseArray is deprecated. The return type is being changed from PromiseArray to a Promise. The only available methods to access on this promise are .then, .catch and .finally`, false, {
          id: 'ember-data:deprecate-promise-proxies',
          until: '5.0',
          for: '@ember-data/store',
          since: {
            available: '4.7',
            enabled: '4.7'
          }
        });
      }
      const value = target[prop];
      if (value && typeof value === 'function' && typeof value.bind === 'function') {
        return value.bind(target);
      }
      if (PROXIED_ARRAY_PROPS.includes(prop)) {
        return value;
      }
      return undefined;
    }
  };
  return new Proxy(promiseObjectProxy, handler);
}
const ProxySymbolString = String(Symbol.for('PROXY_CONTENT'));
function promiseObject(promise) {
  const promiseObjectProxy = _promiseObject(promise);
  if (macroCondition(!getOwnConfig().env.DEBUG)) {
    return promiseObjectProxy;
  }
  const handler = {
    get(target, prop, receiver) {
      if (typeof prop === 'symbol') {
        if (String(prop) === ProxySymbolString) {
          return;
        }
        return Reflect.get(target, prop, receiver);
      }
      if (prop === 'constructor') {
        return target.constructor;
      }
      if (ALLOWABLE_PROPS.includes(prop)) {
        return target[prop];
      }
      if (!ALLOWABLE_METHODS.includes(prop)) {
        deprecate(`Accessing ${prop} on this PromiseObject is deprecated. The return type is being changed from PromiseObject to a Promise. The only available methods to access on this promise are .then, .catch and .finally`, false, {
          id: 'ember-data:deprecate-promise-proxies',
          until: '5.0',
          for: '@ember-data/store',
          since: {
            available: '4.7',
            enabled: '4.7'
          }
        });
      } else {
        return target[prop].bind(target);
      }
      if (PROXIED_OBJECT_PROPS.includes(prop)) {
        return target[prop];
      }
      const value = get(target, prop);
      if (value && typeof value === 'function' && typeof value.bind === 'function') {
        return value.bind(receiver);
      }
      return undefined;
    }
  };
  return new Proxy(promiseObjectProxy, handler);
}
var _class, _descriptor, _class3, _descriptor2;
const ARRAY_GETTER_METHODS = new Set([Symbol.iterator, 'concat', 'entries', 'every', 'fill', 'filter', 'find', 'findIndex', 'flat', 'flatMap', 'forEach', 'includes', 'indexOf', 'join', 'keys', 'lastIndexOf', 'map', 'reduce', 'reduceRight', 'slice', 'some', 'values']);
const ARRAY_SETTER_METHODS = new Set(['push', 'pop', 'unshift', 'shift', 'splice', 'sort']);
const SYNC_PROPS = new Set(['[]', 'length', 'links', 'meta']);
function isArrayGetter(prop) {
  return ARRAY_GETTER_METHODS.has(prop);
}
function isArraySetter(prop) {
  return ARRAY_SETTER_METHODS.has(prop);
}
const IDENTIFIER_ARRAY_TAG = Symbol('#tag');
const SOURCE = Symbol('#source');
const MUTATE = Symbol('#update');
const NOTIFY = Symbol('#notify');
const IS_COLLECTION = Symbol.for('Collection');
function notifyArray(arr) {
  addToTransaction(arr[IDENTIFIER_ARRAY_TAG]);
  if (macroCondition(getOwnConfig().deprecations.DEPRECATE_COMPUTED_CHAINS)) {
    // eslint-disable-next-line
    dirtyTag(tagForProperty(arr, 'length'));
    // eslint-disable-next-line
    dirtyTag(tagForProperty(arr, '[]'));
  }
}
function convertToInt(prop) {
  if (typeof prop === 'symbol') return null;
  const num = Number(prop);
  if (isNaN(num)) return null;
  return num % 1 === 0 ? num : null;
}
let Tag = (_class = class Tag {
  /*
   * whether this was part of a transaction when last mutated
   */

  constructor() {
    _initializerDefineProperty(this, "ref", _descriptor, this);
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      const [arr, prop] = arguments;
      this._debug_base = arr.constructor.name + ':' + String(arr.modelName);
      this._debug_prop = prop;
    }
    this.shouldReset = false;
    this.t = false;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "ref", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _class);
function deprecateArrayLike(className, fnName, replName) {
  deprecate(`The \`${fnName}\` method on the class ${className} is deprecated. Use the native array method \`${replName}\` instead.`, false, {
    id: 'ember-data:deprecate-array-like',
    until: '5.0',
    since: {
      enabled: '4.7',
      available: '4.7'
    },
    for: 'ember-data'
  });
}
function safeForEach(instance, arr, store, callback, target) {
  if (target === undefined) {
    target = null;
  }
  // clone to prevent mutation
  arr = arr.slice();
  assert('`forEach` expects a function as first argument.', typeof callback === 'function');

  // because we retrieveLatest above we need not worry if array is mutated during iteration
  // by unloadRecord/rollbackAttributes
  // push/add/removeObject may still be problematic
  // but this is a more traditionally expected forEach bug.
  const length = arr.length; // we need to access length to ensure we are consumed

  for (let index = 0; index < length; index++) {
    callback.call(target, store._instanceCache.getRecord(arr[index]), index, instance);
  }
  return instance;
}

/**
  A record array is an array that contains records of a certain type (or modelName).
  The record array materializes records as needed when they are retrieved for the first
  time. You should not create record arrays yourself. Instead, an instance of
  `RecordArray` or its subclasses will be returned by your application's store
  in response to queries.

  This class should not be imported and instantiated by consuming applications.

  @class RecordArray
  @public
*/
let IdentifierArray = (_class3 = class IdentifierArray {
  /**
    The flag to signal a `RecordArray` is currently loading data.
    Example
    ```javascript
    let people = store.peekAll('person');
    people.isUpdating; // false
    people.update();
    people.isUpdating; // true
    ```
    @property isUpdating
    @public
    @type Boolean
  */

  [NOTIFY]() {
    notifyArray(this);
  }

  /**
   The modelClass represented by this record array.
    @property type
    @public
    @deprecated
   @type {subclass of Model}
   */

  /**
    The store that created this record array.
     @property store
    @private
    @type Store
    */

  destroy(clear) {
    this.isDestroying = !clear;
    // changing the reference breaks the Proxy
    // this[SOURCE] = [];
    this[SOURCE].length = 0;
    this[NOTIFY]();
    this.isDestroyed = !clear;
  }

  // length must be on self for proxied methods to work properly
  get length() {
    return this[SOURCE].length;
  }
  set length(value) {
    this[SOURCE].length = value;
  }

  // here to support computed chains
  // and {{#each}}
  get '[]'() {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_COMPUTED_CHAINS)) {
      return this;
    }
  }
  constructor(options) {
    _initializerDefineProperty(this, "isUpdating", _descriptor2, this);
    this.isLoaded = true;
    this.isDestroying = false;
    this.isDestroyed = false;
    this._updatingPromise = null;
    this[IS_COLLECTION] = true;
    this[SOURCE] = void 0;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    this.modelName = options.type;
    this.store = options.store;
    this._manager = options.manager;
    this[SOURCE] = options.identifiers;
    // @ts-expect-error
    this[IDENTIFIER_ARRAY_TAG] = macroCondition(getOwnConfig().env.DEBUG) ? new Tag(this, 'length') : new Tag();
    const store = options.store;
    const boundFns = new Map();
    const _TAG = this[IDENTIFIER_ARRAY_TAG];
    const PrivateState = {
      links: options.links || null,
      meta: options.meta || null
    };
    let transaction = false;

    // when a mutation occurs
    // we track all mutations within the call
    // and forward them as one

    const proxy = new Proxy(this[SOURCE], {
      get(target, prop, receiver) {
        let index = convertToInt(prop);
        if (_TAG.shouldReset && (index !== null || SYNC_PROPS.has(prop) || isArrayGetter(prop))) {
          options.manager._syncArray(receiver);
          _TAG.t = false;
          _TAG.shouldReset = false;
        }
        if (index !== null) {
          const identifier = target[index];
          if (!transaction) {
            subscribe(_TAG);
          }
          return identifier && store._instanceCache.getRecord(identifier);
        }
        if (prop === 'meta') return subscribe(_TAG), PrivateState.meta;
        if (prop === 'links') return subscribe(_TAG), PrivateState.links;
        if (prop === '[]') return subscribe(_TAG), receiver;
        if (isArrayGetter(prop)) {
          let fn = boundFns.get(prop);
          if (fn === undefined) {
            if (prop === 'forEach') {
              fn = function () {
                subscribe(_TAG);
                transaction = true;
                const result = safeForEach(receiver, target, store, arguments[0], arguments[1]);
                transaction = false;
                return result;
              };
            } else {
              fn = function () {
                subscribe(_TAG);
                // array functions must run through Reflect to work properly
                // binding via other means will not work.
                transaction = true;
                const result = Reflect.apply(target[prop], receiver, arguments);
                transaction = false;
                return result;
              };
            }
            boundFns.set(prop, fn);
          }
          return fn;
        }
        if (isArraySetter(prop)) {
          let fn = boundFns.get(prop);
          if (fn === undefined) {
            fn = function () {
              // array functions must run through Reflect to work properly
              // binding via other means will not work.
              if (!options.allowMutation) {
                assert(`Mutating this array of records via ${String(prop)} is not allowed.`, options.allowMutation);
                return;
              }
              const args = Array.prototype.slice.call(arguments);
              assert(`Cannot start a new array transaction while a previous transaction is underway`, !transaction);
              transaction = true;
              const result = self[MUTATE](target, receiver, prop, args, _TAG);
              transaction = false;
              return result;
            };
            boundFns.set(prop, fn);
          }
          return fn;
        }
        if (prop in self) {
          if (macroCondition(getOwnConfig().deprecations.DEPRECATE_ARRAY_LIKE)) {
            if (prop === 'firstObject') {
              deprecateArrayLike(self.DEPRECATED_CLASS_NAME, prop, '[0]');
              return receiver[0];
            } else if (prop === 'lastObject') {
              deprecateArrayLike(self.DEPRECATED_CLASS_NAME, prop, 'at(-1)');
              return receiver[receiver.length - 1];
            }
          }
          if (prop === NOTIFY || prop === IDENTIFIER_ARRAY_TAG || prop === SOURCE) {
            return self[prop];
          }
          let fn = boundFns.get(prop);
          if (fn) return fn;
          let outcome = self[prop];
          if (typeof outcome === 'function') {
            fn = function () {
              subscribe(_TAG);
              // array functions must run through Reflect to work properly
              // binding via other means will not work.
              return Reflect.apply(outcome, receiver, arguments);
            };
            boundFns.set(prop, fn);
            return fn;
          }
          return subscribe(_TAG), outcome;
        }
        return target[prop];
      },
      set(target, prop, value, receiver) {
        if (prop === 'length') {
          if (!transaction && value === 0) {
            transaction = true;
            self[MUTATE](target, receiver, 'length 0', [], _TAG);
            transaction = false;
            return true;
          } else if (transaction) {
            return Reflect.set(target, prop, value);
          } else {
            assert(`unexpected length set`);
          }
        }
        if (prop === 'links') {
          PrivateState.links = value || null;
          return true;
        }
        if (prop === 'meta') {
          PrivateState.meta = value || null;
          return true;
        }
        let index = convertToInt(prop);

        // we do not allow "holey" arrays and so if the index is
        // greater than length then we will disallow setting it.
        // however, there is a special case for "unshift" with more than
        // one item being inserted since current items will be moved to the
        // new indices first.
        // we "loosely" detect this by just checking whether we are in
        // a transaction.
        if (index === null || index > target.length) {
          if (index !== null && transaction) {
            const identifier = recordIdentifierFor(value);
            assert(`Cannot set index ${index} past the end of the array.`, isStableIdentifier(identifier));
            target[index] = identifier;
            return true;
          } else if (prop in self) {
            self[prop] = value;
            return true;
          }
          return false;
        }
        if (!options.allowMutation) {
          assert(`Mutating ${String(prop)} on this RecordArray is not allowed.`, options.allowMutation);
          return false;
        }
        let original = target[index];
        let newIdentifier = extractIdentifierFromRecord$1(value);
        assert(`Expected a record`, isStableIdentifier(newIdentifier));
        // We generate "transactions" whenever a setter method on the array
        // is called and might bulk update multiple array cells. Fundamentally,
        // all array operations decompose into individual cell replacements.
        // e.g. a push is really a "replace cell at next index with new value"
        // or a splice is "shift all values left/right by X and set out of new
        // bounds cells to undefined"
        //
        // so, if we are in a transaction, then this is not a user generated change
        // but one generated by a setter method. In this case we want to only apply
        // the change to the target array and not call the MUTATE method.
        // If there is no transaction though, then this means the user themselves has
        // directly changed the value of a specific index and we need to thus generate
        // a mutation for that change.
        // e.g. "arr.push(newVal)" is handled by a "addToRelatedRecords" mutation within
        // a transaction.
        // while "arr[arr.length] = newVal;" is handled by this replace cell code path.
        if (!transaction) {
          self[MUTATE](target, receiver, 'replace cell', [index, original, newIdentifier], _TAG);
        } else {
          target[index] = newIdentifier;
        }
        return true;
      },
      deleteProperty(target, prop) {
        assert(`Deleting keys on managed arrays is disallowed`, transaction);
        if (!transaction) {
          return false;
        }
        return Reflect.deleteProperty(target, prop);
      },
      getPrototypeOf() {
        return IdentifierArray.prototype;
      }
    });
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_A_USAGE)) {
      const meta = Ember.meta(this);
      meta.hasMixin = mixin => {
        deprecate(`Do not call A() on EmberData RecordArrays`, false, {
          id: 'ember-data:no-a-with-array-like',
          until: '5.0',
          since: {
            enabled: '4.7',
            available: '4.7'
          },
          for: 'ember-data'
        });
        // @ts-expect-error ArrayMixin is more than a type
        if (mixin === NativeArray || mixin === ArrayMixin) {
          return true;
        }
        return false;
      };
    } else if (macroCondition(getOwnConfig().env.DEBUG)) {
      const meta = Ember.meta(this);
      meta.hasMixin = mixin => {
        assert(`Do not call A() on EmberData RecordArrays`);
      };
    }
    this[NOTIFY] = this[NOTIFY].bind(proxy);
    return proxy;
  }

  /**
    Used to get the latest version of all of the records in this array
    from the adapter.
     Example
     ```javascript
    let people = store.peekAll('person');
    people.isUpdating; // false
     people.update().then(function() {
      people.isUpdating; // false
    });
     people.isUpdating; // true
    ```
     @method update
    @public
  */
  update() {
    if (this.isUpdating) {
      return this._updatingPromise;
    }
    this.isUpdating = true;
    let updatingPromise = this._update();
    updatingPromise.finally(() => {
      this._updatingPromise = null;
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      this.isUpdating = false;
    });
    this._updatingPromise = updatingPromise;
    return updatingPromise;
  }

  /*
    Update this RecordArray and return a promise which resolves once the update
    is finished.
   */
  _update() {
    assert(`_update cannot be used with this array`, this.modelName);
    return this.store.findAll(this.modelName, {
      reload: true
    });
  }

  // TODO deprecate
  /**
    Saves all of the records in the `RecordArray`.
     Example
     ```javascript
    let messages = store.peekAll('message');
    messages.forEach(function(message) {
      message.hasBeenSeen = true;
    });
    messages.save();
    ```
     @method save
    @public
    @return {PromiseArray} promise
  */
  save() {
    let promise = Promise.all(this.map(record => this.store.saveRecord(record))).then(() => this);
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseArray(promise);
    }
    return promise;
  }
}, _descriptor2 = _applyDecoratedDescriptor(_class3.prototype, "isUpdating", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class3.prototype, "length", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class3.prototype, "length"), _class3.prototype), _class3);
if (macroCondition(getOwnConfig().deprecations.DEPRECATE_SNAPSHOT_MODEL_CLASS_ACCESS)) {
  Object.defineProperty(IdentifierArray.prototype, 'type', {
    get() {
      deprecate(`Using RecordArray.type to access the ModelClass for a record is deprecated. Use store.modelFor(<modelName>) instead.`, false, {
        id: 'ember-data:deprecate-snapshot-model-class-access',
        until: '5.0',
        for: 'ember-data',
        since: {
          available: '4.5.0',
          enabled: '4.5.0'
        }
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (!this.modelName) {
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      return this.store.modelFor(this.modelName);
    }
  });
}
class Collection extends IdentifierArray {
  constructor(options) {
    super(options);
    this.query = null;
    this.query = options.query || null;
    this.isLoaded = options.isLoaded || false;
  }
  _update() {
    const {
      store,
      query
    } = this;

    // TODO save options from initial request?
    assert(`update cannot be used with this array`, this.modelName);
    assert(`update cannot be used with no query`, query);
    const promise = store.query(this.modelName, query, {
      _recordArray: this
    });
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseArray(promise);
    }
    return promise;
  }
  destroy(clear) {
    super.destroy(clear);
    this._manager._managed.delete(this);
    this._manager._pending.delete(this);
  }
}
// trick the proxy "in" check
Collection.prototype.query = null;

// Ensure instanceof works correctly
//Object.setPrototypeOf(IdentifierArray.prototype, Array.prototype);

if (macroCondition(getOwnConfig().deprecations.DEPRECATE_ARRAY_LIKE)) {
  IdentifierArray.prototype.DEPRECATED_CLASS_NAME = 'RecordArray';
  Collection.prototype.DEPRECATED_CLASS_NAME = 'RecordArray';
  const EmberObjectMethods = ['addObserver', 'cacheFor', 'decrementProperty', 'get', 'getProperties', 'incrementProperty', 'notifyPropertyChange', 'removeObserver', 'set', 'setProperties', 'toggleProperty'];
  EmberObjectMethods.forEach(method => {
    IdentifierArray.prototype[method] = function delegatedMethod(...args) {
      deprecate(`The EmberObject ${method} method on the class ${this.DEPRECATED_CLASS_NAME} is deprecated. Use dot-notation javascript get/set access instead.`, false, {
        id: 'ember-data:deprecate-array-like',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        },
        for: 'ember-data'
      });
      return Ember[method](this, ...args);
    };
  });
  IdentifierArray.prototype.addObject = function (obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'addObject', 'push');
    let index = this.indexOf(obj);
    if (index === -1) {
      this.push(obj);
    }
    return this;
  };
  IdentifierArray.prototype.addObjects = function (objs) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'addObjects', 'push');
    objs.forEach(obj => {
      let index = this.indexOf(obj);
      if (index === -1) {
        this.push(obj);
      }
    });
    return this;
  };
  IdentifierArray.prototype.popObject = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'popObject', 'pop');
    return this.pop();
  };
  IdentifierArray.prototype.pushObject = function (obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'pushObject', 'push');
    this.push(obj);
    return obj;
  };
  IdentifierArray.prototype.pushObjects = function (objs) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'pushObjects', 'push');
    this.push(...objs);
    return this;
  };
  IdentifierArray.prototype.shiftObject = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'shiftObject', 'shift');
    return this.shift();
  };
  IdentifierArray.prototype.unshiftObject = function (obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'unshiftObject', 'unshift');
    this.unshift(obj);
    return obj;
  };
  IdentifierArray.prototype.unshiftObjects = function (objs) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'unshiftObjects', 'unshift');
    this.unshift(...objs);
    return this;
  };
  IdentifierArray.prototype.objectAt = function (index) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'objectAt', 'at');
    //For negative index values go back from the end of the array
    let arrIndex = Math.sign(index) === -1 ? this.length + index : index;
    return this[arrIndex];
  };
  IdentifierArray.prototype.objectsAt = function (indices) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'objectsAt', 'at');
    return indices.map(index => this.objectAt(index));
  };
  IdentifierArray.prototype.removeAt = function (index) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'removeAt', 'splice');
    this.splice(index, 1);
    return this;
  };
  IdentifierArray.prototype.insertAt = function (index, obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'insertAt', 'splice');
    this.splice(index, 0, obj);
    return this;
  };
  IdentifierArray.prototype.removeObject = function (obj) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'removeObject', 'splice');
    const index = this.indexOf(obj);
    if (index !== -1) {
      this.splice(index, 1);
    }
    return this;
  };
  IdentifierArray.prototype.removeObjects = function (objs) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'removeObjects', 'splice');
    objs.forEach(obj => {
      const index = this.indexOf(obj);
      if (index !== -1) {
        this.splice(index, 1);
      }
    });
    return this;
  };
  IdentifierArray.prototype.toArray = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'toArray', 'slice');
    return this.slice();
  };
  IdentifierArray.prototype.replace = function (idx, amt, objects) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'replace', 'splice');
    if (objects) {
      this.splice(idx, amt, ...objects);
    } else {
      this.splice(idx, amt);
    }
  };
  IdentifierArray.prototype.clear = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'clear', 'length = 0');
    this.splice(0, this.length);
    return this;
  };
  IdentifierArray.prototype.setObjects = function (objects) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'setObjects', '`arr.length = 0; arr.push(objects);`');
    assert(`${this.DEPRECATED_CLASS_NAME}.setObjects expects to receive an array as its argument`, Array.isArray(objects));
    this.splice(0, this.length);
    this.push(...objects);
    return this;
  };
  IdentifierArray.prototype.reverseObjects = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'reverseObjects', 'reverse');
    this.reverse();
    return this;
  };
  IdentifierArray.prototype.compact = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'compact', 'filter');
    return this.filter(v => v !== null && v !== undefined);
  };
  IdentifierArray.prototype.any = function (callback, target) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'any', 'some');
    return this.some(callback, target);
  };
  IdentifierArray.prototype.isAny = function (prop, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'isAny', 'some');
    let hasValue = arguments.length === 2;
    return this.some(v => hasValue ? v[prop] === value : v[prop] === true);
  };
  IdentifierArray.prototype.isEvery = function (prop, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'isEvery', 'every');
    let hasValue = arguments.length === 2;
    return this.every(v => hasValue ? v[prop] === value : v[prop] === true);
  };
  IdentifierArray.prototype.getEach = function (key) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'getEach', 'map');
    return this.map(value => get(value, key));
  };
  IdentifierArray.prototype.mapBy = function (key) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'mapBy', 'map');
    return this.map(value => get(value, key));
  };
  IdentifierArray.prototype.findBy = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'findBy', 'find');
    if (arguments.length === 2) {
      return this.find(val => {
        return get(val, key) === value;
      });
    } else {
      return this.find(val => Boolean(get(val, key)));
    }
  };
  IdentifierArray.prototype.filterBy = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'filterBy', 'filter');
    if (arguments.length === 2) {
      return this.filter(record => {
        return get(record, key) === value;
      });
    }
    return this.filter(record => {
      return Boolean(get(record, key));
    });
  };
  IdentifierArray.prototype.sortBy = function (...sortKeys) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'sortBy', '.slice().sort');
    return this.slice().sort((a, b) => {
      for (let i = 0; i < sortKeys.length; i++) {
        let key = sortKeys[i];
        let propA = get(a, key);
        let propB = get(b, key);
        // return 1 or -1 else continue to the next sortKey
        let compareValue = compare(propA, propB);
        if (compareValue) {
          return compareValue;
        }
      }
      return 0;
    });
  };

  // @ts-expect-error
  IdentifierArray.prototype.invoke = function (key, ...args) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'invoke', 'forEach');
    return this.map(value => value[key](...args));
  };

  // @ts-expect-error
  IdentifierArray.prototype.addArrayObserver = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'addArrayObserver', 'derived state or reacting at the change source');
  };

  // @ts-expect-error
  IdentifierArray.prototype.removeArrayObserver = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'removeArrayObserver', 'derived state or reacting at the change source');
  };

  // @ts-expect-error
  IdentifierArray.prototype.arrayContentWillChange = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'arrayContentWillChange', 'derived state or reacting at the change source');
  };

  // @ts-expect-error
  IdentifierArray.prototype.arrayContentDidChange = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'arrayContentDidChange', 'derived state or reacting at the change source.');
  };
  IdentifierArray.prototype.reject = function (callback, target) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'reject', 'filter');
    assert('`reject` expects a function as first argument.', typeof callback === 'function');
    return this.filter((...args) => {
      return !callback.apply(target, args);
    });
  };
  IdentifierArray.prototype.rejectBy = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'rejectBy', 'filter');
    if (arguments.length === 2) {
      return this.filter(record => {
        return get(record, key) !== value;
      });
    }
    return this.filter(record => {
      return !get(record, key);
    });
  };
  IdentifierArray.prototype.setEach = function (key, value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'setEach', 'forEach');
    this.forEach(item => set(item, key, value));
  };
  IdentifierArray.prototype.uniq = function () {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'uniq', 'filter');
    // all current managed arrays are already enforced as unique
    return this.slice();
  };

  // @ts-expect-error
  IdentifierArray.prototype.uniqBy = function (key) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'uniqBy', 'filter');
    // all current managed arrays are already enforced as unique
    let seen = new Set();
    let result = [];
    this.forEach(item => {
      let value = get(item, key);
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
      result.push(item);
    });
    return result;
  };
  IdentifierArray.prototype.without = function (value) {
    deprecateArrayLike(this.DEPRECATED_CLASS_NAME, 'without', 'slice');
    const newArr = this.slice();
    const index = this.indexOf(value);
    if (index !== -1) {
      newArr.splice(index, 1);
    }
    return newArr;
  };

  // @ts-expect-error
  IdentifierArray.prototype.firstObject = null;
  // @ts-expect-error
  IdentifierArray.prototype.lastObject = null;
}
function assertRecordPassedToHasMany(record) {
  assert(`All elements of a hasMany relationship must be instances of Model, you passed $${typeof record}`, function () {
    try {
      recordIdentifierFor(record);
      return true;
    } catch {
      return false;
    }
  }());
}
function extractIdentifierFromRecord$1(recordOrPromiseRecord) {
  if (!recordOrPromiseRecord) {
    return null;
  }
  if (isPromiseRecord$1(recordOrPromiseRecord)) {
    let content = recordOrPromiseRecord.content;
    assert('You passed in a promise that did not originate from an EmberData relationship. You can only pass promises that come from a belongsTo relationship.', content !== undefined && content !== null);
    assertRecordPassedToHasMany(content);
    return recordIdentifierFor(content);
  }
  assertRecordPassedToHasMany(recordOrPromiseRecord);
  return recordIdentifierFor(recordOrPromiseRecord);
}
function isPromiseRecord$1(record) {
  return !!record.then;
}

/**
  @module @ember-data/store
*/
const FAKE_ARR = {};
const SLICE_BATCH_SIZE = 1200;
/**
 * This is a clever optimization.
 *
 * clever optimizations rarely stand the test of time, so if you're
 * ever curious or think something better is possible please benchmark
 * and discuss. The benchmark for this at the time of writing is in
 * `scripts/benchmark-push.js`
 *
 * This approach turns out to be 150x faster in Chrome and node than
 * simply using push or concat. It's highly susceptible to the specifics
 * of the batch size, and may require tuning.
 *
 * Clever optimizations should always come with a `why`. This optimization
 * exists for two reasons.
 *
 * 1) array.push(...objects) and Array.prototype.push.apply(arr, objects)
 *   are susceptible to stack overflows. The size of objects at which this
 *   occurs varies by environment, browser, and current stack depth and memory
 *   pressure; however, it occurs in all browsers in fairly pristine conditions
 *   somewhere around 125k to 200k elements. Since EmberData regularly encounters
 *   arrays larger than this in size, we cannot use push.
 *
 * 2) `array.concat` or simply setting the array to a new reference is often an
 *   easier approach; however, native Proxy to an array cannot swap it's target array
 *   and attempts at juggling multiple array sources have proven to be victim to a number
 *   of browser implementation bugs. Should these bugs be addressed then we could
 *   simplify to using `concat`, however, do note this is currently 150x faster
 *   than concat, and due to the overloaded signature of concat will likely always
 *   be faster.
 *
 * Sincerely,
 *   - runspired (Chris Thoburn) 08/21/2022
 *
 * @function fastPush
 * @internal
 * @param target the array to push into
 * @param source the items to push into target
 */
function fastPush(target, source) {
  let startLength = 0;
  let newLength = source.length;
  while (newLength - startLength > SLICE_BATCH_SIZE) {
    // eslint-disable-next-line prefer-spread
    target.push.apply(target, source.slice(startLength, startLength + SLICE_BATCH_SIZE));
    startLength += SLICE_BATCH_SIZE;
  }
  // eslint-disable-next-line prefer-spread
  target.push.apply(target, source.slice(startLength));
}
/**
  @class RecordArrayManager
  @internal
*/
class RecordArrayManager {
  constructor(options) {
    this.store = options.store;
    this.isDestroying = false;
    this.isDestroyed = false;
    this._live = new Map();
    this._managed = new Set();
    this._pending = new Map();
    this._staged = new Map();
    this._keyedArrays = new Map();
    this._identifiers = new Map();
    this._set = new Map();
    this._visibilitySet = new Map();
    this._subscription = this.store.notifications.subscribe('resource', (identifier, type) => {
      if (type === 'added') {
        this._visibilitySet.set(identifier, true);
        this.identifierAdded(identifier);
      } else if (type === 'removed') {
        this._visibilitySet.set(identifier, false);
        this.identifierRemoved(identifier);
      } else if (type === 'state') {
        this.identifierChanged(identifier);
      }
    });
  }
  _syncArray(array) {
    const pending = this._pending.get(array);
    if (!pending || this.isDestroying || this.isDestroyed) {
      return;
    }
    sync(array, pending, this._set.get(array));
    this._pending.delete(array);
  }

  /**
    Get the `RecordArray` for a modelName, which contains all loaded records of
    given modelName.
     @method liveArrayFor
    @internal
    @param {String} modelName
    @return {RecordArray}
  */
  liveArrayFor(type) {
    let array = this._live.get(type);
    let identifiers = [];
    let staged = this._staged.get(type);
    if (staged) {
      staged.forEach((value, key) => {
        if (value === 'add') {
          identifiers.push(key);
        }
      });
      this._staged.delete(type);
    }
    if (!array) {
      array = new IdentifierArray({
        type,
        identifiers,
        store: this.store,
        allowMutation: false,
        manager: this
      });
      this._live.set(type, array);
      this._set.set(array, new Set(identifiers));
    }
    return array;
  }
  createArray(config) {
    let options = {
      type: config.type,
      links: config.doc?.links || null,
      meta: config.doc?.meta || null,
      query: config.query || null,
      identifiers: config.identifiers || [],
      isLoaded: !!config.identifiers?.length,
      allowMutation: false,
      store: this.store,
      manager: this
    };
    let array = new Collection(options);
    this._managed.add(array);
    this._set.set(array, new Set(options.identifiers || []));
    if (config.identifiers) {
      associate(this._identifiers, array, config.identifiers);
    }
    return array;
  }
  dirtyArray(array, delta) {
    if (array === FAKE_ARR) {
      return;
    }
    let tag = array[IDENTIFIER_ARRAY_TAG];
    if (!tag.shouldReset) {
      tag.shouldReset = true;
      addTransactionCB(array[NOTIFY]);
    } else if (delta > 0 && !tag.t) {
      addTransactionCB(array[NOTIFY]);
    }
  }
  _getPendingFor(identifier, includeManaged, isRemove) {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    let liveArray = this._live.get(identifier.type);
    const allPending = this._pending;
    let pending = new Map();
    if (includeManaged) {
      let managed = this._identifiers.get(identifier);
      if (managed) {
        managed.forEach(arr => {
          let changes = allPending.get(arr);
          if (!changes) {
            changes = new Map();
            allPending.set(arr, changes);
          }
          pending.set(arr, changes);
        });
      }
    }

    // during unloadAll we can ignore removes since we've already
    // cleared the array.
    if (liveArray && liveArray[SOURCE].length === 0 && isRemove) {
      const pendingLive = allPending.get(liveArray);
      if (!pendingLive || pendingLive.size === 0) {
        return pending;
      }
    }
    if (!liveArray) {
      // start building a changeset for when we eventually
      // do have a live array
      let changes = this._staged.get(identifier.type);
      if (!changes) {
        changes = new Map();
        this._staged.set(identifier.type, changes);
      }
      pending.set(FAKE_ARR, changes);
    } else {
      let changes = allPending.get(liveArray);
      if (!changes) {
        changes = new Map();
        allPending.set(liveArray, changes);
      }
      pending.set(liveArray, changes);
    }
    return pending;
  }
  populateManagedArray(array, identifiers, payload) {
    this._pending.delete(array);
    const source = array[SOURCE];
    const old = source.slice();
    source.length = 0;
    fastPush(source, identifiers);
    this._set.set(array, new Set(identifiers));
    notifyArray(array);
    array.meta = payload.meta || null;
    array.links = payload.links || null;
    array.isLoaded = true;
    disassociate(this._identifiers, array, old);
    associate(this._identifiers, array, identifiers);
  }
  identifierAdded(identifier) {
    let changeSets = this._getPendingFor(identifier, false);
    if (changeSets) {
      changeSets.forEach((changes, array) => {
        let existing = changes.get(identifier);
        if (existing === 'del') {
          changes.delete(identifier);
        } else {
          changes.set(identifier, 'add');
          this.dirtyArray(array, changes.size);
        }
      });
    }
  }
  identifierRemoved(identifier) {
    let changeSets = this._getPendingFor(identifier, true, true);
    if (changeSets) {
      changeSets.forEach((changes, array) => {
        let existing = changes.get(identifier);
        if (existing === 'add') {
          changes.delete(identifier);
        } else {
          changes.set(identifier, 'del');
          this.dirtyArray(array, changes.size);
        }
      });
    }
  }
  identifierChanged(identifier) {
    let newState = this.store._instanceCache.recordIsLoaded(identifier, true);

    // if the change matches the most recent direct added/removed
    // state, then we can ignore it
    if (this._visibilitySet.get(identifier) === newState) {
      return;
    }
    if (newState) {
      this.identifierAdded(identifier);
    } else {
      this.identifierRemoved(identifier);
    }
  }
  clear(isClear = true) {
    this._live.forEach(array => array.destroy(isClear));
    this._managed.forEach(array => array.destroy(isClear));
    this._managed.clear();
    this._identifiers.clear();
    this._pending.clear();
    this._set.forEach(set => set.clear());
    this._visibilitySet.clear();
  }
  destroy() {
    this.isDestroying = true;
    this.clear(false);
    this._live.clear();
    this.isDestroyed = true;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.store.notifications.unsubscribe(this._subscription);
  }
}
function associate(ArraysCache, array, identifiers) {
  for (let i = 0; i < identifiers.length; i++) {
    let identifier = identifiers[i];
    let cache = ArraysCache.get(identifier);
    if (!cache) {
      cache = new Set();
      ArraysCache.set(identifier, cache);
    }
    cache.add(array);
  }
}
function disassociate(ArraysCache, array, identifiers) {
  for (let i = 0; i < identifiers.length; i++) {
    disassociateIdentifier(ArraysCache, array, identifiers[i]);
  }
}
function disassociateIdentifier(ArraysCache, array, identifier) {
  let cache = ArraysCache.get(identifier);
  if (cache) {
    cache.delete(array);
  }
}
function sync(array, changes, arraySet) {
  let state = array[SOURCE];
  const adds = [];
  const removes = [];
  changes.forEach((value, key) => {
    if (value === 'add') {
      // likely we want to keep a Set along-side
      if (arraySet.has(key)) {
        return;
      }
      adds.push(key);
      arraySet.add(key);
    } else {
      if (arraySet.has(key)) {
        removes.push(key);
        arraySet.delete(key);
      }
    }
  });
  if (removes.length) {
    if (removes.length === state.length) {
      state.length = 0;
      // changing the reference breaks the Proxy
      // state = array[SOURCE] = [];
    } else {
      removes.forEach(i => {
        const index = state.indexOf(i);
        if (index !== -1) {
          state.splice(index, 1);
          arraySet.delete(i);
        }
      });
    }
  }
  if (adds.length) {
    fastPush(state, adds);
    // changing the reference breaks the Proxy
    // else we could do this
    /*
    if (state.length === 0) {
      array[SOURCE] = adds;
    } else {
      array[SOURCE] = state.concat(adds);
    }
    */
  }
}

/**
 * @module @ember-data/store
 */
const Touching = Symbol('touching');
const RequestPromise = Symbol('promise');
function hasRecordIdentifier(op) {
  return 'recordIdentifier' in op;
}

/**
 * The RequestStateService is used to track the state of requests
 * for fetching or updating known resource identifies that are inflight.
 *
 * @class RequestStateService
 * @public
 */
class RequestStateService {
  constructor(store) {
    this._pending = Object.create(null);
    this._done = new Map();
    this._subscriptions = Object.create(null);
    this._toFlush = [];
    this._store = void 0;
    this._store = store;
  }
  _clearEntries(identifier) {
    this._done.delete(identifier);
  }
  _enqueue(promise, queryRequest) {
    let query = queryRequest.data[0];
    if (hasRecordIdentifier(query)) {
      let lid = query.recordIdentifier.lid;
      let type = query.op === 'saveRecord' ? 'mutation' : 'query';
      if (!this._pending[lid]) {
        this._pending[lid] = [];
      }
      let request = {
        state: 'pending',
        request: queryRequest,
        type
      };
      request[Touching] = [query.recordIdentifier];
      request[RequestPromise] = promise;
      this._pending[lid].push(request);
      this._triggerSubscriptions(request);
      return promise.then(result => {
        this._dequeue(lid, request);
        let finalizedRequest = {
          state: 'fulfilled',
          request: queryRequest,
          type,
          response: {
            data: result
          }
        };
        finalizedRequest[Touching] = request[Touching];
        this._addDone(finalizedRequest);
        this._triggerSubscriptions(finalizedRequest);
        return result;
      }, error => {
        this._dequeue(lid, request);
        let finalizedRequest = {
          state: 'rejected',
          request: queryRequest,
          type,
          response: {
            data: error
          }
        };
        finalizedRequest[Touching] = request[Touching];
        this._addDone(finalizedRequest);
        this._triggerSubscriptions(finalizedRequest);
        throw error;
      });
    }
    assert(`Expected a well formed  query`);
  }
  _triggerSubscriptions(req) {
    if (req.state === 'pending') {
      this._flushRequest(req);
      return;
    }
    this._toFlush.push(req);
    if (this._toFlush.length === 1) {
      this._store.notifications._onNextFlush(() => {
        this._flush();
      });
    }
  }
  _flush() {
    this._toFlush.forEach(req => {
      this._flushRequest(req);
    });
    this._toFlush = [];
  }
  _flushRequest(req) {
    req[Touching].forEach(identifier => {
      if (this._subscriptions[identifier.lid]) {
        this._subscriptions[identifier.lid].forEach(callback => callback(req));
      }
    });
  }
  _dequeue(lid, request) {
    this._pending[lid] = this._pending[lid].filter(req => req !== request);
  }
  _addDone(request) {
    request[Touching].forEach(identifier => {
      // TODO add support for multiple
      let requestDataOp = request.request.data[0].op;
      let requests = this._done.get(identifier);
      if (requests) {
        requests = requests.filter(req => {
          // TODO add support for multiple
          let data;
          if (req.request.data instanceof Array) {
            data = req.request.data[0];
          } else {
            data = req.request.data;
          }
          return data.op !== requestDataOp;
        });
      }
      requests = requests || [];
      requests.push(request);
      this._done.set(identifier, requests);
    });
  }

  /**
   * Subscribe to requests for a given resource identity.
   *
   * The callback will receive the current state of the request.
   *
   * ```ts
   * interface RequestState {
   *   state: 'pending' | 'fulfilled' | 'rejected';
   *   type: 'query' | 'mutation';
   *   request: Request;
   *   response?: { data: unknown };
   * }
   * ```
   *
   * Note: It should be considered dangerous to use this API for more than simple
   * state derivation or debugging. The `request` and `response` properties are poorly
   * spec'd and may change unexpectedly when shifting what Handlers are in use or how
   * requests are issued from the Store.
   *
   * We expect to revisit this API in the near future as we continue to refine the
   * RequestManager ergonomics, as a simpler but more powerful direct integration
   * with the RequestManager for these purposes is likely to be a better long-term
   * design.
   *
   * @method subscribeForRecord
   * @public
   * @param {StableRecordIdentifier} identifier
   * @param {(state: RequestState) => void} callback
   */
  subscribeForRecord(identifier, callback) {
    if (!this._subscriptions[identifier.lid]) {
      this._subscriptions[identifier.lid] = [];
    }
    this._subscriptions[identifier.lid].push(callback);
  }

  /**
   * Retrieve all active requests for a given resource identity.
   *
   * @method getPendingRequestsForRecord
   * @public
   * @param {StableRecordIdentifier} identifier
   * @returns {RequestState[]} an array of request states for any pending requests for the given identifier
   */
  getPendingRequestsForRecord(identifier) {
    if (this._pending[identifier.lid]) {
      return this._pending[identifier.lid];
    }
    return [];
  }

  /**
   * Retrieve the last completed request for a given resource identity.
   *
   * @method getLastRequestForRecord
   * @public
   * @param {StableRecordIdentifier} identifier
   * @returns {RequestState | null} the state of the most recent request for the given identifier
   */
  getLastRequestForRecord(identifier) {
    let requests = this._done.get(identifier);
    if (requests) {
      return requests[requests.length - 1];
    }
    return null;
  }
}

/**
  @module @ember-data/store
 */

// hello world

let _Cache;

/**
 * A Store coordinates interaction between your application, a [Cache](https://api.emberjs.com/ember-data/release/classes/%3CInterface%3E%20Cache),
 * and sources of data (such as your API or a local persistence layer)
 * accessed via a [RequestManager](https://github.com/emberjs/data/tree/main/packages/request).
 *
 * ```app/services/store.js
 * import Store from '@ember-data/store';
 *
 * export default class extends Store {}
 * ```
 *
 * Most Ember applications will only have a single `Store` configured as a Service
 * in this manner. However, setting up multiple stores is possible, including using
 * each as a unique service.
 *

  @class Store
  @public
*/ // @ts-expect-error
class Store extends EmberObject {
  /**
   * Provides access to the NotificationManager associated
   * with this Store instance.
   *
   * The NotificationManager can be used to subscribe to
   * changes to the cache.
   *
   * @property {NotificationManager} notifications
   * @public
   */

  /**
   * Provides access to the SchemaService instance
   * for this Store instance.
   *
   * The SchemaService can be used to query for
   * information about the schema of a resource.
   *
   * @property {SchemaService} schema
   * @public
   */
  get schema() {
    return this.getSchemaDefinitionService();
  }

  /**
   * Provides access to the IdentifierCache instance
   * for this store.
   *
   * The IdentifierCache can be used to generate or
   * retrieve a stable unique identifier for any resource.
   *
   * @property {IdentifierCache} identifierCache
   * @public
   */

  /**
   * Provides access to the requestManager instance associated
   * with this Store instance.
   *
   * When using `ember-data` this property is automatically
   * set to an instance of `RequestManager`. When not using `ember-data`
   * you must configure this property yourself, either by declaring
   * it as a service or by initializing it.
   *
   * ```ts
   * import Store, { CacheHandler } from '@ember-data/store';
   * import RequestManager from '@ember-data/request';
   * import Fetch from '@ember/data/request/fetch';
   *
   * class extends Store {
   *   constructor() {
   *     super(...arguments);
   *     this.requestManager = new RequestManager();
   *     this.requestManager.use([Fetch]);
   *     this.requestManager.useCache(CacheHandler);
   *   }
   * }
   * ```
   *
   * @public
   * @property {RequestManager} requestManager
   */

  /**
   * A Property which an App may set to provide a Lifetimes Service
   * to control when a cached request becomes stale.
   *
   * Note, when defined, these methods will only be invoked if a
   * cache key exists for the request, either because the request
   * contains `cacheOptions.key` or because the [IdentifierCache](/ember-data/release/classes/IdentifierCache)
   * was able to generate a key for the request using the configured
   * [generation method](/ember-data/release/functions/@ember-data%2Fstore/setIdentifierGenerationMethod).
   *
   * `isSoftExpired` will only be invoked if `isHardExpired` returns `false`.
   *
   * ```ts
   * store.lifetimes = {
   *   // make the request and ignore the current cache state
   *   isHardExpired(identifier: StableDocumentIdentifier): boolean {
   *     return false;
   *   }
   *
   *   // make the request in the background if true, return cache state
   *   isSoftExpired(identifier: StableDocumentIdentifier): boolean {
   *     return false;
   *   }
   * }
   * ```
   *
   * @public
   * @property {LivetimesService|undefined} lifetimes
   */

  // Private

  // DEBUG-only properties

  // @ts-expect-error
  get isDestroying() {
    return this._isDestroying;
  }
  set isDestroying(value) {
    this._isDestroying = value;
  }
  // @ts-expect-error
  get isDestroyed() {
    return this._isDestroyed;
  }
  set isDestroyed(value) {
    this._isDestroyed = value;
  }

  /**
    @method init
    @private
  */
  constructor(createArgs) {
    super(createArgs);
    Object.assign(this, createArgs);
    this.identifierCache = new IdentifierCache();
    this.notifications = new NotificationManager(this);

    // private but maybe useful to be here, somewhat intimate
    this.recordArrayManager = new RecordArrayManager({
      store: this
    });

    // private
    this._requestCache = new RequestStateService(this);
    this._instanceCache = new InstanceCache(this);
    this._adapterCache = Object.create(null);
    this._serializerCache = Object.create(null);
    this._modelFactoryCache = Object.create(null);
    this._documentCache = new Map();
    this.isDestroying = false;
    this.isDestroyed = false;
  }
  _run(cb) {
    assert(`EmberData should never encounter a nested run`, !this._cbs);
    const _cbs = this._cbs = {};
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      try {
        cb();
        if (_cbs.coalesce) {
          _cbs.coalesce();
        }
        if (_cbs.sync) {
          _cbs.sync();
        }
        if (_cbs.notify) {
          _cbs.notify();
        }
      } finally {
        this._cbs = null;
      }
    } else {
      cb();
      if (_cbs.coalesce) {
        _cbs.coalesce();
      }
      if (_cbs.sync) {
        _cbs.sync();
      }
      if (_cbs.notify) {
        _cbs.notify();
      }
      this._cbs = null;
    }
  }
  _join(cb) {
    if (this._cbs) {
      cb();
    } else {
      this._run(cb);
    }
  }
  _schedule(name, cb) {
    assert(`EmberData expects to schedule only when there is an active run`, !!this._cbs);
    assert(`EmberData expects only one flush per queue name, cannot schedule ${name}`, !this._cbs[name]);
    this._cbs[name] = cb;
  }

  /**
   * Retrieve the RequestStateService instance
   * associated with this Store.
   *
   * This can be used to query the status of requests
   * that have been initiated for a given identifier.
   *
   * @method getRequestStateService
   * @returns {RequestStateService}
   * @public
   */
  getRequestStateService() {
    return this._requestCache;
  }
  _getAllPending() {
    if (macroCondition(getOwnConfig().env.TESTING)) {
      const all = [];
      const pending = this._requestCache._pending;
      const lids = Object.keys(pending);
      lids.forEach(lid => {
        all.push(...pending[lid].map(v => v[RequestPromise]));
      });
      this.requestManager._pending.forEach(v => all.push(v));
      const promise = Promise.allSettled(all);
      promise.length = all.length;
      return promise;
    }
  }

  /**
   * Issue a request via the configured RequestManager,
   * inserting the response into the cache and handing
   * back a Future which resolves to a ResponseDocument
   *
   * Resource data is always updated in the cache.
   *
   * Only GET requests have the request result and document
   * cached by default when a cache key is present.
   *
   * The cache key used is `requestConfig.cacheOptions.key`
   * if present, falling back to `requestconfig.url`.
   *
   * Params are not serialized as part of the cache-key, so
   * either ensure they are already in the url or utilize
   * `requestConfig.cacheOptions.key`. For queries issued
   * via the `POST` method `requestConfig.cacheOptions.key`
   * MUST be supplied for the document to be cached.
   *
   * @method request
   * @param {StoreRequestInfo} requestConfig
   * @returns {Future}
   * @public
   */
  request(requestConfig) {
    // we lazily set the cache handler when we issue the first request
    // because constructor doesn't allow for this to run after
    // the user has had the chance to set the prop.
    let opts = {
      store: this,
      [EnableHydration]: true
    };
    if (macroCondition(getOwnConfig().env.TESTING)) {
      if (this.DISABLE_WAITER) {
        opts.disableTestWaiter = typeof requestConfig.disableTestWaiter === 'boolean' ? requestConfig.disableTestWaiter : true;
      }
    }
    if (macroCondition(getOwnConfig().debug.LOG_REQUESTS)) {
      let options;
      try {
        options = JSON.parse(JSON.stringify(requestConfig));
      } catch {
        options = requestConfig;
      }
      // eslint-disable-next-line no-console
      console.log(`request: [[START]] ${requestConfig.op && !requestConfig.url ? '(LEGACY) ' : ''}${requestConfig.op || '<unknown operation>'} ${requestConfig.url || '<empty url>'}  ${requestConfig.method || '<empty method>'}`, options);
    }
    const future = this.requestManager.request(Object.assign(requestConfig, opts));
    future.onFinalize(() => {
      if (macroCondition(getOwnConfig().debug.LOG_REQUESTS)) {
        // eslint-disable-next-line no-console
        console.log(`request: [[FINALIZE]] ${requestConfig.op && !requestConfig.url ? '(LEGACY) ' : ''}${requestConfig.op || '<unknown operation>'} ${requestConfig.url || '<empty url>'}  ${requestConfig.method || '<empty method>'}`);
      }
      // skip flush for legacy belongsTo
      if (requestConfig.op === 'findBelongsTo' && !requestConfig.url) {
        return;
      }
      this.notifications._flush();
    });
    return future;
  }

  /**
   * A hook which an app or addon may implement. Called when
   * the Store is attempting to create a Record Instance for
   * a resource.
   *
   * This hook can be used to select or instantiate any desired
   * mechanism of presentating cache data to the ui for access
   * mutation, and interaction.
   *
   * @method instantiateRecord (hook)
   * @param identifier
   * @param createRecordArgs
   * @param recordDataFor deprecated use this.cache
   * @param notificationManager deprecated use this.notifications
   * @returns A record instance
   * @public
   */
  instantiateRecord(identifier, createRecordArgs) {
    if (macroCondition(getOwnConfig().packages.HAS_MODEL_PACKAGE)) {
      let modelName = identifier.type;
      const cache = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? this._instanceCache.getResourceCache(identifier) : this.cache;
      // TODO deprecate allowing unknown args setting
      let createOptions = {
        _createProps: createRecordArgs,
        // TODO @deprecate consider deprecating accessing record properties during init which the below is necessary for
        _secretInit: {
          identifier,
          cache,
          store: this,
          cb: secretInit
        }
      };

      // ensure that `getOwner(this)` works inside a model instance
      setOwner(createOptions, getOwner(this));
      const factory = getModelFactory(this, this._modelFactoryCache, modelName);
      assert(`No model was found for '${modelName}'`, factory);
      return factory.class.create(createOptions);
    }
    assert(`You must implement the store's instantiateRecord hook for your custom model class.`);
  }

  /**
   * A hook which an app or addon may implement. Called when
   * the Store is destroying a Record Instance. This hook should
   * be used to teardown any custom record instances instantiated
   * with `instantiateRecord`.
   *
   * @method teardownRecord (hook)
   * @public
   * @param record
   */
  teardownRecord(record) {
    if (macroCondition(getOwnConfig().packages.HAS_MODEL_PACKAGE)) {
      assert(`expected to receive an instance of DSModel. If using a custom model make sure you implement teardownRecord`, 'destroy' in record);
      record.destroy();
    } else {
      assert(`You must implement the store's teardownRecord hook for your custom models`);
    }
  }

  /**
   * Provides access to the SchemaDefinitionService instance
   * for this Store instance.
   *
   * The SchemaDefinitionService can be used to query for
   * information about the schema of a resource.
   *
   * @method getSchemaDefinitionService
   * @public
   */
  getSchemaDefinitionService() {
    if (macroCondition(getOwnConfig().packages.HAS_MODEL_PACKAGE)) {
      if (!this._schema) {
        // it is potentially a mistake for the RFC to have not enabled chaining these services, though highlander rule is nice.
        // what ember-m3 did via private API to allow both worlds to interop would be much much harder using this.
        this._schema = new DSModelSchemaDefinitionService(this);
      }
    }
    assert(`You must registerSchemaDefinitionService with the store to use custom model classes`, this._schema);
    return this._schema;
  }

  /**
   * DEPRECATED - Use `registerSchema` instead.
   *
   * Allows an app to register a custom SchemaService
   * for use when information about a resource's schema needs
   * to be queried.
   *
   * This method can only be called more than once, but only one schema
   * definition service may exist. Therefore if you wish to chain services
   * you must lookup the existing service and close over it with the new
   * service by accessing `store.schema` prior to registration.
   *
   * For Example:
   *
   * ```ts
   * import Store from '@ember-data/store';
   *
   * class SchemaDelegator {
   *   constructor(schema) {
   *     this._schema = schema;
   *   }
   *
   *   doesTypeExist(type: string): boolean {
   *     if (AbstractSchemas.has(type)) {
   *       return true;
   *     }
   *     return this._schema.doesTypeExist(type);
   *   }
   *
   *   attributesDefinitionFor(identifier: RecordIdentifier | { type: string }): AttributesSchema {
   *     return this._schema.attributesDefinitionFor(identifier);
   *   }
   *
   *   relationshipsDefinitionFor(identifier: RecordIdentifier | { type: string }): RelationshipsSchema {
   *     const schema = AbstractSchemas.get(identifier.type);
   *     return schema || this._schema.relationshipsDefinitionFor(identifier);
   *   }
   * }
   *
   * export default class extends Store {
   *   constructor(...args) {
   *     super(...args);
   *
   *     const schema = this.schema;
   *     this.registerSchemaDefinitionService(new SchemaDelegator(schema));
   *   }
   * }
   * ```
   *
   * @method registerSchemaDefinitionService
   * @param {SchemaService} schema
   * @deprecated
   * @public
   */
  registerSchemaDefinitionService(schema) {
    this._schema = schema;
  }
  /**
   * Allows an app to register a custom SchemaService
   * for use when information about a resource's schema needs
   * to be queried.
   *
   * This method can only be called more than once, but only one schema
   * definition service may exist. Therefore if you wish to chain services
   * you must lookup the existing service and close over it with the new
   * service by accessing `store.schema` prior to registration.
   *
   * For Example:
   *
   * ```ts
   * import Store from '@ember-data/store';
   *
   * class SchemaDelegator {
   *   constructor(schema) {
   *     this._schema = schema;
   *   }
   *
   *   doesTypeExist(type: string): boolean {
   *     if (AbstractSchemas.has(type)) {
   *       return true;
   *     }
   *     return this._schema.doesTypeExist(type);
   *   }
   *
   *   attributesDefinitionFor(identifier: RecordIdentifier | { type: string }): AttributesSchema {
   *     return this._schema.attributesDefinitionFor(identifier);
   *   }
   *
   *   relationshipsDefinitionFor(identifier: RecordIdentifier | { type: string }): RelationshipsSchema {
   *     const schema = AbstractSchemas.get(identifier.type);
   *     return schema || this._schema.relationshipsDefinitionFor(identifier);
   *   }
   * }
   *
   * export default class extends Store {
   *   constructor(...args) {
   *     super(...args);
   *
   *     const schema = this.schema;
   *     this.registerSchema(new SchemaDelegator(schema));
   *   }
   * }
   * ```
   *
   * @method registerSchema
   * @param {SchemaService} schema
   * @public
   */
  registerSchema(schema) {
    this._schema = schema;
  }

  /**
    Returns the schema for a particular `modelName`.
     When used with Model from @ember-data/model the return is the model class,
    but this is not guaranteed.
     If looking to query attribute or relationship information it is
    recommended to use `getSchemaDefinitionService` instead. This method
    should be considered legacy and exists primarily to continue to support
    Adapter/Serializer APIs which expect it's return value in their method
    signatures.
     The class of a model might be useful if you want to get a list of all the
    relationship names of the model, see
    [`relationshipNames`](/ember-data/release/classes/Model?anchor=relationshipNames)
    for example.
     @method modelFor
    @public
    @param {String} modelName
    @return {subclass of Model | ShimModelClass}
    */
  // TODO @deprecate in favor of schema APIs, requires adapter/serializer overhaul or replacement

  modelFor(modelName) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyedStoreOnly(this, 'modelFor');
    }
    assert(`You need to pass a model name to the store's modelFor method`, modelName);
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
    if (macroCondition(getOwnConfig().packages.HAS_MODEL_PACKAGE)) {
      let normalizedModelName = normalizeModelName$1(modelName);
      let maybeFactory = getModelFactory(this, this._modelFactoryCache, normalizedModelName);

      // for factorFor factory/class split
      const klass = maybeFactory && maybeFactory.class ? maybeFactory.class : null;
      if (!klass || !klass.isModel || this._forceShim) {
        assert(`No model was found for '${modelName}' and no schema handles the type`, this.getSchemaDefinitionService().doesTypeExist(modelName));
        return getShimClass(this, modelName);
      } else {
        // TODO @deprecate ever returning the klass, always return the shim
        return klass;
      }
    }
    assert(`No model was found for '${modelName}' and no schema handles the type`, this.getSchemaDefinitionService().doesTypeExist(modelName));
    return getShimClass(this, modelName);
  }

  /**
    Create a new record in the current store. The properties passed
    to this method are set on the newly created record.
     To create a new instance of a `Post`:
     ```js
    store.createRecord('post', {
      title: 'Ember is awesome!'
    });
    ```
     To create a new instance of a `Post` that has a relationship with a `User` record:
     ```js
    let user = this.store.peekRecord('user', 1);
    store.createRecord('post', {
      title: 'Ember is awesome!',
      user: user
    });
    ```
     @method createRecord
    @public
    @param {String} modelName
    @param {Object} inputProperties a hash of properties to set on the
      newly created record.
    @return {Model} record
  */
  createRecord(modelName, inputProperties) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'createRecord');
    }
    assert(`You need to pass a model name to the store's createRecord method`, modelName);
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');

    // This is wrapped in a `run.join` so that in test environments users do not need to manually wrap
    //   calls to `createRecord`. The run loop usage here is because we batch the joining and updating
    //   of record-arrays via ember's run loop, not our own.
    //
    //   to remove this, we would need to move to a new `async` API.
    let record;
    _backburner.join(() => {
      this._join(() => {
        let normalizedModelName = normalizeModelName$1(modelName);
        let properties = {
          ...inputProperties
        };

        // If the passed properties do not include a primary key,
        // give the adapter an opportunity to generate one. Typically,
        // client-side ID generators will use something like uuid.js
        // to avoid conflicts.

        if (properties.id === null || properties.id === undefined) {
          let adapter = this.adapterFor(modelName);
          if (adapter && adapter.generateIdForRecord) {
            properties.id = adapter.generateIdForRecord(this, modelName, properties);
          } else {
            properties.id = null;
          }
        }

        // Coerce ID to a string
        properties.id = coerceId(properties.id);
        const resource = {
          type: normalizedModelName,
          id: properties.id
        };
        if (resource.id) {
          const identifier = this.identifierCache.peekRecordIdentifier(resource);
          assert(`The id ${properties.id} has already been used with another '${normalizedModelName}' record.`, !identifier);
        }
        const identifier = this.identifierCache.createIdentifierForNewRecord(resource);
        const cache = macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? this._instanceCache.getResourceCache(identifier) : this.cache;
        const createOptions = normalizeProperties(this, identifier, properties, cache.managedVersion === '1');
        const resultProps = cache.clientDidCreate(identifier, createOptions);
        record = this._instanceCache.getRecord(identifier, resultProps);
      });
    });
    return record;
  }

  /**
    For symmetry, a record can be deleted via the store.
     Example
     ```javascript
    let post = store.createRecord('post', {
      title: 'Ember is awesome!'
    });
     store.deleteRecord(post);
    ```
     @method deleteRecord
    @public
    @param {Model} record
  */
  deleteRecord(record) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'deleteRecord');
    }
    const identifier = peekRecordIdentifier(record);
    const cache = identifier && (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? this._instanceCache.peek({
      identifier,
      bucket: 'resourceCache'
    }) : this.cache);
    assert(`expected a cache instance to exist for the record`, cache);
    this._join(() => {
      cache.setIsDeleted(identifier, true);
      if (cache.isNew(identifier)) {
        _backburner.join(() => {
          this._instanceCache.unloadRecord(identifier);
        });
      }
    });
  }

  /**
    For symmetry, a record can be unloaded via the store.
    This will cause the record to be destroyed and freed up for garbage collection.
     Example
     ```javascript
    store.findRecord('post', 1).then(function(post) {
      store.unloadRecord(post);
    });
    ```
     @method unloadRecord
    @public
    @param {Model} record
  */
  unloadRecord(record) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'unloadRecord');
    }
    const identifier = peekRecordIdentifier(record);
    if (identifier) {
      this._instanceCache.unloadRecord(identifier);
    }
  }

  /**
    @method find
    @param {String} modelName
    @param {String|Integer} id
    @param {Object} options
    @return {Promise} promise
    @deprecated
    @private
  */
  find(modelName, id, options) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'find');
    }
    // The default `model` hook in Route calls `find(modelName, id)`,
    // that's why we have to keep this method around even though `findRecord` is
    // the public way to get a record by modelName and id.
    assert(`Using store.find(type) has been removed. Use store.findAll(modelName) to retrieve all records for a given type.`, arguments.length !== 1);
    assert(`Calling store.find(modelName, id, { preload: preload }) is no longer supported. Use store.findRecord(modelName, id, { preload: preload }) instead.`, !options);
    assert(`You need to pass the model name and id to the store's find method`, arguments.length === 2);
    assert(`You cannot pass '${id}' as id to the store's find method`, typeof id === 'string' || typeof id === 'number');
    assert(`Calling store.find() with a query object is no longer supported. Use store.query() instead.`, typeof id !== 'object');
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_STORE_FIND)) {
      deprecate(`Using store.find is deprecated, use store.findRecord instead. Likely this means you are relying on the implicit store fetching behavior of routes unknowingly.`, false, {
        id: 'ember-data:deprecate-store-find',
        since: {
          available: '4.5',
          enabled: '4.5'
        },
        for: 'ember-data',
        until: '5.0'
      });
      return this.findRecord(modelName, id);
    }
    assert(`store.find has been removed. Use store.findRecord instead.`);
  }

  /**
    This method returns a record for a given identifier or type and id combination.
     The `findRecord` method will always resolve its promise with the same
    object for a given identifier or type and `id`.
     The `findRecord` method will always return a **promise** that will be
    resolved with the record.
     **Example 1**
     ```app/routes/post.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model({ post_id }) {
        return this.store.findRecord('post', post_id);
      }
    }
    ```
     **Example 2**
     `findRecord` can be called with a single identifier argument instead of the combination
    of `type` (modelName) and `id` as separate arguments. You may recognize this combo as
    the typical pairing from [JSON:API](https://jsonapi.org/format/#document-resource-object-identification)
     ```app/routes/post.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model({ post_id: id }) {
        return this.store.findRecord({ type: 'post', id });
      }
    }
    ```
     **Example 3**
     If you have previously received an lid via an Identifier for this record, and the record
    has already been assigned an id, you can find the record again using just the lid.
     ```app/routes/post.js
    store.findRecord({ lid });
    ```
     If the record is not yet available, the store will ask the adapter's `findRecord`
    method to retrieve and supply the necessary data. If the record is already present
    in the store, it depends on the reload behavior _when_ the returned promise
    resolves.
     ### Preloading
     You can optionally `preload` specific attributes and relationships that you know of
    by passing them via the passed `options`.
     For example, if your Ember route looks like `/posts/1/comments/2` and your API route
    for the comment also looks like `/posts/1/comments/2` if you want to fetch the comment
    without also fetching the post you can pass in the post to the `findRecord` call:
     ```app/routes/post-comments.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model({ post_id, comment_id: id }) {
        return this.store.findRecord({ type: 'comment', id, { preload: { post: post_id }} });
      }
    }
    ```
     In your adapter you can then access this id without triggering a network request via the
    snapshot:
     ```app/adapters/application.js
    import EmberObject from '@ember/object';
     export default class Adapter extends EmberObject {
       findRecord(store, schema, id, snapshot) {
        let type = schema.modelName;
         if (type === 'comment')
          let postId = snapshot.belongsTo('post', { id: true });
           return fetch(`./posts/${postId}/comments/${id}`)
            .then(response => response.json())
        }
      }
    }
    ```
     This could also be achieved by supplying the post id to the adapter via the adapterOptions
    property on the options hash.
     ```app/routes/post-comments.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model({ post_id, comment_id: id }) {
        return this.store.findRecord({ type: 'comment', id, { adapterOptions: { post: post_id }} });
      }
    }
    ```
     ```app/adapters/application.js
    import EmberObject from '@ember/object';
     export default class Adapter extends EmberObject {
       findRecord(store, schema, id, snapshot) {
        let type = schema.modelName;
         if (type === 'comment')
          let postId = snapshot.adapterOptions.post;
           return fetch(`./posts/${postId}/comments/${id}`)
            .then(response => response.json())
        }
      }
    }
    ```
     If you have access to the post model you can also pass the model itself to preload:
     ```javascript
    let post = await store.findRecord('post', 1);
    let comment = await store.findRecord('comment', 2, { post: myPostModel });
    ```
     ### Reloading
     The reload behavior is configured either via the passed `options` hash or
    the result of the adapter's `shouldReloadRecord`.
     If `{ reload: true }` is passed or `adapter.shouldReloadRecord` evaluates
    to `true`, then the returned promise resolves once the adapter returns
    data, regardless if the requested record is already in the store:
     ```js
    store.push({
      data: {
        id: 1,
        type: 'post',
        revision: 1
      }
    });
     // adapter#findRecord resolves with
    // [
    //   {
    //     id: 1,
    //     type: 'post',
    //     revision: 2
    //   }
    // ]
    store.findRecord('post', 1, { reload: true }).then(function(post) {
      post.revision; // 2
    });
    ```
     If no reload is indicated via the above mentioned ways, then the promise
    immediately resolves with the cached version in the store.
     ### Background Reloading
     Optionally, if `adapter.shouldBackgroundReloadRecord` evaluates to `true`,
    then a background reload is started, which updates the records' data, once
    it is available:
     ```js
    // app/adapters/post.js
    import ApplicationAdapter from "./application";
     export default class PostAdapter extends ApplicationAdapter {
      shouldReloadRecord(store, snapshot) {
        return false;
      },
       shouldBackgroundReloadRecord(store, snapshot) {
        return true;
      }
    });
     // ...
     store.push({
      data: {
        id: 1,
        type: 'post',
        revision: 1
      }
    });
     let blogPost = store.findRecord('post', 1).then(function(post) {
      post.revision; // 1
    });
     // later, once adapter#findRecord resolved with
    // [
    //   {
    //     id: 1,
    //     type: 'post',
    //     revision: 2
    //   }
    // ]
     blogPost.revision; // 2
    ```
     If you would like to force or prevent background reloading, you can set a
    boolean value for `backgroundReload` in the options object for
    `findRecord`.
     ```app/routes/post/edit.js
    import Route from '@ember/routing/route';
     export default class PostEditRoute extends Route {
      model(params) {
        return this.store.findRecord('post', params.post_id, { backgroundReload: false });
      }
    }
    ```
     If you pass an object on the `adapterOptions` property of the options
    argument it will be passed to your adapter via the snapshot
     ```app/routes/post/edit.js
    import Route from '@ember/routing/route';
     export default class PostEditRoute extends Route {
      model(params) {
        return this.store.findRecord('post', params.post_id, {
          adapterOptions: { subscribe: false }
        });
      }
    }
    ```
     ```app/adapters/post.js
    import MyCustomAdapter from './custom-adapter';
     export default class PostAdapter extends MyCustomAdapter {
      findRecord(store, type, id, snapshot) {
        if (snapshot.adapterOptions.subscribe) {
          // ...
        }
        // ...
      }
    }
    ```
     See [peekRecord](../methods/peekRecord?anchor=peekRecord) to get the cached version of a record.
     ### Retrieving Related Model Records
     If you use an adapter such as Ember's default
    [`JSONAPIAdapter`](/ember-data/release/classes/JSONAPIAdapter)
    that supports the [JSON API specification](http://jsonapi.org/) and if your server
    endpoint supports the use of an
    ['include' query parameter](http://jsonapi.org/format/#fetching-includes),
    you can use `findRecord()` or `findAll()` to automatically retrieve additional records related to
    the one you request by supplying an `include` parameter in the `options` object.
     For example, given a `post` model that has a `hasMany` relationship with a `comment`
    model, when we retrieve a specific post we can have the server also return that post's
    comments in the same request:
     ```app/routes/post.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model(params) {
        return this.store.findRecord('post', params.post_id, { include: 'comments' });
      }
    }
    ```
     ```app/adapters/application.js
    import EmberObject from '@ember/object';
     export default class Adapter extends EmberObject {
       findRecord(store, schema, id, snapshot) {
        let type = schema.modelName;
         if (type === 'post')
          let includes = snapshot.adapterOptions.include;
           return fetch(`./posts/${postId}?include=${includes}`)
            .then(response => response.json())
        }
      }
    }
    ```
     In this case, the post's comments would then be available in your template as
    `model.comments`.
     Multiple relationships can be requested using an `include` parameter consisting of a
    comma-separated list (without white-space) while nested relationships can be specified
    using a dot-separated sequence of relationship names. So to request both the post's
    comments and the authors of those comments the request would look like this:
     ```app/routes/post.js
    import Route from '@ember/routing/route';
     export default class PostRoute extends Route {
      model(params) {
        return this.store.findRecord('post', params.post_id, { include: 'comments,comments.author' });
      }
    }
    ```
     ### Retrieving Specific Fields by Type
     If your server endpoint supports the use of a ['fields' query parameter](https://jsonapi.org/format/#fetching-sparse-fieldsets),
    you can use pass those fields through to your server.  At this point in time, this requires a few manual steps on your part.
     1. Implement `buildQuery` in your adapter.
     ```app/adapters/application.js
    buildQuery(snapshot) {
      let query = super.buildQuery(...arguments);
       let { fields } = snapshot.adapterOptions;
       if (fields) {
        query.fields = fields;
      }
       return query;
    }
    ```
     2. Then pass through the applicable fields to your `findRecord` request.
     Given a `post` model with attributes body, title, publishDate and meta, you can retrieve a filtered list of attributes.
     ```app/routes/post.js
    import Route from '@ember/routing/route';
    export default Route.extend({
      model(params) {
        return this.store.findRecord('post', params.post_id, { adapterOptions: { fields: { post: 'body,title' } });
      }
    });
    ```
     Moreover, you can filter attributes on related models as well. If a `post` has a `belongsTo` relationship to a user,
    just include the relationship key and attributes.
     ```app/routes/post.js
    import Route from '@ember/routing/route';
    export default Route.extend({
      model(params) {
        return this.store.findRecord('post', params.post_id, { adapterOptions: { fields: { post: 'body,title', user: 'name,email' } });
      }
    });
    ```
     @since 1.13.0
    @method findRecord
    @public
    @param {String|object} modelName - either a string representing the modelName or a ResourceIdentifier object containing both the type (a string) and the id (a string) for the record or an lid (a string) of an existing record
    @param {(String|Integer|Object)} id - optional object with options for the request only if the first param is a ResourceIdentifier, else the string id of the record to be retrieved
    @param {Object} [options] - if the first param is a string this will be the optional options for the request. See examples for available options.
    @return {Promise} promise
  */

  findRecord(resource, id, options) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'findRecord');
    }
    assert(`You need to pass a modelName or resource identifier as the first argument to the store's findRecord method`, resource);
    if (isMaybeIdentifier(resource)) {
      options = id;
    } else {
      assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${resource}`, typeof resource === 'string');
      const type = normalizeModelName$1(resource);
      const normalizedId = ensureStringId(id);
      resource = constructResource(type, normalizedId);
    }
    const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
    options = options || {};
    if (options.preload) {
      // force reload if we preload to ensure we don't resolve the promise
      // until we are complete, else we will end up background-reloading
      // even for initial load.
      if (!this._instanceCache.recordIsLoaded(identifier)) {
        options.reload = true;
      }
      this._join(() => {
        preloadData(this, identifier, options.preload);
      });
    }
    const promise = this.request({
      op: 'findRecord',
      data: {
        record: identifier,
        options
      },
      cacheOptions: {
        [SkipCache]: true
      }
    });
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseObject(promise.then(document => {
        return document.content;
      }));
    }
    return promise.then(document => {
      return document.content;
    });
  }

  /**
    Get the reference for the specified record.
     Example
     ```javascript
    let userRef = store.getReference('user', 1);
     // check if the user is loaded
    let isLoaded = userRef.value() !== null;
     // get the record of the reference (null if not yet available)
    let user = userRef.value();
     // get the identifier of the reference
    if (userRef.remoteType() === 'id') {
    let id = userRef.id();
    }
     // load user (via store.find)
    userRef.load().then(...)
     // or trigger a reload
    userRef.reload().then(...)
     // provide data for reference
    userRef.push({ id: 1, username: '@user' }).then(function(user) {
      userRef.value() === user;
    });
    ```
     @method getReference
    @public
    @param {String|object} resource - modelName (string) or Identifier (object)
    @param {String|Integer} id
    @since 2.5.0
    @return {RecordReference}
  */
  // TODO @deprecate getReference (and references generally)
  getReference(resource, id) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'getReference');
    }
    let resourceIdentifier;
    if (arguments.length === 1 && isMaybeIdentifier(resource)) {
      resourceIdentifier = resource;
    } else {
      const type = normalizeModelName$1(resource);
      const normalizedId = ensureStringId(id);
      resourceIdentifier = constructResource(type, normalizedId);
    }
    assert('getReference expected to receive either a resource identifier or type and id as arguments', isMaybeIdentifier(resourceIdentifier));
    let identifier = this.identifierCache.getOrCreateRecordIdentifier(resourceIdentifier);
    return this._instanceCache.getReference(identifier);
  }

  /**
    Get a record by a given type and ID without triggering a fetch.
     This method will synchronously return the record if it is available in the store,
    otherwise it will return `null`. A record is available if it has been fetched earlier, or
    pushed manually into the store.
     See [findRecord](../methods/findRecord?anchor=findRecord) if you would like to request this record from the backend.
     _Note: This is a synchronous method and does not return a promise._
     **Example 1**
     ```js
    let post = store.peekRecord('post', 1);
     post.id; // 1
    ```
     `peekRecord` can be called with a single identifier argument instead of the combination
    of `type` (modelName) and `id` as separate arguments. You may recognize this combo as
    the typical pairing from [JSON:API](https://jsonapi.org/format/#document-resource-object-identification)
     **Example 2**
     ```js
    let post = store.peekRecord({ type: 'post', id });
    post.id; // 1
    ```
     If you have previously received an lid from an Identifier for this record, you can lookup the record again using
    just the lid.
     **Example 3**
     ```js
    let post = store.peekRecord({ lid });
    post.id; // 1
    ```
      @since 1.13.0
    @method peekRecord
    @public
    @param {String|object} modelName - either a string representing the modelName or a ResourceIdentifier object containing both the type (a string) and the id (a string) for the record or an lid (a string) of an existing record
    @param {String|Integer} id - optional only if the first param is a ResourceIdentifier, else the string id of the record to be retrieved.
    @return {Model|null} record
  */

  peekRecord(identifier, id) {
    if (arguments.length === 1 && isMaybeIdentifier(identifier)) {
      const stableIdentifier = this.identifierCache.peekRecordIdentifier(identifier);
      const isLoaded = stableIdentifier && this._instanceCache.recordIsLoaded(stableIdentifier);
      // TODO come up with a better mechanism for determining if we have data and could peek.
      // this is basically an "are we not empty" query.
      return isLoaded ? this._instanceCache.getRecord(stableIdentifier) : null;
    }
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'peekRecord');
    }
    assert(`You need to pass a model name to the store's peekRecord method`, identifier);
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${identifier}`, typeof identifier === 'string');
    const type = normalizeModelName$1(identifier);
    const normalizedId = ensureStringId(id);
    const resource = {
      type,
      id: normalizedId
    };
    const stableIdentifier = this.identifierCache.peekRecordIdentifier(resource);
    const isLoaded = stableIdentifier && this._instanceCache.recordIsLoaded(stableIdentifier);
    return isLoaded ? this._instanceCache.getRecord(stableIdentifier) : null;
  }

  /**
   This method returns true if a record for a given modelName and id is already
   loaded in the store. Use this function to know beforehand if a findRecord()
   will result in a request or that it will be a cache hit.
    Example
    ```javascript
   store.hasRecordForId('post', 1); // false
   store.findRecord('post', 1).then(function() {
     store.hasRecordForId('post', 1); // true
   });
   ```
     @method hasRecordForId
    @deprecated
    @public
    @param {String} modelName
    @param {(String|Integer)} id
    @return {Boolean}
  */
  hasRecordForId(modelName, id) {
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_HAS_RECORD)) {
      deprecate(`store.hasRecordForId has been deprecated in favor of store.peekRecord`, false, {
        id: 'ember-data:deprecate-has-record-for-id',
        since: {
          available: '4.5',
          enabled: '4.5'
        },
        until: '5.0',
        for: 'ember-data'
      });
      if (macroCondition(getOwnConfig().env.DEBUG)) {
        assertDestroyingStore(this, 'hasRecordForId');
      }
      assert(`You need to pass a model name to the store's hasRecordForId method`, modelName);
      assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
      const type = normalizeModelName$1(modelName);
      const trueId = ensureStringId(id);
      const resource = {
        type,
        id: trueId
      };
      const identifier = this.identifierCache.peekRecordIdentifier(resource);
      return Boolean(identifier && this._instanceCache.recordIsLoaded(identifier));
    }
    assert(`store.hasRecordForId has been removed`);
  }

  /**
    This method delegates a query to the adapter. This is the one place where
    adapter-level semantics are exposed to the application.
     Each time this method is called a new request is made through the adapter.
     Exposing queries this way seems preferable to creating an abstract query
    language for all server-side queries, and then require all adapters to
    implement them.
     ---
     If you do something like this:
     ```javascript
    store.query('person', { page: 1 });
    ```
     The request made to the server will look something like this:
     ```
    GET "/api/v1/person?page=1"
    ```
     ---
     If you do something like this:
     ```javascript
    store.query('person', { ids: [1, 2, 3] });
    ```
     The request made to the server will look something like this:
     ```
    GET "/api/v1/person?ids%5B%5D=1&ids%5B%5D=2&ids%5B%5D=3"
    decoded: "/api/v1/person?ids[]=1&ids[]=2&ids[]=3"
    ```
     This method returns a promise, which is resolved with a
    [`Collection`](/ember-data/release/classes/Collection)
    once the server returns.
     @since 1.13.0
    @method query
    @public
    @param {String} modelName
    @param {any} query an opaque query to be used by the adapter
    @param {Object} options optional, may include `adapterOptions` hash which will be passed to adapter.query
    @return {Promise} promise
  */
  query(modelName, query, options) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'query');
    }
    assert(`You need to pass a model name to the store's query method`, modelName);
    assert(`You need to pass a query hash to the store's query method`, query);
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
    const promise = this.request({
      op: 'query',
      data: {
        type: normalizeModelName$1(modelName),
        query,
        options: options || {}
      },
      cacheOptions: {
        [SkipCache]: true
      }
    });
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseArray(promise.then(document => document.content));
    }
    return promise.then(document => document.content);
  }

  /**
    This method makes a request for one record, where the `id` is not known
    beforehand (if the `id` is known, use [`findRecord`](../methods/findRecord?anchor=findRecord)
    instead).
     This method can be used when it is certain that the server will return a
    single object for the primary data.
     Each time this method is called a new request is made through the adapter.
     Let's assume our API provides an endpoint for the currently logged in user
    via:
     ```
    // GET /api/current_user
    {
      user: {
        id: 1234,
        username: 'admin'
      }
    }
    ```
     Since the specific `id` of the `user` is not known beforehand, we can use
    `queryRecord` to get the user:
     ```javascript
    store.queryRecord('user', {}).then(function(user) {
      let username = user.username;
      // do thing
    });
    ```
     The request is made through the adapters' `queryRecord`:
     ```app/adapters/user.js
    import Adapter from '@ember-data/adapter';
    import $ from 'jquery';
     export default class UserAdapter extends Adapter {
      queryRecord(modelName, query) {
        return $.getJSON('/api/current_user');
      }
    }
    ```
     Note: the primary use case for `store.queryRecord` is when a single record
    is queried and the `id` is not known beforehand. In all other cases
    `store.query` and using the first item of the array is likely the preferred
    way:
     ```
    // GET /users?username=unique
    {
      data: [{
        id: 1234,
        type: 'user',
        attributes: {
          username: "unique"
        }
      }]
    }
    ```
     ```javascript
    store.query('user', { username: 'unique' }).then(function(users) {
      return users.firstObject;
    }).then(function(user) {
      let id = user.id;
    });
    ```
     This method returns a promise, which resolves with the found record.
     If the adapter returns no data for the primary data of the payload, then
    `queryRecord` resolves with `null`:
     ```
    // GET /users?username=unique
    {
      data: null
    }
    ```
     ```javascript
    store.queryRecord('user', { username: 'unique' }).then(function(user) {
       // user is null
    });
    ```
     @since 1.13.0
    @method queryRecord
    @public
    @param {String} modelName
    @param {any} query an opaque query to be used by the adapter
    @param {Object} options optional, may include `adapterOptions` hash which will be passed to adapter.queryRecord
    @return {Promise} promise which resolves with the found record or `null`
  */
  queryRecord(modelName, query, options) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'queryRecord');
    }
    assert(`You need to pass a model name to the store's queryRecord method`, modelName);
    assert(`You need to pass a query hash to the store's queryRecord method`, query);
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
    const promise = this.request({
      op: 'queryRecord',
      data: {
        type: normalizeModelName$1(modelName),
        query,
        options: options || {}
      },
      cacheOptions: {
        [SkipCache]: true
      }
    });
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseObject(promise.then(document => document.content));
    }
    return promise.then(document => document.content);
  }

  /**
    `findAll` asks the adapter's `findAll` method to find the records for the
    given type, and returns a promise which will resolve with all records of
    this type present in the store, even if the adapter only returns a subset
    of them.
     ```app/routes/authors.js
    import Route from '@ember/routing/route';
     export default class AuthorsRoute extends Route {
      model(params) {
        return this.store.findAll('author');
      }
    }
    ```
     _When_ the returned promise resolves depends on the reload behavior,
    configured via the passed `options` hash and the result of the adapter's
    `shouldReloadAll` method.
     ### Reloading
     If `{ reload: true }` is passed or `adapter.shouldReloadAll` evaluates to
    `true`, then the returned promise resolves once the adapter returns data,
    regardless if there are already records in the store:
     ```js
    store.push({
      data: {
        id: 'first',
        type: 'author'
      }
    });
     // adapter#findAll resolves with
    // [
    //   {
    //     id: 'second',
    //     type: 'author'
    //   }
    // ]
    store.findAll('author', { reload: true }).then(function(authors) {
      authors.getEach('id'); // ['first', 'second']
    });
    ```
     If no reload is indicated via the above mentioned ways, then the promise
    immediately resolves with all the records currently loaded in the store.
     ### Background Reloading
     Optionally, if `adapter.shouldBackgroundReloadAll` evaluates to `true`,
    then a background reload is started. Once this resolves, the array with
    which the promise resolves, is updated automatically so it contains all the
    records in the store:
     ```app/adapters/application.js
    import Adapter from '@ember-data/adapter';
     export default class ApplicationAdapter extends Adapter {
      shouldReloadAll(store, snapshotsArray) {
        return false;
      },
       shouldBackgroundReloadAll(store, snapshotsArray) {
        return true;
      }
    });
     // ...
     store.push({
      data: {
        id: 'first',
        type: 'author'
      }
    });
     let allAuthors;
    store.findAll('author').then(function(authors) {
      authors.getEach('id'); // ['first']
       allAuthors = authors;
    });
     // later, once adapter#findAll resolved with
    // [
    //   {
    //     id: 'second',
    //     type: 'author'
    //   }
    // ]
     allAuthors.getEach('id'); // ['first', 'second']
    ```
     If you would like to force or prevent background reloading, you can set a
    boolean value for `backgroundReload` in the options object for
    `findAll`.
     ```app/routes/post/edit.js
    import Route from '@ember/routing/route';
     export default class PostEditRoute extends Route {
      model() {
        return this.store.findAll('post', { backgroundReload: false });
      }
    }
    ```
     If you pass an object on the `adapterOptions` property of the options
    argument it will be passed to you adapter via the `snapshotRecordArray`
     ```app/routes/posts.js
    import Route from '@ember/routing/route';
     export default class PostsRoute extends Route {
      model(params) {
        return this.store.findAll('post', {
          adapterOptions: { subscribe: false }
        });
      }
    }
    ```
     ```app/adapters/post.js
    import MyCustomAdapter from './custom-adapter';
     export default class UserAdapter extends MyCustomAdapter {
      findAll(store, type, sinceToken, snapshotRecordArray) {
        if (snapshotRecordArray.adapterOptions.subscribe) {
          // ...
        }
        // ...
      }
    }
    ```
     See [peekAll](../methods/peekAll?anchor=peekAll) to get an array of current records in the
    store, without waiting until a reload is finished.
     ### Retrieving Related Model Records
     If you use an adapter such as Ember's default
    [`JSONAPIAdapter`](/ember-data/release/classes/JSONAPIAdapter)
    that supports the [JSON API specification](http://jsonapi.org/) and if your server
    endpoint supports the use of an
    ['include' query parameter](http://jsonapi.org/format/#fetching-includes),
    you can use `findAll()` to automatically retrieve additional records related to
    those requested by supplying an `include` parameter in the `options` object.
     For example, given a `post` model that has a `hasMany` relationship with a `comment`
    model, when we retrieve all of the post records we can have the server also return
    all of the posts' comments in the same request:
     ```app/routes/posts.js
    import Route from '@ember/routing/route';
     export default class PostsRoute extends Route {
      model() {
        return this.store.findAll('post', { include: 'comments' });
      }
    }
    ```
    Multiple relationships can be requested using an `include` parameter consisting of a
    comma-separated list (without white-space) while nested relationships can be specified
    using a dot-separated sequence of relationship names. So to request both the posts'
    comments and the authors of those comments the request would look like this:
     ```app/routes/posts.js
    import Route from '@ember/routing/route';
     export default class PostsRoute extends Route {
      model() {
        return this.store.findAll('post', { include: 'comments,comments.author' });
      }
    }
    ```
     See [query](../methods/query?anchor=query) to only get a subset of records from the server.
     @since 1.13.0
    @method findAll
    @public
    @param {String} modelName
    @param {Object} options
    @return {Promise} promise
  */
  findAll(modelName, options = {}) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'findAll');
    }
    assert(`You need to pass a model name to the store's findAll method`, modelName);
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
    const promise = this.request({
      op: 'findAll',
      data: {
        type: normalizeModelName$1(modelName),
        options: options || {}
      },
      cacheOptions: {
        [SkipCache]: true
      }
    });
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
      return promiseArray(promise.then(document => document.content));
    }
    return promise.then(document => document.content);
  }

  /**
    This method returns a filtered array that contains all of the
    known records for a given type in the store.
     Note that because it's just a filter, the result will contain any
    locally created records of the type, however, it will not make a
    request to the backend to retrieve additional records. If you
    would like to request all the records from the backend please use
    [store.findAll](../methods/findAll?anchor=findAll).
     Also note that multiple calls to `peekAll` for a given type will always
    return the same `RecordArray`.
     Example
     ```javascript
    let localPosts = store.peekAll('post');
    ```
     @since 1.13.0
    @method peekAll
    @public
    @param {String} modelName
    @return {RecordArray}
  */
  peekAll(modelName) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'peekAll');
    }
    assert(`You need to pass a model name to the store's peekAll method`, modelName);
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
    let type = normalizeModelName$1(modelName);
    return this.recordArrayManager.liveArrayFor(type);
  }

  /**
    This method unloads all records in the store.
    It schedules unloading to happen during the next run loop.
     Optionally you can pass a type which unload all records for a given type.
     ```javascript
    store.unloadAll();
    store.unloadAll('post');
    ```
     @method unloadAll
    @public
    @param {String} modelName
  */
  unloadAll(modelName) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyedStoreOnly(this, 'unloadAll');
    }
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, !modelName || typeof modelName === 'string');
    this._join(() => {
      if (modelName === undefined) {
        // destroy the graph before unloadAll
        // since then we avoid churning relationships
        // during unload
        if (macroCondition(getOwnConfig().packages.HAS_GRAPH_PACKAGE)) {
          const peekGraph = importSync('@ember-data/graph/-private').peekGraph;
          const graph = peekGraph(this);
          if (graph) {
            graph.identifiers.clear();
          }
        }
        this.recordArrayManager.clear();
        this._instanceCache.clear();
      } else {
        let normalizedModelName = normalizeModelName$1(modelName);
        this._instanceCache.clear(normalizedModelName);
      }
    });
  }

  /**
    Push some data for a given type into the store.
     This method expects normalized [JSON API](http://jsonapi.org/) document. This means you have to follow [JSON API specification](http://jsonapi.org/format/) with few minor adjustments:
    - record's `type` should always be in singular, dasherized form
    - members (properties) should be camelCased
     [Your primary data should be wrapped inside `data` property](http://jsonapi.org/format/#document-top-level):
     ```js
    store.push({
      data: {
        // primary data for single record of type `Person`
        id: '1',
        type: 'person',
        attributes: {
          firstName: 'Daniel',
          lastName: 'Kmak'
        }
      }
    });
    ```
     [Demo.](http://ember-twiddle.com/fb99f18cd3b4d3e2a4c7)
     `data` property can also hold an array (of records):
     ```js
    store.push({
      data: [
        // an array of records
        {
          id: '1',
          type: 'person',
          attributes: {
            firstName: 'Daniel',
            lastName: 'Kmak'
          }
        },
        {
          id: '2',
          type: 'person',
          attributes: {
            firstName: 'Tom',
            lastName: 'Dale'
          }
        }
      ]
    });
    ```
     [Demo.](http://ember-twiddle.com/69cdbeaa3702159dc355)
     There are some typical properties for `JSONAPI` payload:
    * `id` - mandatory, unique record's key
    * `type` - mandatory string which matches `model`'s dasherized name in singular form
    * `attributes` - object which holds data for record attributes - `attr`'s declared in model
    * `relationships` - object which must contain any of the following properties under each relationships' respective key (example path is `relationships.achievements.data`):
      - [`links`](http://jsonapi.org/format/#document-links)
      - [`data`](http://jsonapi.org/format/#document-resource-object-linkage) - place for primary data
      - [`meta`](http://jsonapi.org/format/#document-meta) - object which contains meta-information about relationship
     For this model:
     ```app/models/person.js
    import Model, { attr, hasMany } from '@ember-data/model';
     export default class PersonRoute extends Route {
      @attr('string') firstName;
      @attr('string') lastName;
       @hasMany('person') children;
    }
    ```
     To represent the children as IDs:
     ```js
    {
      data: {
        id: '1',
        type: 'person',
        attributes: {
          firstName: 'Tom',
          lastName: 'Dale'
        },
        relationships: {
          children: {
            data: [
              {
                id: '2',
                type: 'person'
              },
              {
                id: '3',
                type: 'person'
              },
              {
                id: '4',
                type: 'person'
              }
            ]
          }
        }
      }
    }
    ```
     [Demo.](http://ember-twiddle.com/343e1735e034091f5bde)
     To represent the children relationship as a URL:
     ```js
    {
      data: {
        id: '1',
        type: 'person',
        attributes: {
          firstName: 'Tom',
          lastName: 'Dale'
        },
        relationships: {
          children: {
            links: {
              related: '/people/1/children'
            }
          }
        }
      }
    }
    ```
     If you're streaming data or implementing an adapter, make sure
    that you have converted the incoming data into this form. The
    store's [normalize](../methods/normalize?anchor=normalize) method is a convenience
    helper for converting a json payload into the form Ember Data
    expects.
     ```js
    store.push(store.normalize('person', data));
    ```
     This method can be used both to push in brand new
    records, as well as to update existing records.
     @method push
    @public
    @param {Object} data
    @return the record(s) that was created or
      updated.
  */

  push(data) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'push');
    }
    let pushed = this._push(data, false);
    if (Array.isArray(pushed)) {
      let records = pushed.map(identifier => this._instanceCache.getRecord(identifier));
      return records;
    }
    if (pushed === null) {
      return null;
    }
    return this._instanceCache.getRecord(pushed);
  }

  /**
    Push some data in the form of a json-api document into the store,
    without creating materialized records.
     @method _push
    @private
    @param {Object} jsonApiDoc
    @return {StableRecordIdentifier|Array<StableRecordIdentifier>} identifiers for the primary records that had data loaded
  */
  _push(jsonApiDoc, asyncFlush) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, '_push');
    }
    if (macroCondition(getOwnConfig().debug.LOG_PAYLOADS)) {
      try {
        let data = JSON.parse(JSON.stringify(jsonApiDoc));
        // eslint-disable-next-line no-console
        console.log('EmberData | Payload - push', data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('EmberData | Payload - push', jsonApiDoc);
      }
    }
    if (asyncFlush) {
      this._enableAsyncFlush = true;
    }
    let ret;
    this._join(() => {
      if (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA)) {
        ret = legacyCachePut(this, {
          content: jsonApiDoc
        });
      } else {
        ret = this.cache.put({
          content: jsonApiDoc
        });
      }
    });
    this._enableAsyncFlush = null;
    return ret.data;
  }

  /**
    Push some raw data into the store.
     This method can be used both to push in brand new
    records, as well as to update existing records. You
    can push in more than one type of object at once.
    All objects should be in the format expected by the
    serializer.
     ```app/serializers/application.js
    import RESTSerializer from '@ember-data/serializer/rest';
     export default class ApplicationSerializer extends RESTSerializer;
    ```
     ```js
    let pushData = {
      posts: [
        { id: 1, postTitle: "Great post", commentIds: [2] }
      ],
      comments: [
        { id: 2, commentBody: "Insightful comment" }
      ]
    }
     store.pushPayload(pushData);
    ```
     By default, the data will be deserialized using a default
    serializer (the application serializer if it exists).
     Alternatively, `pushPayload` will accept a model type which
    will determine which serializer will process the payload.
     ```app/serializers/application.js
    import RESTSerializer from '@ember-data/serializer/rest';
      export default class ApplicationSerializer extends RESTSerializer;
    ```
     ```app/serializers/post.js
    import JSONSerializer from '@ember-data/serializer/json';
     export default JSONSerializer;
    ```
     ```js
    store.pushPayload(pushData); // Will use the application serializer
    store.pushPayload('post', pushData); // Will use the post serializer
    ```
     @method pushPayload
    @public
    @param {String} modelName Optionally, a model type used to determine which serializer will be used
    @param {Object} inputPayload
  */
  // TODO @runspired @deprecate pushPayload in favor of looking up the serializer
  pushPayload(modelName, inputPayload) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'pushPayload');
    }
    let serializer;
    let payload;
    if (!inputPayload) {
      payload = modelName;
      serializer = this.serializerFor('application');
      assert(`You cannot use 'store#pushPayload' without a modelName unless your default serializer defines 'pushPayload'`, typeof serializer.pushPayload === 'function');
    } else {
      payload = inputPayload;
      assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
      let normalizedModelName = normalizeModelName$1(modelName);
      serializer = this.serializerFor(normalizedModelName);
    }
    assert(`You must define a pushPayload method in your serializer in order to call store.pushPayload`, serializer.pushPayload);
    serializer.pushPayload(this, payload);
  }

  // TODO @runspired @deprecate records should implement their own serialization if desired
  serializeRecord(record, options) {
    // TODO we used to check if the record was destroyed here
    if (macroCondition(getOwnConfig().packages.HAS_COMPAT_PACKAGE)) {
      if (!this._fetchManager) {
        const FetchManager = importSync('@ember-data/legacy-compat/-private').FetchManager;
        this._fetchManager = new FetchManager(this);
      }
      return this._fetchManager.createSnapshot(recordIdentifierFor(record)).serialize(options);
    }
    assert(`Store.serializeRecord is only available when utilizing @ember-data/legacy-compat for legacy compatibility`);
  }

  /**
   * Trigger a save for a Record.
   *
   * @method saveRecord
   * @public
   * @param {RecordInstance} record
   * @param options
   * @returns {Promise<RecordInstance>}
   */
  saveRecord(record, options = {}) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'saveRecord');
    }
    assert(`Unable to initate save for a record in a disconnected state`, storeFor(record));
    let identifier = recordIdentifierFor(record);
    const cache = identifier && (macroCondition(getOwnConfig().deprecations.DEPRECATE_V1_RECORD_DATA) ? this._instanceCache.peek({
      identifier,
      bucket: 'resourceCache'
    }) : this.cache);
    if (!cache) {
      // this commonly means we're disconnected
      // but just in case we reject here to prevent bad things.
      return Promise.reject(`Record Is Disconnected`);
    }
    // TODO we used to check if the record was destroyed here
    assert(`Cannot initiate a save request for an unloaded record: ${identifier}`, cache && this._instanceCache.recordIsLoaded(identifier));
    if (resourceIsFullyDeleted(this._instanceCache, identifier)) {
      return Promise.resolve(record);
    }
    if (isDSModel(record)) {
      record.errors.clear();
    }
    if (!options) {
      options = {};
    }
    let operation = 'updateRecord';
    if (cache.isNew(identifier)) {
      operation = 'createRecord';
    } else if (cache.isDeleted(identifier)) {
      operation = 'deleteRecord';
    }
    const request = {
      op: operation,
      data: {
        options,
        record: identifier
      },
      cacheOptions: {
        [SkipCache]: true
      }
    };
    return this.request(request).then(document => document.content);
  }

  /**
   * Instantiation hook allowing applications or addons to configure the store
   * to utilize a custom Cache implementation.
   *
   * This hook should not be called directly by consuming applications or libraries.
   * Use `Store.cache` to access the Cache instance.
   *
   * @method createCache (hook)
   * @public
   * @param storeWrapper
   * @returns {Cache}
   */
  createCache(storeWrapper) {
    if (macroCondition(getOwnConfig().packages.HAS_JSON_API_PACKAGE)) {
      if (_Cache === undefined) {
        _Cache = importSync('@ember-data/json-api').default;
      }
      return new _Cache(storeWrapper);
    }
    assert(`Expected store.createCache to be implemented but it wasn't`);
  }

  /**
   * Returns the cache instance associated to this Store, instantiates the Cache
   * if necessary via `Store.createCache`
   *
   * @property {Cache} cache
   * @public
   */
  get cache() {
    let {
      cache
    } = this._instanceCache;
    if (!cache) {
      cache = this._instanceCache.cache = this.createCache(this._instanceCache._storeWrapper);
      if (macroCondition(getOwnConfig().env.DEBUG)) {
        cache = new SingletonCacheManager(cache);
      }
    }
    return cache;
  }

  /**
   * [DEPRECATED] use Store.createCache
   *
   * Instantiation hook allowing applications or addons to configure the store
   * to utilize a custom RecordData implementation.
   *
   * @method createRecordDataFor (hook)
   * @deprecated
   * @public
   * @param identifier
   * @param storeWrapper
   * @returns {Cache}
   */

  /**
    `normalize` converts a json payload into the normalized form that
    [push](../methods/push?anchor=push) expects.
     Example
     ```js
    socket.on('message', function(message) {
      let modelName = message.model;
      let data = message.data;
      store.push(store.normalize(modelName, data));
    });
    ```
     @method normalize
    @public
    @param {String} modelName The name of the model type for this payload
    @param {Object} payload
    @return {Object} The normalized payload
  */
  // TODO @runspired @deprecate users should call normalize on the associated serializer directly
  normalize(modelName, payload) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'normalize');
    }
    assert(`You need to pass a model name to the store's normalize method`, modelName);
    assert(`Passing classes to store methods has been removed. Please pass a dasherized string instead of ${typeof modelName}`, typeof modelName === 'string');
    let normalizedModelName = normalizeModelName$1(modelName);
    let serializer = this.serializerFor(normalizedModelName);
    let model = this.modelFor(normalizedModelName);
    assert(`You must define a normalize method in your serializer in order to call store.normalize`, serializer?.normalize);
    return serializer.normalize(model, payload);
  }

  /**
    Returns an instance of the adapter for a given type. For
    example, `adapterFor('person')` will return an instance of
    the adapter located at `app/adapters/person.js`
     If no `person` adapter is found, this method will look
    for an `application` adapter (the default adapter for
    your entire application).
     @method adapterFor
    @public
    @param {String} modelName
    @return Adapter
  */
  adapterFor(modelName) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'adapterFor');
    }
    assert(`You need to pass a model name to the store's adapterFor method`, modelName);
    assert(`Passing classes to store.adapterFor has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
    let normalizedModelName = normalizeModelName$1(modelName);
    let {
      _adapterCache
    } = this;
    let adapter = _adapterCache[normalizedModelName];
    if (adapter) {
      return adapter;
    }
    let owner = getOwner(this);

    // name specific adapter
    adapter = owner.lookup(`adapter:${normalizedModelName}`);
    if (adapter !== undefined) {
      _adapterCache[normalizedModelName] = adapter;
      return adapter;
    }

    // no adapter found for the specific name, fallback and check for application adapter
    adapter = _adapterCache.application || owner.lookup('adapter:application');
    if (adapter !== undefined) {
      _adapterCache[normalizedModelName] = adapter;
      _adapterCache.application = adapter;
      return adapter;
    }
    if (macroCondition(getOwnConfig().deprecations.DEPRECATE_JSON_API_FALLBACK)) {
      // final fallback, no model specific adapter, no application adapter, no
      // `adapter` property on store: use json-api adapter
      adapter = _adapterCache['-json-api'] || owner.lookup('adapter:-json-api');
      if (adapter !== undefined) {
        deprecate(`Your application is utilizing a deprecated hidden fallback adapter (-json-api). Please implement an application adapter to function as your fallback.`, false, {
          id: 'ember-data:deprecate-secret-adapter-fallback',
          for: 'ember-data',
          until: '5.0',
          since: {
            available: '4.5',
            enabled: '4.5'
          }
        });
        _adapterCache[normalizedModelName] = adapter;
        _adapterCache['-json-api'] = adapter;
        return adapter;
      }
    }
    assert(`No adapter was found for '${modelName}' and no 'application' adapter was found as a fallback.`);
  }

  /**
    Returns an instance of the serializer for a given type. For
    example, `serializerFor('person')` will return an instance of
    `App.PersonSerializer`.
     If no `App.PersonSerializer` is found, this method will look
    for an `App.ApplicationSerializer` (the default serializer for
    your entire application).
     If a serializer cannot be found on the adapter, it will fall back
    to an instance of `JSONSerializer`.
     @method serializerFor
    @public
    @param {String} modelName the record to serialize
    @return {Serializer}
  */
  serializerFor(modelName) {
    if (macroCondition(getOwnConfig().env.DEBUG)) {
      assertDestroyingStore(this, 'serializerFor');
    }
    assert(`You need to pass a model name to the store's serializerFor method`, modelName);
    assert(`Passing classes to store.serializerFor has been removed. Please pass a dasherized string instead of ${modelName}`, typeof modelName === 'string');
    let normalizedModelName = normalizeModelName$1(modelName);
    let {
      _serializerCache
    } = this;
    let serializer = _serializerCache[normalizedModelName];
    if (serializer) {
      return serializer;
    }
    let owner = getOwner(this);

    // by name
    serializer = owner.lookup(`serializer:${normalizedModelName}`);
    if (serializer !== undefined) {
      _serializerCache[normalizedModelName] = serializer;
      return serializer;
    }

    // no serializer found for the specific model, fallback and check for application serializer
    serializer = _serializerCache.application || owner.lookup('serializer:application');
    if (serializer !== undefined) {
      _serializerCache[normalizedModelName] = serializer;
      _serializerCache.application = serializer;
      return serializer;
    }
    return null;
  }

  // @ts-expect-error
  destroy() {
    if (this.isDestroyed) {
      // @ember/test-helpers will call destroy multiple times
      return;
    }
    this.isDestroying = true;
    // enqueue destruction of any adapters/serializers we have created
    for (let adapterName in this._adapterCache) {
      let adapter = this._adapterCache[adapterName];
      if (typeof adapter.destroy === 'function') {
        adapter.destroy();
      }
    }
    for (let serializerName in this._serializerCache) {
      let serializer = this._serializerCache[serializerName];
      if (typeof serializer.destroy === 'function') {
        serializer.destroy();
      }
    }
    if (macroCondition(getOwnConfig().packages.HAS_GRAPH_PACKAGE)) {
      const peekGraph = importSync('@ember-data/graph/-private').peekGraph;
      let graph = peekGraph(this);
      if (graph) {
        graph.destroy();
      }
    }
    this.notifications.destroy();
    this.recordArrayManager.destroy();
    this.identifierCache.destroy();
    this.unloadAll();
    this.isDestroyed = true;
  }
  static create(args) {
    return new this(args);
  }
}
let assertDestroyingStore;
let assertDestroyedStoreOnly;
if (macroCondition(getOwnConfig().env.DEBUG)) {
  assertDestroyingStore = function assertDestroyedStore(store, method) {
    assert(`Attempted to call store.${method}(), but the store instance has already been destroyed.`, !(store.isDestroying || store.isDestroyed));
  };
  assertDestroyedStoreOnly = function assertDestroyedStoreOnly(store, method) {
    assert(`Attempted to call store.${method}(), but the store instance has already been destroyed.`, !store.isDestroyed);
  };
}
function isMaybeIdentifier(maybeIdentifier) {
  return Boolean(maybeIdentifier !== null && typeof maybeIdentifier === 'object' && ('id' in maybeIdentifier && 'type' in maybeIdentifier && maybeIdentifier.id && maybeIdentifier.type || maybeIdentifier.lid));
}
function isDSModel(record) {
  if (macroCondition(!getOwnConfig().packages.HAS_MODEL_PACKAGE)) {
    return false;
  }
  return !!record && 'constructor' in record && 'isModel' in record.constructor && record.constructor.isModel === true;
}
function normalizeProperties(store, identifier, properties, isForV1 = false) {
  // assert here
  if (properties !== undefined) {
    if ('id' in properties) {
      assert(`expected id to be a string or null`, properties.id !== undefined);
    }
    assert(`You passed '${typeof properties}' as properties for record creation instead of an object.`, typeof properties === 'object' && properties !== null);
    const {
      type
    } = identifier;

    // convert relationship Records to RecordDatas before passing to RecordData
    let defs = store.getSchemaDefinitionService().relationshipsDefinitionFor({
      type
    });
    if (defs !== null) {
      let keys = Object.keys(properties);
      let relationshipValue;
      for (let i = 0; i < keys.length; i++) {
        let prop = keys[i];
        let def = defs[prop];
        if (def !== undefined) {
          if (def.kind === 'hasMany') {
            if (macroCondition(getOwnConfig().env.DEBUG)) {
              assertRecordsPassedToHasMany(properties[prop]);
            }
            relationshipValue = extractIdentifiersFromRecords(properties[prop], isForV1);
          } else {
            relationshipValue = extractIdentifierFromRecord(properties[prop], isForV1);
          }
          properties[prop] = relationshipValue;
        }
      }
    }
  }
  return properties;
}
function assertRecordsPassedToHasMany(records) {
  assert(`You must pass an array of records to set a hasMany relationship`, Array.isArray(records));
  assert(`All elements of a hasMany relationship must be instances of Model, you passed ${records.map(r => `${typeof r}`).join(', ')}`, function () {
    return records.every(record => {
      try {
        recordIdentifierFor(record);
        return true;
      } catch {
        return false;
      }
    });
  }());
}
function extractIdentifiersFromRecords(records, isForV1 = false) {
  return records.map(record => extractIdentifierFromRecord(record, isForV1));
}
function extractIdentifierFromRecord(recordOrPromiseRecord, isForV1 = false) {
  if (!recordOrPromiseRecord) {
    return null;
  }
  const extract = isForV1 ? peekCache : recordIdentifierFor;
  if (macroCondition(getOwnConfig().deprecations.DEPRECATE_PROMISE_PROXIES)) {
    if (isPromiseRecord(recordOrPromiseRecord)) {
      let content = recordOrPromiseRecord.content;
      assert('You passed in a promise that did not originate from an EmberData relationship. You can only pass promises that come from a belongsTo or hasMany relationship to the get call.', content !== undefined);
      deprecate(`You passed in a PromiseProxy to a Relationship API that now expects a resolved value. await the value before setting it.`, false, {
        id: 'ember-data:deprecate-promise-proxies',
        until: '5.0',
        since: {
          enabled: '4.7',
          available: '4.7'
        },
        for: 'ember-data'
      });
      return content ? extract(content) : null;
    }
  }
  return extract(recordOrPromiseRecord);
}
function isPromiseRecord(record) {
  return !!record.then;
}
function secretInit(record, cache, identifier, store) {
  setRecordIdentifier(record, identifier);
  StoreMap.set(record, store);
  setCacheFor(record, cache);
}
function normalizeModelName(modelName) {
  if (macroCondition(getOwnConfig().deprecations.DEPRECATE_HELPERS)) {
    deprecate(`the helper function normalizeModelName is deprecated. You should use model names that are already normalized, or use string helpers of your own. This function is primarily an alias for dasherize from @ember/string.`, false, {
      id: 'ember-data:deprecate-normalize-modelname-helper',
      for: 'ember-data',
      until: '5.0',
      since: {
        available: '4.7',
        enabled: '4.7'
      }
    });
    return normalizeModelName$1(modelName);
  }
  assert(`normalizeModelName support has been removed`);
}
export { CacheHandler as C, IdentifierArray as I, MUTATE as M, RecordArrayManager as R, Store as S, _clearCaches as _, setIdentifierGenerationMethod as a, setIdentifierUpdateMethod as b, setIdentifierForgetMethod as c, setIdentifierResetMethod as d, constructResource as e, coerceId as f, ensureStringId as g, Collection as h, isStableIdentifier as i, notifyArray as j, SOURCE as k, IDENTIFIER_ARRAY_TAG as l, fastPush as m, normalizeModelName as n, removeRecordDataFor as o, peekCache as p, recordIdentifierFor as r, storeFor as s };